import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "../../utils/axiosConfig";
import JSZip from "jszip";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  ListItemButton,
  Divider,
  Alert,
  LinearProgress,
  Badge,
  Tooltip,
  Menu,
  CardMedia,
  Drawer,
  AppBar,
  Toolbar,
  Fab,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  Skeleton,
  CircularProgress,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Tabs,
  Tab,
  Stack,
  Autocomplete,
  Checkbox,
  FormGroup,
  InputAdornment,
} from "@mui/material";
import { getProductPrimaryImageUrl, getFullImageUrl } from "../../utils/imageUtils";
import {
  Dashboard as DashboardIcon,
  Inventory,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Add,
  Edit,
  Delete,
  Visibility,
  MoreVert,
  ContentCopy,
  AttachMoney,
  LocalShipping,
  Star,
  Upload,
  FolderZip,
  Download,
  FileDownload,
  Refresh,
  Analytics,
  Campaign,
  Receipt,
  Warning,
  CheckCircle,
  Pending,
  Cancel,
  Search,
  FilterList,
  Store,
  Assessment,
  Settings,
  Notifications,
  Home,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Business,
  MonetizationOn,
  Timeline,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  ShowChart,
  Verified,
  AccountBalance,
  CreditCard,
  LocalOffer,
  Group,
  Category,
  Report,
  PhotoCamera,
  CloudUpload,
  Schedule,
  RateReview,
  Storefront,
  ProductionQuantityLimits,
  HomeWork,
  AccessTime,
  Close,
  ErrorOutline,
  Info,
  LocationOn,
  Person,
  Phone,
  CalendarToday,
  LocalShipping as VehicleIcon,
  RateReview as ReviewIcon,
  Percent,
  AccountCircle,
  Assignment,
  Payment,
  Straighten,
  Email,
  PlayArrow,
  FavoriteBorder,
  ShoppingBag,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  fetchSellerStats,
  fetchSellerReviews,
  fetchSellerOrders,
  fetchSellerPendingOrders,
  fetchSellerProducts,
  fetchSellerPendingProducts,
  fetchSellerApprovedProducts,
  fetchSellerRejectedProducts,
  clearSellerData,
  createSellerProduct,
  createSellerProductWithImages,
  updateSellerProduct,
  deleteSellerProduct,
  duplicateSellerProduct,
  applyPromotion,
  deletePromotion,
  loadSellerPromotions,
  updatePromotionStatus,
} from "../../store/sellerSlice";
import { updateSellerLogo, updateSellerProfile } from "../../store/slices/authSlice";
import { 
  fetchVisitRequests, 
  updateVisitRequestStatus, 
  getVisitRequestStats,
  clearError 
} from "../../store/slices/visitRequestSlice";
import { shipOrder, deliverOrder } from "../../store/slices/orderSlice";
import { addSellerResponse, flagReview } from "../../store/slices/reviewSlice";
// Removed fetchProductReviewAnalytics - using product statistics instead to avoid duplicate API calls
import lotRequestService from "../../services/lotRequestService";
import { fileService } from "../../services/fileService";
import { productService } from "../../services/productService";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8083/api";

const mapUserToProfileForm = (user) => {
  // Format address object properly - businessAddress is an Address object with street, city, state, country, postalCode
  let formattedAddress = "";
  if (user?.businessProfile?.businessAddress) {
    const addr = user.businessProfile.businessAddress;
    if (typeof addr === 'object' && addr !== null) {
      // Format as: "street, city, state, country - postalCode"
      const parts = [];
      if (addr.street) parts.push(addr.street);
      if (addr.city) parts.push(addr.city);
      if (addr.state) parts.push(addr.state);
      if (addr.country) parts.push(addr.country);
      const addressStr = parts.join(', ');
      if (addr.postalCode || addr.pincode) {
        formattedAddress = `${addressStr} - ${addr.postalCode || addr.pincode}`;
      } else {
        formattedAddress = addressStr;
      }
    } else if (typeof addr === 'string') {
      formattedAddress = addr;
    }
  }
  
  // Fallback to user.address if businessAddress not available
  if (!formattedAddress && user?.address) {
    if (typeof user.address === 'object' && user.address !== null) {
      const addr = user.address;
      const parts = [];
      if (addr.street) parts.push(addr.street);
      if (addr.city) parts.push(addr.city);
      if (addr.state) parts.push(addr.state);
      if (addr.country) parts.push(addr.country);
      const addressStr = parts.join(', ');
      if (addr.postalCode || addr.pincode) {
        formattedAddress = `${addressStr} - ${addr.postalCode || addr.pincode}`;
      } else {
        formattedAddress = addressStr;
      }
    } else if (typeof user.address === 'string') {
      formattedAddress = user.address;
    }
  }
  
  return {
    storeName:
      user?.businessProfile?.businessName ||
      user?.storeName ||
      `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
      "My Store",
    email: user?.businessProfile?.businessEmail || user?.email || "",
    phone: user?.phoneNumber || user?.phone || "",
    address: formattedAddress,
    description:
      user?.businessProfile?.description ||
      "Premium building materials supplier with over 10 years of experience.",
    businessType: user?.businessProfile?.businessType || "Building Materials",
  };
};

// StatCard component defined outside to prevent recreation on every render
const StatCard = React.memo(({
  title,
  value,
  icon,
  trend,
  trendValue,
  color = "primary",
  loading = false,
}) => (
  <Card sx={{ 
    height: 120,
    minHeight: 120,
    maxHeight: 150,
    borderRadius: 0,
    border: "2px solid #2C3E50",
    "&:hover": {
      borderColor: "#2C3E50",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
    },
    transition: "all 0.3s ease"
  }}>
    <CardContent sx={{ 
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      p: 2,
      overflow: "hidden"
    }}>
      {loading ? (
        <Box>
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: '50%', float: 'right', mt: -5 }} />
          <Skeleton variant="text" width="40%" height={16} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "100%"
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            <Typography 
              color="#000000" 
              gutterBottom 
              variant="body2" 
              sx={{ 
                fontWeight: 700, 
                fontSize: '0.875rem', 
                color: '#000000',
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block"
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                fontSize: '1.25rem', 
                color: '#000000',
                mt: 0.5
              }}
            >
              {typeof value === "number" && value > 0 ? value : (typeof value === "string" && value !== "0" ? value : "")}
            </Typography>
            {trend && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                {trend === "up" ? (
                  <TrendingUp color="success" sx={{ mr: 0.5, fontSize: 16 }} />
                ) : (
                  <TrendingDown color="error" sx={{ mr: 0.5, fontSize: 16 }} />
                )}
                <Typography
                  variant="body2"
                  color={trend === "up" ? "success.main" : "error.main"}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {trendValue}%
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 40, height: 40, flexShrink: 0 }}>
            {React.cloneElement(icon, { sx: { fontSize: 20 } })}
          </Avatar>
        </Box>
      )}
    </CardContent>
  </Card>
), (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.title === nextProps.title &&
    prevProps.value === nextProps.value &&
    prevProps.trend === nextProps.trend &&
    prevProps.trendValue === nextProps.trendValue &&
    prevProps.color === nextProps.color &&
    prevProps.loading === nextProps.loading
    // Intentionally skip icon comparison as it's always a new JSX element
  );
});

StatCard.displayName = 'StatCard';

const SellerDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  // Track if initial data load has completed to prevent flicker on subsequent updates
  const initialLoadCompleteRef = useRef(false);
  const hasInitialOrdersRef = useRef(false);
  
  // Cache chart data to prevent flickering during refresh
  const cachedSalesDataRef = useRef([]);
  const cachedCategoryPerformanceRef = useRef([]);
  
  // Track which products have had analytics fetched to prevent duplicate API calls
  const fetchedAnalyticsProductsRef = useRef(new Set());
  const isFetchingAnalyticsRef = useRef(false);
  const previousProductIdsRef = useRef(new Set());
  const previousApprovedProductIdsRef = useRef(new Set());
  
  // Track which products have analytics loaded (separate from fetch tracking)
  const analyticsLoadedRef = useRef(new Set());
  
  // Track if reviews have been fetched for Reviews tab to prevent duplicate calls
  const reviewsFetchedRef = useRef(false);
  const isFetchingReviewsRef = useRef(false);
  const previousUserIdRef = useRef(null);
  
  // Reset reviews fetch flag when user changes
  useEffect(() => {
    if (previousUserIdRef.current !== null && previousUserIdRef.current !== user?.id) {
      // User changed, reset the fetch flag
      reviewsFetchedRef.current = false;
      isFetchingReviewsRef.current = false;
    }
    previousUserIdRef.current = user?.id;
  }, [user?.id]);
  
  // Check if seller is approved
  const isSellerApproved = user?.businessProfile?.approvalStatus === 'APPROVED';
  
  // Memoize common sx objects to prevent recreation on every render
  const commonTextFieldSx = useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      height: '48px',
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      fontSize: '14px',
      color: '#374151',
      willChange: 'auto', // Prevent flickering
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none'
      },
      '&:hover': {
        borderColor: '#9ca3af'
      },
      '&.Mui-focused': {
        borderColor: '#667eea',
        boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
      }
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#9ca3af',
      opacity: 1
    },
    '& .MuiFormHelperText-root': {
      fontSize: '12px',
      color: '#ef4444'
    }
  }), []);
  
  const commonMultilineTextFieldSx = useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      fontSize: '14px',
      color: '#374151',
      willChange: 'auto', // Prevent flickering
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none'
      },
      '&:hover': {
        borderColor: '#9ca3af'
      },
      '&.Mui-focused': {
        borderColor: '#667eea',
        boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
      }
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#9ca3af',
      opacity: 1
    },
    '& .MuiFormHelperText-root': {
      fontSize: '12px',
      color: '#ef4444'
    }
  }), []);
  
  const commonTypographySx = useMemo(() => ({
    color: '#000000',
    fontWeight: 600,
    mb: 1,
    fontSize: '14px',
    willChange: 'auto', // Prevent re-rendering flicker
  }), []);
  
  const commonSectionTypographySx = useMemo(() => ({
    fontSize: '18px',
    fontWeight: 600,
    color: '#2d3748',
    mb: 3,
    pb: 1,
    borderBottom: '2px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    willChange: 'auto', // Prevent re-rendering flicker
  }), []);
  
  const commonGridContainerSx = useMemo(() => ({
    willChange: 'auto', // Prevent flickering on re-renders
  }), []);
  const {
    stats: sellerStats,
    statsLoading,
    reviews,
    reviewsAnalytics,
    orders,
    pendingOrders,
    pendingOrdersCount,
    products,
    productsLoading,
    pendingProducts,
    pendingProductsLoading,
    approvedProducts,
    approvedProductsLoading,
    rejectedProducts,
    rejectedProductsLoading,
    ordersLoading,
    createProductLoading,
  } = useSelector((state) => state.seller);

  // Get product analytics for ratings
  const { productAnalytics } = useSelector((state) => state.reviews);
  
  // Product statistics state
  const [productStatistics, setProductStatistics] = useState({});
  
  // Get promotions for active count
  const sellerPromotions = useSelector((state) => state.seller.promotions);
  
  // Calculate active promotions count
  const activePromotionsCount = useMemo(() => {
    if (!sellerPromotions || sellerPromotions.length === 0) return 0;
    const now = new Date();
    return sellerPromotions.filter(p => {
      // Check status (can be 'active', 'ACTIVE', or 'Active')
      const status = (p.status || '').toLowerCase();
      if (status !== 'active') return false;
      
      // Check date range if available
      if (p.startDateTime && p.endDateTime) {
        const start = new Date(p.startDateTime);
        const end = new Date(p.endDateTime);
        return now >= start && now <= end;
      } else if (p.startDate && p.endDate) {
        const start = new Date(p.startDate + 'T00:00:00');
        const end = new Date(p.endDate + 'T23:59:59');
        return now >= start && now <= end;
      }
      
      // If no dates, just check status
      return status === 'active';
    }).length;
  }, [sellerPromotions]);

  const [activeTab, setActiveTab] = useState(0);
  const [productSubTab, setProductSubTab] = useState(0); // 0: pending, 1: approved, 2: rejected
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  
  // Save sidebar collapsed state to localStorage and dispatch event
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('sidebarStateChanged'));
  }, [sidebarCollapsed]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    category: '',
    price: '',
    priceUnit: 'PER_SQ_FT',
    stockQuantity: '',
    minimumOrderQuantity: '',
    description: '',
    origin: '',
    lotSize: '',
    lotPercentage: '',
    // Lot Size Details (default/first lot)
    lotName: '',
    lotLength: '',
    lotWidth: '',
    lotNos: '',
    lotLocation: '',
    tags: [],
    images: [],
    thumbnailImageIndex: null,
    specifications: {
      finish: '',
      thickness: '',
      color: ''
    },
    // Custom fields for 'others' options
    customCategory: '',
    customFinish: '',
    customOrigin: '',
    customColor: ''
  });
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [productMenuAnchorEl, setProductMenuAnchorEl] = useState(null);
  const [selectedProductForMenu, setSelectedProductForMenu] = useState(null);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState(null);
  const [dateRange, setDateRange] = useState("all");
  const [loading, setLoading] = useState(false);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    category: "",
    price: "",
    priceUnit: "PER_SQ_FT",
    stockQuantity: "",
    minimumOrderQuantity: "",
    description: "",
    origin: "",
    lotSize: "",
    lotPercentage: "",
    // Lot Size Details (default/first lot)
    lotName: "",
    lotLength: "",
    lotWidth: "",
    lotNos: "",
    lotLocation: "",
    tags: [],
    images: [],
    videoUrls: [],
    thumbnailImageIndex: null, // Index of selected thumbnail image
    specifications: {
      finish: "",
      thickness: "",
      color: ""
    },
    // Custom fields for 'others' options
    customCategory: "",
    customFinish: "",
    customOrigin: "",
    customColor: ""
  });
  // Additional lots beyond the default lot (for multi-lot support)
  const [newProductAdditionalLots, setNewProductAdditionalLots] = useState([]);
  const [editFormAdditionalLots, setEditFormAdditionalLots] = useState([]);

  const emptyAdditionalLot = () => ({
    lotId: `lot_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    lotLength: '',
    lotWidth: '',
    lotNos: '',
  });

  const handleNewProductAdditionalLotChange = (idx, field, value) => {
    setNewProductAdditionalLots(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleEditFormAdditionalLotChange = (idx, field, value) => {
    setEditFormAdditionalLots(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  // Auto-derived stock quantity = sum of all lot Nos
  const computedNewProductStockQty = [
    parseInt(newProduct.lotNos) || 0,
    ...newProductAdditionalLots.map(l => parseInt(l.lotNos) || 0)
  ].reduce((sum, n) => sum + n, 0);

  // Auto-derived total SFT = sum(length * width * Nos) / 144 (dimensions in inches)
  const computedNewProductTotalSft = (() => {
    const allRows = [
      { l: parseFloat(newProduct.lotLength) || 0, w: parseFloat(newProduct.lotWidth) || 0, n: parseInt(newProduct.lotNos) || 0 },
      ...newProductAdditionalLots.map(r => ({ l: parseFloat(r.lotLength) || 0, w: parseFloat(r.lotWidth) || 0, n: parseInt(r.lotNos) || 0 }))
    ];
    return allRows.reduce((sum, r) => sum + r.l * r.w * r.n, 0) / 144;
  })();

  // Same derived values for the edit form
  const computedEditStockQty = [
    parseInt(editForm.lotNos) || 0,
    ...editFormAdditionalLots.map(l => parseInt(l.lotNos) || 0)
  ].reduce((sum, n) => sum + n, 0);

  const computedEditTotalSft = (() => {
    const allRows = [
      { l: parseFloat(editForm.lotLength) || 0, w: parseFloat(editForm.lotWidth) || 0, n: parseInt(editForm.lotNos) || 0 },
      ...editFormAdditionalLots.map(r => ({ l: parseFloat(r.lotLength) || 0, w: parseFloat(r.lotWidth) || 0, n: parseInt(r.lotNos) || 0 }))
    ];
    return allRows.reduce((sum, r) => sum + r.l * r.w * r.n, 0) / 144;
  })();

  const [productErrors, setProductErrors] = useState({});
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [imageValidationMessages, setImageValidationMessages] = useState([]);
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [orderToShip, setOrderToShip] = useState(null);
  const [shippingForm, setShippingForm] = useState({
    trackingNumber: '',
    carrier: ''
  });
  const [shippingLoading, setShippingLoading] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadImagesFile, setBulkUploadImagesFile] = useState(null);
  const [bulkUploadFilteredZipFile, setBulkUploadFilteredZipFile] = useState(null); // Filtered ZIP with only valid images
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadResults, setBulkUploadResults] = useState(null);
  const [bulkUploadValidationMessages, setBulkUploadValidationMessages] = useState([]);
  const [validatingBulkImages, setValidatingBulkImages] = useState(false);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all'); // Filter for orders tab
  const [productSearchFilter, setProductSearchFilter] = useState(''); // Search filter for products (debounced)
  const [productSearchInput, setProductSearchInput] = useState(''); // Local input value (not debounced, prevents cursor jump)
  const [productCategoryFilter, setProductCategoryFilter] = useState([]); // Category filter for products (array for multiple selection)
  const [editUploadingImages, setEditUploadingImages] = useState(false);
  const [editUploadingVideos, setEditUploadingVideos] = useState(false);
  const [editImageValidationMessages, setEditImageValidationMessages] = useState([]);
  const editImageInputRef = useRef(null);
  const editVideoInputRef = useRef(null);
  const [resubmittingProduct, setResubmittingProduct] = useState(false);
  const [imagePreview, setImagePreview] = useState({ open: false, src: '', alt: '' });
  const openImagePreview = useCallback((src, alt = 'Image preview', type = 'image') => {
    if (!src) return;
    setImagePreview({ open: true, src, alt, type });
  }, []);
  const closeImagePreview = useCallback(() => {
    setImagePreview({ open: false, src: '', alt: '' });
  }, []);
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }),
    []
  );
  const parseAmount = useCallback((value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }, []);
  const formatCurrency = useCallback(
    (value) => currencyFormatter.format(parseAmount(value)),
    [currencyFormatter, parseAmount]
  );
  const formatCompactCurrency = useCallback(
    (value) => {
      const amount = parseAmount(value);
      if (amount >= 1e7) return `₹${(amount / 1e7).toFixed(1)} Cr`;
      if (amount >= 1e5) return `₹${(amount / 1e5).toFixed(1)} L`;
      if (amount >= 1e3) return `₹${(amount / 1e3).toFixed(0)} K`;
      return `₹${Math.round(amount)}`;
    },
    [parseAmount]
  );

  // Track if we have initial orders (cached or loaded) to prevent flicker
  useEffect(() => {
    if (orders && orders.length > 0) {
      hasInitialOrdersRef.current = true;
    }
  }, [orders]);

  // Fetch seller data on component mount
  // NOTE: Charts will render instantly with cached orders data from Redux initialState
  // Fresh data will be fetched in background and update charts when ready
  useEffect(() => {
    const sellerId = user?.id;
    if (sellerId) {
      // Track if we have initial orders (cached or loaded)
      if (orders && orders.length > 0) {
        hasInitialOrdersRef.current = true;
      }
      
      // Charts are already showing with cached data from initialState
      // Now fetch fresh data in background (non-blocking)
      
      // PRIORITY 1: Fetch fresh orders FIRST (needed for charts) - must complete for charts
      // Cached data already displayed, so charts render instantly, this updates them when ready
      dispatch(fetchSellerOrders({ sellerId, size: 120 })).then(() => {
        initialLoadCompleteRef.current = true;
        hasInitialOrdersRef.current = true;
      }).catch(() => {
        initialLoadCompleteRef.current = true;
      });
      
      // PRIORITY 2: Fetch reviews and stats (lightweight, non-blocking for charts)
      // fetchSellerStats already fetches reviews with size:1 for analytics
      // We'll fetch reviews with size:5 only when Reviews tab is accessed (lazy loading)
      dispatch(fetchSellerStats(sellerId));
      
      // PRIORITY 3: Defer product fetches until Products tab is accessed (lazy loading)
      // Only fetch pending products count if not already cached
      // This prevents blocking the dashboard and reduces flickering
      if (!pendingProducts || pendingProducts.length === 0) {
        // Fetch pending products in background with delay (for count only initially)
        setTimeout(() => {
          dispatch(fetchSellerPendingProducts({ sellerId }));
        }, 500); // Defer after critical data loads
      }
      
      // Fetch approved and rejected products on mount for count display
      // Defer other product fetches until needed
      setTimeout(() => {
        dispatch(fetchSellerApprovedProducts({ sellerId }));
        dispatch(fetchSellerRejectedProducts({ sellerId }));
      }, 800);
      
      // PRIORITY 4: Fetch pending orders (slow operation, non-blocking, charts don't depend on it)
      // Delayed significantly to prioritize chart data loading
      setTimeout(() => {
        dispatch(fetchSellerPendingOrders({ sellerId }));
      }, 300);
      
      // PRIORITY 5: Load promotions (non-blocking, can be deferred)
      setTimeout(() => {
        dispatch(loadSellerPromotions(sellerId));
      }, 1000);
    }

    return () => {
      // Don't clear cached orders on unmount - they'll be useful for next visit
      // Only clear other non-cached data
      // dispatch(clearSellerData()); // Commented out to preserve cache
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.id]);

  // Lazy load products when Products tab is accessed
  useEffect(() => {
    const sellerId = user?.id;
    if (activeTab === 1 && sellerId) {
      // Only fetch if not currently loading and products haven't been fetched yet
      // Use null check: null = not fetched, array = fetched (even if empty)
      const hasPendingProducts = pendingProducts !== null;
      const hasApprovedProducts = approvedProducts !== null;
      const hasRejectedProducts = rejectedProducts !== null;
      
      // Fetch all product lists when Products tab is accessed
      // This ensures we only fetch when needed (lazy loading)
      if (productSubTab === 0 && !hasPendingProducts && !pendingProductsLoading) {
        dispatch(fetchSellerPendingProducts({ sellerId }));
      } else if (productSubTab === 1 && !hasApprovedProducts && !approvedProductsLoading) {
        dispatch(fetchSellerApprovedProducts({ sellerId }));
      } else if (productSubTab === 2 && !hasRejectedProducts && !rejectedProductsLoading) {
        dispatch(fetchSellerRejectedProducts({ sellerId }));
      }
    }
  }, [activeTab, productSubTab, dispatch, user?.id, pendingProducts, approvedProducts, rejectedProducts, pendingProductsLoading, approvedProductsLoading, rejectedProductsLoading]);

  // Debounce search input to prevent cursor jumping - update filter after user stops typing
  // Only search if input has at least 3 characters
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      // Only update filter if input has at least 3 characters, or if input is empty (to clear filter)
      if (productSearchInput.length === 0 || productSearchInput.length >= 3) {
        setProductSearchFilter(productSearchInput);
      } else {
        // If less than 3 characters, clear the filter
        setProductSearchFilter('');
      }
    }, 500); // 500ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [productSearchInput]);

  // Refetch products when filters change - filters are now applied on backend
  useEffect(() => {
    const sellerId = user?.id;
    if (activeTab === 1 && sellerId) {
      const searchParam = productSearchFilter && productSearchFilter.trim() ? productSearchFilter.trim() : null;
      const categoryParam = productCategoryFilter && productCategoryFilter.length > 0 ? productCategoryFilter.join(',') : null;
      
      if (productSubTab === 0) {
        // Refetch pending products with filters
        dispatch(fetchSellerPendingProducts({ 
          sellerId,
          search: searchParam,
          category: categoryParam
        }));
      } else if (productSubTab === 1) {
        // Refetch approved products with filters
        dispatch(fetchSellerApprovedProducts({ 
          sellerId,
          search: searchParam,
          category: categoryParam
        }));
      } else if (productSubTab === 2) {
        // Refetch rejected products with filters
        dispatch(fetchSellerRejectedProducts({ 
          sellerId,
          search: searchParam,
          category: categoryParam
        }));
      }
    }
  }, [productSearchFilter, productCategoryFilter, activeTab, productSubTab, dispatch, user?.id]);

  // Update analytics loaded ref when productAnalytics changes
  useEffect(() => {
    if (productAnalytics && typeof productAnalytics === 'object') {
      Object.keys(productAnalytics).forEach(productId => {
        if (productAnalytics[productId] && !analyticsLoadedRef.current.has(productId)) {
          analyticsLoadedRef.current.add(productId);
        }
      });
    }
  }, [productAnalytics]);

  // Fetch product analytics for rating display - optimized to prevent duplicate calls
  useEffect(() => {
    if (!products || products.length === 0) return;
    
    // Get current product IDs
    const currentProductIds = new Set(
      products
        .filter(p => p?.id)
        .map(p => p.id)
    );
    
    // Check if product IDs actually changed (not just array reference)
    const productIdsChanged = 
      previousProductIdsRef.current.size !== currentProductIds.size ||
      !Array.from(currentProductIds).every(id => previousProductIdsRef.current.has(id));
    
    // Only proceed if product IDs actually changed
    if (!productIdsChanged && previousProductIdsRef.current.size > 0) {
      return;
    }
    
    // Update previous product IDs
    previousProductIdsRef.current = currentProductIds;
    
    // Only fetch for products that haven't been fetched yet and don't have analytics loaded
    const productsToFetch = Array.from(currentProductIds).filter(id => {
      const alreadyFetched = fetchedAnalyticsProductsRef.current.has(id);
      const hasAnalyticsLoaded = analyticsLoadedRef.current.has(id);
      return !alreadyFetched && !hasAnalyticsLoaded;
    });
    
    // NOTE: Analytics (including averageRating) are now fetched via product statistics
    // No need to call fetchProductReviewAnalytics - it causes duplicate API calls
    // Product statistics are already being fetched in the useEffect at line 1840
    // Mark products as having analytics loaded from statistics
    productsToFetch.forEach(productId => {
      fetchedAnalyticsProductsRef.current.add(productId);
      analyticsLoadedRef.current.add(productId);
    });
  }, [products, dispatch]);

  // Also fetch analytics for approved products - optimized to prevent duplicate calls
  useEffect(() => {
    if (!approvedProducts || approvedProducts.length === 0) return;
    
    // Get current approved product IDs
    const currentApprovedProductIds = new Set(
      approvedProducts
        .filter(p => p?.id)
        .map(p => p.id)
    );
    
    // Check if product IDs actually changed (not just array reference)
    const approvedProductIdsChanged = 
      previousApprovedProductIdsRef.current.size !== currentApprovedProductIds.size ||
      !Array.from(currentApprovedProductIds).every(id => previousApprovedProductIdsRef.current.has(id));
    
    // Only proceed if product IDs actually changed
    if (!approvedProductIdsChanged && previousApprovedProductIdsRef.current.size > 0) {
      return;
    }
    
    // Update previous approved product IDs
    previousApprovedProductIdsRef.current = currentApprovedProductIds;
    
    // Only fetch for products that haven't been fetched yet and don't have analytics loaded
    const approvedProductsToFetch = Array.from(currentApprovedProductIds).filter(id => {
      const alreadyFetched = fetchedAnalyticsProductsRef.current.has(id);
      const hasAnalyticsLoaded = analyticsLoadedRef.current.has(id);
      return !alreadyFetched && !hasAnalyticsLoaded;
    });
    
    // NOTE: Analytics (including averageRating) are now fetched via product statistics
    // No need to call fetchProductReviewAnalytics - it causes duplicate API calls
    // Product statistics are already being fetched in the useEffect at line 1840
    // Mark products as having analytics loaded from statistics
    approvedProductsToFetch.forEach(productId => {
      fetchedAnalyticsProductsRef.current.add(productId);
      analyticsLoadedRef.current.add(productId);
    });
  }, [approvedProducts, dispatch]);

  // Refresh data function
  const handleRefreshData = () => {
    if (user?.id) {
      setLoading(true);
      Promise.all([
        dispatch(fetchSellerStats(user.id)),
        dispatch(fetchSellerReviews({ sellerId: user.id, size: 5 })),
        dispatch(fetchSellerOrders({ sellerId: user.id, size: 120 })),
        dispatch(fetchSellerPendingOrders({ sellerId: user.id })),
        dispatch(fetchSellerProducts({ sellerId: user.id, status: 'ACTIVE', approvalStatus: 'APPROVED' })),
        dispatch(fetchSellerPendingProducts({ sellerId: user.id })),
        dispatch(fetchSellerApprovedProducts({ sellerId: user.id })),
        dispatch(fetchSellerRejectedProducts({ sellerId: user.id })),
      ]).finally(() => {
        setLoading(false);
        // Data refreshed - no toast needed
      });
    }
  };

  // Returns dimension label strings based on selected price unit
  const getLotUnitLabels = (priceUnit) => {
    switch (priceUnit) {
      case 'PER_SQ_FT': return { length: 'Length (ft)', width: 'Width (ft)', total: 'Total (sq ft)' };
      case 'PER_SQ_M':  return { length: 'Length (m)',  width: 'Width (m)',  total: 'Total (sq m)'  };
      case 'PER_INCH':  return { length: 'Length (inch)', width: 'Width (inch)', total: 'Total (sq inch)' };
      default:          return { length: 'Length', width: 'Width', total: 'Total' };
    }
  };

  // Validate product form
  const validateProduct = (product) => {
    const errors = {};

    // Basic Information
    if (!product.name || !product.name.trim()) {
      errors.name = "Product name is required";
    }

    if (!product.category) {
      errors.category = "Category is required";
    }

    // Product Description
    if (!product.description || !product.description.trim()) {
      errors.description = "Product description is required";
    }

    // Pricing & Stock
    if (!product.price || parseFloat(product.price) <= 0) {
      errors.price = "Price must be greater than 0";
    }

    if (!product.minimumOrderQuantity || parseFloat(product.minimumOrderQuantity) <= 0) {
      errors.minimumOrderQuantity = "Minimum Order SFT must be greater than 0";
    }

    // Origin (stored at top level)
    if (!product.origin || !product.origin.trim()) {
      errors.origin = "Origin is required";
    }

    // Product Specifications (nested fields)
    if (!product.specifications?.finish || !product.specifications.finish.trim()) {
      errors.finish = "Finish is required";
    }

    if (!product.specifications?.color || !product.specifications.color.trim()) {
      errors.color = "Color is required";
    }

    // Thickness (stored in specifications)
    if (!product.specifications?.thickness || !product.specifications.thickness.toString().trim()) {
      errors.thickness = "Thickness is required";
    }

    // Lot Size Details - All Required
    if (!product.lotLength || parseFloat(product.lotLength) <= 0) {
      errors.lotLength = "Length is required and must be greater than 0";
    }

    if (!product.lotWidth || parseFloat(product.lotWidth) <= 0) {
      errors.lotWidth = "Width is required and must be greater than 0";
    }

    if (!product.lotNos || parseInt(product.lotNos) <= 0) {
      errors.lotNos = "Nos (Quantity) is required and must be greater than 0";
    }

    // Product Images - At least one image required
    if (!product.images || product.images.length === 0) {
      errors.images = "At least one product image is required";
    }

    return errors;
  };

  // Handle image upload with AI validation for stone detection
  const handleImageUpload = async (files) => {
    setUploadingImages(true);
    setImageValidationMessages([]); // Clear previous messages
    try {
      const imageObjects = [];
      const validationMessages = [];
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
      
      // Validate each image with AI before adding
      for (const file of Array.from(files)) {
        try {
          // Create preview URL for display
          const previewUrl = URL.createObjectURL(file);
          
          // Validate image with AI service
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            const response = await axios.post(`${API_BASE_URL}/files/analyze-image`, formData);

            const aiResult = response.data;

            // Check if image is valid stone image
            if (aiResult.error) {
              // Image is not valid - show error and skip (do not add to images)
              validationMessages.push({
                type: 'error',
                filename: file.name,
                message: aiResult.error || aiResult.detected_image || 'Image is not suitable for our catalog'
              });
              URL.revokeObjectURL(previewUrl); // Clean up preview URL
              continue; // Skip this image - do not add it
            }
            
            if (aiResult.warning) {
              // Image is stone but quality is poor - show warning but allow
              validationMessages.push({
                type: 'warning',
                filename: file.name,
                message: aiResult.warning || 'Image quality is low'
              });
            }
            
            if (aiResult.success || aiResult.is_stone) {
              // Valid stone image - add to list
              imageObjects.push({
                file, // Store the actual file object for later upload
                url: previewUrl, // Preview URL for display
                name: file.name,
                aiValidated: true, // Mark as AI validated
                aiResult: aiResult, // Store AI result for reference
              });
              validationMessages.push({
                type: 'success',
                filename: file.name,
                message: aiResult.success || 'Image validated successfully'
              });
            } else {
              // Unknown result - skip with warning
              validationMessages.push({
                type: 'warning',
                filename: file.name,
                message: 'Unable to validate image. Skipping.'
              });
              URL.revokeObjectURL(previewUrl);
              continue;
            }
          } catch (aiError) {
            // If AI service fails, allow image but log warning
            console.warn(`AI validation failed for ${file.name}:`, aiError);
            validationMessages.push({
              type: 'warning',
              filename: file.name,
              message: 'AI validation unavailable. Image added without validation.'
            });
            
            imageObjects.push({
              file,
              url: previewUrl,
              name: file.name,
              aiValidated: false,
            });
          }
        } catch (fileError) {
          console.error(`Error processing ${file.name}:`, fileError);
          validationMessages.push({
            type: 'error',
            filename: file.name,
            message: `Failed to process: ${fileError.message || 'Unknown error'}`
          });
        }
      }

      // Update validation messages
      setImageValidationMessages(validationMessages);

      if (imageObjects.length > 0) {
        setNewProduct((prev) => ({
          ...prev,
          images: [...prev.images, ...imageObjects],
        }));
      }
    } catch (error) {
      console.error('Error processing images:', error);
      setImageValidationMessages([{
        type: 'error',
        filename: 'System',
        message: 'Failed to process images. Please try again.'
      }]);
    } finally {
      setUploadingImages(false);
    }
  };

  // Handle video upload
  const handleVideoUpload = async (files) => {
    setUploadingVideo(true);
    try {
      const videoObjects = Array.from(files).map((file) => {
        // Create preview URL for display
        const previewUrl = URL.createObjectURL(file);
        
        return {
          file, // Store the actual file object for later upload
          url: previewUrl, // Preview URL for display
          name: file.name,
        };
      });

      setNewProduct((prev) => ({
        ...prev,
        videoUrls: [...(prev.videoUrls || []), ...videoObjects],
      }));

      toast.success(`${videoObjects.length} video(s) added successfully!`);
    } catch (error) {
      console.error('Error processing videos:', error);
      toast.error('Failed to process videos. Please try again.');
    } finally {
      setUploadingVideo(false);
    }
  };

  // Handle product form submission
  const handleAddProduct = async () => {
    console.log('handleAddProduct called - starting product creation process');
    
    // Allow all sellers to add products, but inform about approval status
    if (!isSellerApproved) {
      // Info about pending products - no toast needed
    }

    const errors = validateProduct(newProduct);

    // Validate that computed stock is positive
    if (computedNewProductStockQty <= 0) {
      errors.lotNos = "Total Nos across all lots must be greater than 0";
    }

    // Minimum Order SFT must not exceed total SFT
    if (
      computedNewProductTotalSft > 0 &&
      parseFloat(newProduct.minimumOrderQuantity) > computedNewProductTotalSft
    ) {
      errors.minimumOrderQuantity = `Minimum Order SFT cannot exceed total SFT (${computedNewProductTotalSft.toFixed(3)} SFT)`;
    }

    // Validate additional lot dimension rows
    newProductAdditionalLots.forEach((lot, idx) => {
      const n = idx + 2;
      if (!lot.lotLength || parseFloat(lot.lotLength) <= 0) errors[`lot${n}Length`] = `Row ${n}: Length is required and must be > 0`;
      if (!lot.lotWidth || parseFloat(lot.lotWidth) <= 0) errors[`lot${n}Width`] = `Row ${n}: Width is required and must be > 0`;
      if (!lot.lotNos || parseInt(lot.lotNos) <= 0) errors[`lot${n}Nos`] = `Row ${n}: Nos is required and must be > 0`;
    });

    setProductErrors(errors);

    // Debug: Log validation errors and current product data
    console.log('Validation errors:', errors);
    console.log('Current product data:', newProduct);

    if (Object.keys(errors).length > 0) {
      // Show specific error messages via toast
      const errorMessages = Object.values(errors);
      if (errorMessages.length === 1) {
        toast.error(errorMessages[0]);
      } else {
        toast.error(`Please fix the following errors: ${errorMessages.slice(0, 3).join(', ')}${errorMessages.length > 3 ? '...' : ''}`);
      }
      return;
    }

    try {
      // Create FormData for product with images
      const formData = new FormData();
      
      // Reorder images so thumbnail is first (backend uses first image as primaryImageUrl)
      const orderedImages = [...newProduct.images];
      if (newProduct.thumbnailImageIndex !== null && newProduct.thumbnailImageIndex >= 0 && newProduct.thumbnailImageIndex < orderedImages.length) {
        // Move thumbnail image to first position
        const thumbnailImage = orderedImages[newProduct.thumbnailImageIndex];
        orderedImages.splice(newProduct.thumbnailImageIndex, 1);
        orderedImages.unshift(thumbnailImage);
      }

      // Add product data as JSON
      // Note: primaryImageUrl will be set by backend from first image after upload
      const productData = {
        name: newProduct.name,
        description: newProduct.description,
        category: newProduct.category,
        price: parseFloat(newProduct.price), // Convert to number for BigDecimal compatibility
        priceUnit: "PER_SQ_FT",
        stockQuantity: computedNewProductStockQty,
        minimumOrderQuantity: parseFloat(newProduct.minimumOrderQuantity) || 1,
        origin: newProduct.origin,
        lotSize: newProduct.lotSize,
        lotPercentage: newProduct.lotPercentage ? parseFloat(newProduct.lotPercentage) : null,
        // Legacy single-lot fields (default lot) — kept for backward compatibility
        lotLength: newProduct.lotLength ? parseFloat(newProduct.lotLength) : null,
        lotWidth: newProduct.lotWidth ? parseFloat(newProduct.lotWidth) : null,
        lotNos: newProduct.lotNos ? parseInt(newProduct.lotNos) : null,
        lotLocation: newProduct.lotLocation || "",
        // Multi-lot array: each lot contains only lotLength, lotWidth, lotNos
        lots: [
          {
            lotId: 'default',
            lotLength: newProduct.lotLength ? parseFloat(newProduct.lotLength) : null,
            lotWidth: newProduct.lotWidth ? parseFloat(newProduct.lotWidth) : null,
            lotNos: newProduct.lotNos ? parseInt(newProduct.lotNos) : null,
          },
          ...newProductAdditionalLots.map(lot => ({
            lotId: lot.lotId || `lot_${Date.now()}`,
            lotLength: lot.lotLength ? parseFloat(lot.lotLength) : null,
            lotWidth: lot.lotWidth ? parseFloat(lot.lotWidth) : null,
            lotNos: lot.lotNos ? parseInt(lot.lotNos) : null,
          }))
        ],
        tags: newProduct.tags,
        // Map frontend specifications to backend ProductSpecifications structure
        specifications: {
          finish: newProduct.specifications.finish || "",
          color: newProduct.specifications.color || "",
          thickness: newProduct.specifications.thickness ? parseFloat(newProduct.specifications.thickness) : null,
          // Add default values for other required fields
          pattern: "",
          length: null,
          width: null,
          density: null,
          porosity: null,
          waterAbsorption: null,
          hardness: "",
          compressiveStrength: null,
          flexuralStrength: null,
          frostResistance: "",
          slipResistance: "",
          surfaceTexture: "",
          edgeType: "",
          grainDirection: "",
          quarryLocation: "",
          processingLocation: "",
          qualityCertification: "",
          gradeClassification: "",
          installationMethod: "",
          maintenanceRequirements: "",
          sealingRequired: "",
          cleaningInstructions: "",
          sustainabilityCertification: "",
          recyclability: "",
          environmentalImpact: "",
          indoorUse: "YES",
          outdoorUse: "NO",
          floorUse: "YES",
          wallUse: "YES",
          kitchenUse: "YES",
          bathroomUse: "YES",
          additionalProperties: {}
        }
      };
      
      // Ensure productData is always sent as valid JSON string
      let productDataJson;
      try {
        productDataJson = JSON.stringify(productData);
        console.log('ProductData JSON length:', productDataJson ? productDataJson.length : 0);
        console.log('ProductData JSON preview:', productDataJson ? productDataJson.substring(0, 200) : 'null');
      } catch (error) {
        console.error('Error stringifying productData:', error, productData);
        toast.error('Failed to prepare product data. Please try again.');
        return;
      }
      
      // Always append productData first (required by backend)
      if (!productDataJson || typeof productDataJson !== 'string' || productDataJson.trim() === '') {
        console.error('productDataJson is empty or invalid:', {
          productDataJson,
          type: typeof productDataJson,
          length: productDataJson ? productDataJson.length : 0
        });
        toast.error('Failed to prepare product data. Please try again.');
        return;
      }
      
      // Append productData as a string (required by backend)
      formData.append('productData', productDataJson);
      
      // Verify it was appended correctly
      const initialFormDataKeys = Array.from(formData.keys());
      console.log('FormData keys after appending productData:', initialFormDataKeys);
      const appendedValue = formData.get('productData');
      console.log('Verification - productData in FormData:', appendedValue ? (typeof appendedValue === 'string' ? 'String (OK)' : `Type: ${typeof appendedValue}`) : 'MISSING');
      
      // Add image files in correct order (thumbnail first)
      orderedImages.forEach((image) => {
        if (image && image.file) {
          formData.append('images', image.file);
        }
      });

      // Add video files
      if (newProduct.videoUrls && newProduct.videoUrls.length > 0) {
        newProduct.videoUrls.forEach((video, index) => {
          if (video.file) {
            formData.append('videos', video.file);
          }
        });
      }

      // Verify FormData has productData before sending (re-check after adding images/videos)
      const finalFormDataKeys = Array.from(formData.keys());
      console.log('FormData keys before dispatch:', finalFormDataKeys);
      const hasProductData = formData.has('productData');
      const productDataValue = formData.get('productData');
      console.log('Has productData:', hasProductData);
      console.log('productData value type:', typeof productDataValue);
      console.log('productData value length:', productDataValue ? productDataValue.length : 'null');
      
      if (!hasProductData || !productDataValue || (typeof productDataValue === 'string' && productDataValue.trim() === '')) {
        console.error('productData is missing or empty from FormData!', {
          hasProductData,
          productDataValue: productDataValue ? (typeof productDataValue === 'string' ? productDataValue.substring(0, 100) : String(productDataValue).substring(0, 100)) : null
        });
        toast.error('Failed to prepare product data. Please try again.');
        return;
      }

      // Use Redux action to create product with images
      const result = await dispatch(createSellerProductWithImages(formData));
      
      if (createSellerProductWithImages.fulfilled.match(result)) {
        // Success - handle the response
        const response = result.payload;
        
        let successMessage = 'Product created successfully!';
        if (response && typeof response === 'object') {
          if (response.message && typeof response.message === 'string') {
            successMessage = response.message;
          } else if (response.success && response.productId) {
            const imagesCount = response.imagesUploaded || 0;
            successMessage = `Product created successfully with ${imagesCount} images! Product ID: ${response.productId}`;
          }
        }
        
        toast.success(successMessage);
        
        // Reset form
        setNewProduct({
          name: "",
          category: "",
          price: "",
          priceUnit: "PER_SQ_FT",
          stockQuantity: "",
          minimumOrderQuantity: "",
          description: "",
          origin: "",
          lotSize: "",
          lotPercentage: "",
          lotName: "",
          lotLength: "",
          lotWidth: "",
          lotNos: "",
          lotLocation: "",
          tags: [],
          images: [],
          videoUrls: [],
          thumbnailImageIndex: null,
          specifications: {
            finish: "",
            thickness: "",
            color: ""
          }
        });
        setNewProductAdditionalLots([]);
        setProductErrors({});
        setImageValidationMessages([]);
        setAddProductDialogOpen(false);
        
        // Refresh pending products
        dispatch(fetchSellerPendingProducts({ sellerId: user.id }));
      } else {
        // Error occurred
        const errorMessage = result.payload || 'Failed to create product. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating product:', error);
      const errorMessage = typeof error === 'string' ? error : 
                          (error && error.message && typeof error.message === 'string') ? error.message : 
                          'Failed to create product. Please try again.';
      toast.error(errorMessage);
    }
  };

  // Handle product form input changes - memoized to prevent unnecessary re-renders
  const handleProductInputChange = useCallback((field, value) => {
    setNewProduct((prev) => {
      // Only update if value actually changed
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });

    // Clear error for this field - debounced to prevent flickering
    setProductErrors((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: "" };
    });
  }, []);

  // Remove uploaded image
  const removeImage = (index) => {
    setNewProduct((prev) => {
      const newImages = prev.images.filter((_, i) => i !== index);
      // If the removed image was the thumbnail, reset thumbnail selection
      let newThumbnailIndex = prev.thumbnailImageIndex;
      if (prev.thumbnailImageIndex === index) {
        newThumbnailIndex = null;
      } else if (prev.thumbnailImageIndex !== null && prev.thumbnailImageIndex > index) {
        // Adjust thumbnail index if image before it was removed
        newThumbnailIndex = prev.thumbnailImageIndex - 1;
      }
      return {
        ...prev,
        images: newImages,
        thumbnailImageIndex: newThumbnailIndex,
      };
    });
  };

  // Set thumbnail image
  const setThumbnailImage = (index) => {
    setNewProduct((prev) => ({
      ...prev,
      thumbnailImageIndex: prev.thumbnailImageIndex === index ? null : index,
    }));
  };

  // Remove uploaded video
  const removeVideo = (index) => {
    setNewProduct((prev) => ({
      ...prev,
      videoUrls: (prev.videoUrls || []).filter((_, i) => i !== index),
    }));
  };

  // Handle image upload with AI validation for stone detection in edit form
  const handleEditImageUpload = async (files) => {
    const imageFiles = Array.from(files || []).filter(
      (file) => file && file.type && file.type.startsWith("image/")
    );

    if (!imageFiles.length) {
      toast.error("Please select valid image files");
      return;
    }

    if (!selectedProduct?.id) {
      toast.warn("Select a product before uploading images");
      return;
    }

    setEditUploadingImages(true);
    setEditImageValidationMessages([]); // Clear previous messages

    try {
      const imageObjects = [];
      const validationMessages = [];
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
      
      // Validate each image with AI before uploading
      for (const file of imageFiles) {
        try {
          // Validate image with AI service
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            const response = await axios.post(`${API_BASE_URL}/files/analyze-image`, formData);

            const aiResult = response.data;

            // Check if image is valid stone image
            if (aiResult.error) {
              // Image is not valid - show error and skip (do not upload)
              validationMessages.push({
                type: 'error',
                filename: file.name,
                message: aiResult.error || aiResult.detected_image || 'Image is not suitable for our catalog'
              });
              continue; // Skip this image - do not upload it
            }
            
            if (aiResult.warning) {
              // Image is stone but quality is poor - show warning but allow
              validationMessages.push({
                type: 'warning',
                filename: file.name,
                message: aiResult.warning || 'Image quality is low'
              });
            }
            
            if (aiResult.success || aiResult.is_stone) {
              // Valid stone image - add to list for upload
              imageObjects.push({
                file, // Store the actual file object for upload
                name: file.name,
                aiValidated: true, // Mark as AI validated
                aiResult: aiResult, // Store AI result for reference
              });
              validationMessages.push({
                type: 'success',
                filename: file.name,
                message: aiResult.success || 'Image validated successfully'
              });
            } else {
              // Unknown result - skip with warning
              validationMessages.push({
                type: 'warning',
                filename: file.name,
                message: 'Unable to validate image. Skipping.'
              });
              continue;
            }
          } catch (aiError) {
            // If AI service fails, allow image but log warning
            console.warn(`AI validation failed for ${file.name}:`, aiError);
            validationMessages.push({
              type: 'warning',
              filename: file.name,
              message: 'AI validation unavailable. Image will be uploaded without validation.'
            });
            
            imageObjects.push({
              file,
              name: file.name,
              aiValidated: false,
            });
          }
        } catch (fileError) {
          console.error(`Error processing ${file.name}:`, fileError);
          validationMessages.push({
            type: 'error',
            filename: file.name,
            message: `Failed to process: ${fileError.message || 'Unknown error'}`
          });
        }
      }

      // Update validation messages
      setEditImageValidationMessages(validationMessages);

      // Upload only validated images
      if (imageObjects.length > 0) {
        const filesToUpload = imageObjects.map(obj => obj.file);
        const uploadResponses = await fileService.uploadProductImages(filesToUpload);

        const baseIndex = editForm.images?.length || 0;
        const uploadResults = uploadResponses.reduce((acc, uploaded, idx) => {
          if (
            !uploaded ||
            uploaded.success === false ||
            !(uploaded.imageUrl || uploaded.fullUrl)
          ) {
            return acc;
          }

          acc.push({
            imageUrl: uploaded.imageUrl || uploaded.fullUrl,
            thumbnailUrl: uploaded.fullUrl || uploaded.imageUrl,
            altText: `${editForm.name || "Product"} image ${baseIndex + acc.length + 1}`,
            caption: "",
            imageType: baseIndex + acc.length === 0 ? "PRIMARY" : "GALLERY",
            displayOrder: baseIndex + acc.length,
            fileName: uploaded.fileName || uploaded.originalName,
            fileType: uploaded.contentType || filesToUpload[idx]?.type,
            fileSize: uploaded.fileSize || filesToUpload[idx]?.size,
            aiValidated: imageObjects[idx]?.aiValidated || false,
          });

          return acc;
        }, []);

        if (uploadResults.length > 0) {
          setEditForm((prev) => ({
            ...prev,
            images: [...(prev.images || []), ...uploadResults],
          }));

          toast.success(`${uploadResults.length} image(s) added successfully!`);
        } else {
          toast.error("Failed to upload product images. Please try again.");
        }
      } else {
        toast.warn("No valid images to upload. Please check the validation messages.");
      }
    } catch (error) {
      console.error("Error uploading product images:", error);
      toast.error(
        typeof error === "string"
          ? error
          : error?.message || "Failed to upload product images. Please try again."
      );
      setEditImageValidationMessages([{
        type: 'error',
        filename: 'System',
        message: 'Failed to process images. Please try again.'
      }]);
    } finally {
      setEditUploadingImages(false);
      if (editImageInputRef.current) {
        editImageInputRef.current.value = "";
      }
    }
  };

  const handleEditImageDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files?.length) {
      handleEditImageUpload(event.dataTransfer.files);
    }
  };

  const handleRemoveEditImage = (index) => {
    setEditForm((prev) => {
      const newImages = (prev.images || []).filter((_, i) => i !== index);
      // If the removed image was the thumbnail, reset thumbnail selection
      let newThumbnailIndex = prev.thumbnailImageIndex;
      if (prev.thumbnailImageIndex === index) {
        newThumbnailIndex = null;
      } else if (prev.thumbnailImageIndex !== null && prev.thumbnailImageIndex > index) {
        // Adjust thumbnail index if image before it was removed
        newThumbnailIndex = prev.thumbnailImageIndex - 1;
      }
      return {
        ...prev,
        images: newImages,
        thumbnailImageIndex: newThumbnailIndex,
      };
    });
  };

  // Set thumbnail image for edit form
  const setThumbnailImageEdit = (index) => {
    setEditForm((prev) => ({
      ...prev,
      thumbnailImageIndex: prev.thumbnailImageIndex === index ? null : index,
    }));
  };

  // Handle video upload for edit form
  const handleEditVideoUpload = async (files) => {
    setEditUploadingVideos(true);
    try {
      const videoFiles = Array.from(files || []).filter(
        (file) => file && file.type && file.type.startsWith("video/")
      );

      if (!videoFiles.length) {
        toast.error("Please select valid video files");
        return;
      }

      const videoObjects = videoFiles.map((file) => {
        const previewUrl = URL.createObjectURL(file);
        return {
          file,
          url: previewUrl,
          name: file.name,
        };
      });

      setEditForm((prev) => ({
        ...prev,
        videoUrls: [...(prev.videoUrls || []), ...videoObjects],
      }));

      toast.success(`${videoObjects.length} video(s) added successfully!`);
    } catch (error) {
      console.error('Error processing videos:', error);
      toast.error('Failed to process videos. Please try again.');
    } finally {
      setEditUploadingVideos(false);
    }
  };

  // Remove video from edit form
  const handleRemoveEditVideo = (index) => {
    setEditForm((prev) => ({
      ...prev,
      videoUrls: (prev.videoUrls || []).filter((_, i) => i !== index),
    }));
  };

  const salesData = useMemo(() => {
    // If loading and we have cached data, return cached data to prevent flickering
    if (ordersLoading && cachedSalesDataRef.current.length > 0) {
      return cachedSalesDataRef.current;
    }
    
    if (!orders || orders.length === 0 || !user?.id) {
      cachedSalesDataRef.current = [];
      return [];
    }

    const monthFormatter = new Intl.DateTimeFormat("en-IN", {
      month: "short",
      year: "2-digit",
    });

    const monthlyBuckets = new Map();

    orders.forEach((order) => {
      const orderDateRaw = order.orderDate || order.createdAt || order.updatedAt;
      if (!orderDateRaw) {
        return;
      }

      const orderDate = new Date(orderDateRaw);
      if (Number.isNaN(orderDate.getTime())) {
        return;
      }

      const sellerOrderTotal = (order.items || []).reduce((sum, item) => {
        if (!item || item.sellerId !== user.id) {
          return sum;
        }

        const quantity = item.quantity ?? 0;
        const finalPrice = parseAmount(item.finalPrice);
        const totalPrice = parseAmount(item.totalPrice);
        const unitPrice = parseAmount(item.unitPrice);

        if (finalPrice > 0) {
          return sum + finalPrice;
        }

        if (totalPrice > 0) {
          return sum + totalPrice;
        }

        if (unitPrice > 0 && quantity > 0) {
          return sum + unitPrice * quantity;
        }

        return sum;
      }, 0);

      if (sellerOrderTotal <= 0) {
        return;
      }

      const bucketKey = `${orderDate.getFullYear()}-${orderDate.getMonth()}`;
      const bucketDate = new Date(orderDate.getFullYear(), orderDate.getMonth(), 1);

      if (!monthlyBuckets.has(bucketKey)) {
        monthlyBuckets.set(bucketKey, {
          date: bucketDate,
          month: monthFormatter.format(bucketDate),
          sales: 0,
          orders: 0,
        });
      }

      const bucket = monthlyBuckets.get(bucketKey);
      bucket.sales += sellerOrderTotal;
      bucket.orders += 1;
    });

    const computedData = Array.from(monthlyBuckets.values())
      .sort((a, b) => a.date - b.date)
      .slice(-8)
      .map((bucket) => ({
        month: bucket.month,
        sales: Number(bucket.sales.toFixed(2)),
        orders: bucket.orders,
      }));
    
    // Cache the computed data
    cachedSalesDataRef.current = computedData;
    return computedData;
  }, [orders, parseAmount, user?.id, ordersLoading]);

  const categoryPerformance = useMemo(() => {
    // If loading and we have cached data, return cached data to prevent flickering
    if (ordersLoading && cachedCategoryPerformanceRef.current.length > 0) {
      return cachedCategoryPerformanceRef.current;
    }
    
    if (!orders || orders.length === 0 || !user?.id) {
      cachedCategoryPerformanceRef.current = [];
      return [];
    }

    // Build category map from products if available, but don't block on it
    // Chart can render immediately using order item categories
    const categoryMap = new Map();
    const allProducts = [
      ...(products || []),
      ...(approvedProducts || []),
      ...(pendingProducts || []),
    ];

    // Only use products if they're loaded (non-blocking)
    // This allows chart to render immediately with order data
    allProducts.forEach((product) => {
      if (product?.id) {
        categoryMap.set(product.id, product.category || "Uncategorized");
      }
    });

    const totalsByCategory = new Map();

    // Process orders immediately - don't wait for products
    // If item.category exists in order, use it directly (fast path)
    // Otherwise try categoryMap, then fallback to "Uncategorized"
    orders.forEach((order) => {
      (order.items || []).forEach((item) => {
        if (!item || item.sellerId !== user.id) {
          return;
        }

        // Fast path: use category from order item if available
        // This allows chart to render immediately without waiting for products
        const category =
          item.category ||
          categoryMap.get(item.productId) ||
          item.productCategory ||
          "Uncategorized";

        const quantity = item.quantity ?? 0;
        const finalPrice = parseAmount(item.finalPrice);
        const totalPrice = parseAmount(item.totalPrice);
        const unitPrice = parseAmount(item.unitPrice);

        let lineValue = 0;
        if (finalPrice > 0) {
          lineValue = finalPrice;
        } else if (totalPrice > 0) {
          lineValue = totalPrice;
        } else if (unitPrice > 0 && quantity > 0) {
          lineValue = unitPrice * quantity;
        }

        if (lineValue <= 0) {
          return;
        }

        if (!totalsByCategory.has(category)) {
          totalsByCategory.set(category, { sales: 0, orders: 0 });
        }

        const stats = totalsByCategory.get(category);
        stats.sales += lineValue;
        stats.orders += quantity || 1;
      });
    });

    const totalRevenue = Array.from(totalsByCategory.values()).reduce(
      (sum, value) => sum + value.sales,
      0
    );

    const colorPalette = [
      "#6366F1",
      "#22D3EE",
      "#F97316",
      "#34D399",
      "#A855F7",
      "#F59E0B",
      "#EF4444",
      "#14B8A6",
    ];

    const computedData = Array.from(totalsByCategory.entries())
      .sort((a, b) => b[1].sales - a[1].sales)
      .map(([name, value], index) => {
        const percentage =
          totalRevenue > 0
            ? Number(((value.sales / totalRevenue) * 100).toFixed(1))
            : 0;

        return {
          name,
          sales: Number(value.sales.toFixed(2)),
          orders: value.orders,
          color: colorPalette[index % colorPalette.length],
          percentage,
        };
      });
    
    // Cache the computed data
    cachedCategoryPerformanceRef.current = computedData;
    return computedData;
  }, [orders, parseAmount, products, approvedProducts, pendingProducts, user?.id, ordersLoading]);

    // Use approved products for analytics and promotions
    const myProducts = useMemo(() => {
      const map = new Map();
      [...(approvedProducts || []), ...(products || [])].forEach((product) => {
        const key = String(product.id ?? product._id);
        if (!map.has(key)) {
          map.set(key, product);
        }
      });
      return Array.from(map.values());
    }, [approvedProducts, products]);
  
  // Fetch product statistics when products change
  useEffect(() => {
    const fetchStatistics = async () => {
      const allProducts = [
        ...(products || []),
        ...(approvedProducts || []),
        ...(pendingProducts || []),
        ...(rejectedProducts || [])
      ];
      
      if (allProducts.length === 0) return;
      
      const productIds = allProducts
        .map(p => p.id)
        .filter(id => id && !productStatistics[id]); // Only fetch if not already cached
      
      if (productIds.length === 0) return;
      
      try {
        const response = await productService.getProductStatisticsBatch(productIds);
        if (response.success && response.statistics) {
          setProductStatistics(prev => ({
            ...prev,
            ...response.statistics
          }));
        }
      } catch (error) {
        console.error('Failed to fetch product statistics:', error);
      }
    };
    
    fetchStatistics();
  }, [products, approvedProducts, pendingProducts, rejectedProducts]);
  
  // Helper function to get product statistics
  const getProductStats = (productId) => {
    return productStatistics[productId] || {
      totalViews: 0,
      totalOrders: 0,
      totalReviews: 0,
      approvedReviews: 0,
      averageRating: 0.0
    };
  };

  // Products with order counts from statistics (for backward compatibility)
  const productsWithOrderCounts = useMemo(() => {
    if (!myProducts || myProducts.length === 0) return [];
    
    return myProducts.map(product => ({
      ...product,
      orderCount: getProductStats(product.id).totalOrders || 0
    }));
  }, [myProducts, productStatistics]);

    // Memoize product image URLs to prevent recalculation and flickering
    const productImageUrlCache = useMemo(() => {
      const cache = new Map();
      const allProducts = [
        ...(products || []),
        ...(approvedProducts || []),
        ...(pendingProducts || []),
        ...(rejectedProducts || [])
      ];
      
      allProducts.forEach(product => {
        if (product?.id) {
          const imageUrl = getProductPrimaryImageUrl(product);
          cache.set(product.id, imageUrl);
        }
      });
      
      return cache;
    }, [products, approvedProducts, pendingProducts, rejectedProducts]);
  
    // Transform backend order data to match frontend expectations
  const transformOrderData = useCallback((orders) => {
    return orders.map(order => ({
      id: order.orderNumber || order.id,
      customer: order.customerName || 'Unknown Customer',
      product: order.items && order.items.length > 0 
        ? order.items[0].productName 
        : 'Multiple Items',
      productImage: order.items && order.items.length > 0 
        ? (order.items[0].productImageUrl || order.items[0].productImage || null)
        : null,
      productImageUrl: order.items && order.items.length > 0 
        ? (order.items[0].productImageUrl || order.items[0].productImage || null)
        : null,
      productId: order.items && order.items.length > 0 && order.items[0].productId
        ? order.items[0].productId
        : null,
      quantity: order.items && order.items.length > 0 
        ? order.items.reduce((total, item) => total + item.quantity, 0)
        : 0,
      amount: order.totalAmount || 0,
      status: order.status ? order.status.toLowerCase() : 'pending',
      date: order.orderDate 
        ? new Date(order.orderDate).toLocaleDateString('en-IN')
        : new Date().toLocaleDateString('en-IN'),
      shippingAddress: order.shippingAddress 
        ? `${order.shippingAddress.city}, ${order.shippingAddress.state}`
        : 'Address not available',
      customerPhone: order.customerPhone || 'Not available',
      priority: 'Medium',
      paymentStatus: order.paymentStatus || 'PENDING',
      paymentMethod: order.paymentMethod || 'COD'
    }));
  }, []);

  
  
  // Compute seller-specific analytics from real data
  const sellerAnalytics = useMemo(() => {
    if (!orders || !products) {
      return {
        monthlySalesData: [],
        conversionRate: 0,
        returnRate: 0,
        totalViews: 0,
        totalSales: 0
      };
    }

    // Calculate monthly sales data from orders
    const monthlyData = {};
    const last6Months = [];
    const currentDate = new Date();
    
    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthKey = date.toLocaleString('en-US', { month: 'short' });
      const fullKey = `${date.getFullYear()}-${date.getMonth()}`;
      last6Months.push({ monthKey, fullKey });
      monthlyData[fullKey] = { month: monthKey, sales: 0, orders: 0 };
    }

    // Aggregate orders by month
    orders.forEach(order => {
      if (order.orderDate) {
        const orderDate = new Date(order.orderDate);
        const monthKey = `${orderDate.getFullYear()}-${orderDate.getMonth()}`;
        if (monthlyData[monthKey]) {
          monthlyData[monthKey].sales += order.totalAmount || 0;
          monthlyData[monthKey].orders += 1;
        }
      }
    });

    const monthlySalesData = last6Months.map(m => monthlyData[m.fullKey]);

    // Calculate conversion rate (orders / views) - approximate
    const totalViews = products.reduce((sum, p) => sum + (p.analytics?.totalViews || p.views || 0), 0);
    const totalOrders = orders.length;
    const conversionRate = totalViews > 0 ? ((totalOrders / totalViews) * 100).toFixed(1) : 0;

    // Calculate return rate (cancelled / total)
    const cancelledOrders = orders.filter(o => o.status?.toLowerCase() === 'cancelled').length;
    const returnRate = totalOrders > 0 ? ((cancelledOrders / totalOrders) * 100).toFixed(1) : 0;

    return {
      monthlySalesData,
      conversionRate: parseFloat(conversionRate),
      returnRate: parseFloat(returnRate),
      totalViews,
      totalSales: orders.reduce((sum, o) => sum + (o.totalAmount || 0), 0)
    };
  }, [orders, products]);

  // Use seller orders data with status filter
  const recentOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    
    const filteredOrders = orders.filter(order => {
        const status = order.status?.toLowerCase();
      
      // Apply status filter
      if (orderStatusFilter === 'all') {
        return true; // Show all orders
      } else if (orderStatusFilter === 'pending') {
        return status === 'pending';
      } else if (orderStatusFilter === 'delivered') {
        return status === 'delivered';
      } else if (orderStatusFilter === 'shipped') {
        return status === 'shipped';
      } else if (orderStatusFilter === 'cancelled') {
        return status === 'cancelled';
      }
      return true;
    });
    
    return transformOrderData(filteredOrders);
  }, [orders, orderStatusFilter]);

  // Function to get order count from statistics (now stored in DB)
  const getProductOrderCount = (productId) => {
    const stats = getProductStats(productId);
    return stats.totalOrders || 0;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditFormChange = (field, value) => {
    if (field.includes('.')) {
      // Handle nested objects like specifications.finish
      const [parent, child] = field.split('.');
      setEditForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const closeProductDialog = () => {
    setProductDialogOpen(false);
    setResubmittingProduct(false);
  };

  const openProductEditor = (product, { resubmit = false } = {}) => {
    if (!product) return;
    setSelectedProduct(product);
    setResubmittingProduct(resubmit);
    
    // Define valid dropdown options
    const validCategories = ['Marble', 'Granite', 'Tiles', 'Sandstone', 'Indian Marble', 'Quartz', 'Slate', 'Kota stone', 'Quartzite', 'Others'];
    const validFinishes = ['Polished', 'Honed', 'Brushed', 'Sandblasted', 'Flamed', 'Others'];
    const validColors = ['White', 'Beige', 'Grey', 'Red', 'Blue', 'Green', 'Black', 'Brown', 'Pink', 'Others'];
    
    // Check if category is in dropdown, if not, treat as "Others"
    const productCategory = product.category || '';
    const isCategoryCustom = productCategory && !validCategories.includes(productCategory);
    const displayCategory = isCategoryCustom ? 'Others' : productCategory;
    const customCategory = isCategoryCustom ? productCategory : '';
    
    // Check if finish is in dropdown, if not, treat as "Others"
    const productFinish = product.specifications?.finish || '';
    const isFinishCustom = productFinish && !validFinishes.includes(productFinish);
    const displayFinish = isFinishCustom ? 'Others' : productFinish;
    const customFinish = isFinishCustom ? productFinish : '';
    
    // Check if color is in dropdown, if not, treat as "Others"
    const productColor = product.specifications?.color || '';
    const isColorCustom = productColor && !validColors.includes(productColor);
    const displayColor = isColorCustom ? 'Others' : productColor;
    const customColor = isColorCustom ? productColor : '';
    
    setEditForm({
      name: product.name || '',
      category: displayCategory, // Use display value (either original or "Others")
      price: product.price || '',
      priceUnit: product.priceUnit || 'PER_SQ_FT',
      stockQuantity: product.stockQuantity || '',
      minimumOrderQuantity: product.minimumOrderQuantity || '',
      description: product.description || '',
      origin: product.origin || '',
      lotSize: product.lotSize || '',
      lotPercentage: product.lotPercentage || '',
      // Default lot: prefer the first entry in product.lots if it exists
      lotName: (product.lots && product.lots.length > 0 ? product.lots[0].lotName : '') || '',
      lotLength: (product.lots && product.lots.length > 0 ? product.lots[0].lotLength : product.lotLength) || '',
      lotWidth: (product.lots && product.lots.length > 0 ? product.lots[0].lotWidth : product.lotWidth) || '',
      lotNos: (product.lots && product.lots.length > 0 ? product.lots[0].lotNos : product.lotNos) || '',
      lotLocation: (product.lots && product.lots.length > 0 ? product.lots[0].lotLocation : product.lotLocation) || product.lotLocation || '',
      tags: product.tags || [],
      images: product.images || [],
      videoUrls: product.videoUrls || [],
      thumbnailImageIndex: product.images && product.images.length > 0 && product.primaryImageUrl
        ? product.images.findIndex(img => {
            const imgUrl = img.imageUrl || img.url || img.thumbnailUrl;
            return imgUrl === product.primaryImageUrl || imgUrl === product.primaryImageUrl?.replace(/^.*\/api\//, '/api/');
          })
        : null,
      specifications: {
        finish: displayFinish, // Use display value (either original or "Others")
        thickness: product.specifications?.thickness || '',
        color: displayColor // Use display value (either original or "Others")
      },
      customCategory: customCategory, // Set custom value if it was custom
      customFinish: customFinish, // Set custom value if it was custom
      customOrigin: '',
      customColor: customColor // Set custom value if it was custom
    });
    // Populate additional dimension rows from product.lots (skip index 0 = default lot)
    if (product.lots && product.lots.length > 1) {
      setEditFormAdditionalLots(product.lots.slice(1).map(lot => ({
        lotId: lot.lotId || `lot_${Date.now()}`,
        lotLength: lot.lotLength !== null && lot.lotLength !== undefined ? String(lot.lotLength) : '',
        lotWidth: lot.lotWidth !== null && lot.lotWidth !== undefined ? String(lot.lotWidth) : '',
        lotNos: lot.lotNos !== null && lot.lotNos !== undefined ? String(lot.lotNos) : '',
      })));
    } else {
      setEditFormAdditionalLots([]);
    }
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    // Validate Minimum Order SFT against total SFT before saving edit
    if (
      computedEditTotalSft > 0 &&
      parseFloat(editForm.minimumOrderQuantity) > computedEditTotalSft
    ) {
      toast.error(`Minimum Order SFT cannot exceed total SFT (${computedEditTotalSft.toFixed(3)} SFT)`);
      return;
    }

    try {
      setLoading(true);
      console.log('Starting product update...', { selectedProduct: selectedProduct?.id, editForm });

      // Prepare comprehensive product data for API
      const productData = {
        name: editForm.name,
        description: editForm.description,
        category: editForm.category === "Others" ? editForm.customCategory : editForm.category,
        price: parseFloat(editForm.price) || 1,
        priceUnit: editForm.priceUnit || 'PER_SQ_FT',
        stockQuantity: computedEditStockQty || 1,
        minimumOrderQuantity: parseFloat(editForm.minimumOrderQuantity) || 1,
        origin: editForm.origin,
        lotLength: parseFloat(editForm.lotLength) || 0,
        lotWidth: parseFloat(editForm.lotWidth) || 0,
        lotNos: parseInt(editForm.lotNos) || 0,
        lotLocation: editForm.lotLocation,
        lots: [
          {
            lotId: 'default',
            lotLength: parseFloat(editForm.lotLength) || 0,
            lotWidth: parseFloat(editForm.lotWidth) || 0,
            lotNos: parseInt(editForm.lotNos) || 0,
          },
          ...editFormAdditionalLots.map(lot => ({
            lotId: lot.lotId || `lot_${Date.now()}`,
            lotLength: parseFloat(lot.lotLength) || 0,
            lotWidth: parseFloat(lot.lotWidth) || 0,
            lotNos: parseInt(lot.lotNos) || 0,
          }))
        ],
        specifications: {
          finish: editForm.specifications.finish === "Others" ? editForm.customFinish : editForm.specifications.finish,
          thickness: editForm.specifications.thickness,
          color: editForm.specifications.color === "Others" ? editForm.customColor : editForm.specifications.color
        },
        primaryImageUrl: (editForm.thumbnailImageIndex !== null && editForm.thumbnailImageIndex >= 0 && editForm.images && editForm.images[editForm.thumbnailImageIndex])
          ? (editForm.images[editForm.thumbnailImageIndex].imageUrl || editForm.images[editForm.thumbnailImageIndex].url || editForm.images[editForm.thumbnailImageIndex].thumbnailUrl)
          : (editForm.images && editForm.images.length > 0)
            ? (editForm.images[0].imageUrl || editForm.images[0].url || editForm.images[0].thumbnailUrl)
            : selectedProduct?.primaryImageUrl || "",
        images: (editForm.images || []).map(image => {
          // Filter out fields that backend doesn't expect
          const { aspectRatio, primary, fullUrl, aiValidated, aiResult, ...cleanImage } = image;
          return cleanImage;
        }),
        // videoUrls will be set after uploading new videos (see below)
        videoUrls: []
      };
    
    if (resubmittingProduct) {
      productData.approvalStatus = 'PENDING';
      productData.status = 'PENDING_APPROVAL';
      // Preserve approvalComments to identify resubmitted products on admin side
      // Keep the original rejection comments so admin can see it was resubmitted
      // The backend will preserve this if we send it (see ProductServiceImpl line 115-117)
      if (selectedProduct?.approvalComments) {
        productData.approvalComments = selectedProduct.approvalComments;
      }
      productData.approvedBy = null;
      productData.approvedAt = null;
    } else {
      // For regular edits, set approvalStatus to PENDING but preserve approvedAt/approvedBy
      // This helps distinguish edited products (has approvedAt/approvedBy) from new products
      productData.approvalStatus = 'PENDING';
      productData.status = 'PENDING_APPROVAL';
      // Explicitly clear approvalComments to ensure edited products don't have them
      // This helps distinguish edited products (no approvalComments) from resubmitted products (has approvalComments)
      productData.approvalComments = null;
      // Preserve approvedAt and approvedBy to help admin identify edited products
      // These fields will be preserved by backend if they exist in the product
      if (selectedProduct?.approvedAt) {
        productData.approvedAt = selectedProduct.approvedAt;
      }
      if (selectedProduct?.approvedBy) {
        productData.approvedBy = selectedProduct.approvedBy;
      }
    }
      
      // Upload new video files before updating product
      const newVideoFiles = (editForm.videoUrls || []).filter(video => video.file);
      let uploadedVideoUrls = [];

      if (newVideoFiles.length > 0) {
        try {
          const videoFiles = newVideoFiles.map(video => video.file);
          const uploadResponses = await fileService.uploadProductVideos(videoFiles);
          
          uploadedVideoUrls = uploadResponses
            .filter(response => response.success !== false && response.videoUrl)
            .map(response => response.videoUrl);
          
          if (uploadedVideoUrls.length > 0) {
            toast.success(`${uploadedVideoUrls.length} video(s) uploaded successfully!`);
          }
        } catch (error) {
          console.error('Error uploading videos:', error);
          toast.error('Failed to upload some videos. Please try again.');
          // Continue with update even if video upload fails
        }
      }

      // Combine existing video URLs with newly uploaded ones
      const existingVideoUrls = (editForm.videoUrls || [])
        .filter(video => !video.file) // Exclude files that were just uploaded
        .map(video => typeof video === 'string' ? video : (video.url || video.videoUrl || ''))
        .filter(Boolean);
      
      productData.videoUrls = [...existingVideoUrls, ...uploadedVideoUrls];

      console.log('Product data prepared:', productData);
      
      // Use Redux thunk for update
      const resultAction = await dispatch(updateSellerProduct({
        productId: selectedProduct.id,
        productData: productData
      }));
      
      console.log('Update result:', resultAction);
      
      if (updateSellerProduct.fulfilled.match(resultAction)) {
        console.log('Update successful:', resultAction.payload);
        const updatedProduct = resultAction.payload?.product;
        if (updatedProduct) {
          setSelectedProduct(updatedProduct);
        }
      const successMessage = resubmittingProduct
        ? "Product updated and resubmitted for admin approval!"
        : (resultAction.payload?.message || "Product updated successfully!");
      toast.success(successMessage);
      if (resubmittingProduct) {
        setProductSubTab(0);
      }
      closeProductDialog();
        
        // Reset edit form
        setEditForm({
          name: '',
          category: '',
          price: '',
          priceUnit: 'PER_SQ_FT',
          stockQuantity: '',
          minimumOrderQuantity: '',
          description: '',
          origin: '',
          lotSize: '',
          lotPercentage: '',
          lotName: '',
          lotLength: '',
          lotWidth: '',
          lotNos: '',
          lotLocation: '',
          tags: [],
          images: [],
          videoUrls: [],
          specifications: {
            finish: '',
            thickness: '',
            color: ''
          },
          customCategory: '',
          customFinish: '',
          customOrigin: '',
          customColor: ''
        });
        setEditFormAdditionalLots([]);

        // Refresh the products and analytics data to reflect latest changes
        dispatch(fetchSellerProducts({ sellerId: user.id, status: 'ACTIVE', approvalStatus: 'APPROVED' }));
        dispatch(fetchSellerApprovedProducts({ sellerId: user.id }));
        dispatch(fetchSellerPendingProducts({ sellerId: user.id }));
        dispatch(fetchSellerRejectedProducts({ sellerId: user.id }));
        dispatch(fetchSellerOrders({ sellerId: user.id, size: 120 }));
        dispatch(fetchSellerStats(user.id));
      } else {
        console.log('Update failed:', resultAction);
        toast.error(resultAction.payload || "Failed to update product");
      }
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error("An error occurred while updating the product");
    } finally {
      setLoading(false);
    }
  };

  const handleResubmitProductClick = (product) => {
    openProductEditor(product, { resubmit: true });
  };

  const handleProductAction = async (action, product) => {
    switch (action) {
      case "view":
        setSelectedProduct(product);
        setViewDialogOpen(true);
        break;
      case "edit":
        openProductEditor(product);
        break;
      case "resubmit":
        openProductEditor(product, { resubmit: true });
        break;
      case "delete":
        if (window.confirm(`Are you sure you want to delete "${product.name}"?`)) {
          try {
            const resultAction = await dispatch(deleteSellerProduct(product.id));
            if (deleteSellerProduct.fulfilled.match(resultAction)) {
              toast.success(`Product "${product.name}" deleted successfully`);
              // Refresh the products data
              dispatch(fetchSellerProducts({ sellerId: user.id }));
              dispatch(fetchSellerPendingProducts({ sellerId: user.id }));
              dispatch(fetchSellerApprovedProducts({ sellerId: user.id }));
              dispatch(fetchSellerRejectedProducts({ sellerId: user.id }));
            } else {
              toast.error(resultAction.payload || 'Failed to delete product');
            }
          } catch (error) {
            toast.error('Failed to delete product');
          }
        }
        break;
      case "duplicate":
        try {
          const resultAction = await dispatch(duplicateSellerProduct(product));
          if (duplicateSellerProduct.fulfilled.match(resultAction)) {
            toast.success(`Product "${product.name}" duplicated successfully`);
            // Refresh the products data
            dispatch(fetchSellerProducts({ sellerId: user.id }));
            dispatch(fetchSellerPendingProducts({ sellerId: user.id }));
            dispatch(fetchSellerRejectedProducts({ sellerId: user.id }));
          } else {
            toast.error(resultAction.payload || 'Failed to duplicate product');
          }
        } catch (error) {
          toast.error('Failed to duplicate product');
        }
        break;
      default:
        break;
    }
    setAnchorEl(null);
  };

  const handleProductMenuClick = (event, product) => {
    event.preventDefault();
    event.stopPropagation();
    // Ensure we capture the actual button element, not a parent
    const target = event.currentTarget;
    // Get the bounding rectangle of the button relative to viewport
    const rect = target.getBoundingClientRect();
    // Store the element for anchor reference (fallback)
    setProductMenuAnchorEl(target);
    setSelectedProductForMenu(product);
    // Calculate position relative to viewport for accurate positioning
    // anchorPosition uses viewport coordinates (getBoundingClientRect already gives viewport coords)
    setMenuAnchorPosition({
      top: rect.bottom,
      left: rect.right
    });
  };

  const handleProductMenuClose = () => {
    setProductMenuAnchorEl(null);
    setSelectedProductForMenu(null);
    setMenuAnchorPosition(null);
  };

  const handleProductMenuAction = async (action) => {
    if (selectedProductForMenu) {
      await handleProductAction(action, selectedProductForMenu);
    }
    handleProductMenuClose();
  };

  const handleOrderAction = (action, order) => {
    switch (action) {
      case "view":
        setSelectedOrder(order);
        setOrderDialogOpen(true);
        break;
      case "ship":
        setOrderToShip(order);
        setShipDialogOpen(true);
        setShippingForm({ trackingNumber: '', carrier: '' });
        break;
      case "deliver":
        handleDeliverOrder(order);
        break;
      case "cancel":
        // Order cancelled - no toast needed
        break;
      default:
        break;
    }
  };

  const handleShipOrder = async () => {
    if (!orderToShip || !shippingForm.trackingNumber.trim()) {
      toast.error('Please enter a tracking number');
      return;
    }

    setShippingLoading(true);
    try {
      await dispatch(shipOrder({
        orderId: orderToShip.id,
        trackingNumber: shippingForm.trackingNumber,
        carrier: shippingForm.carrier || 'Standard'
      })).unwrap();
      
      toast.success(`Order ${orderToShip.id} marked as shipped successfully!`);
      setShipDialogOpen(false);
      setOrderToShip(null);
      setShippingForm({ trackingNumber: '', carrier: '' });
      
      // Only refresh pending orders count (lighter operation)
      // The shipped order is already updated in Redux state by shipOrder action
      if (user?.id) {
        dispatch(fetchSellerPendingOrders({ sellerId: user.id }));
      }
    } catch (error) {
      toast.error(error || 'Failed to ship order');
    } finally {
      setShippingLoading(false);
    }
  };

  const handleDeliverOrder = async (order) => {
    try {
      await dispatch(deliverOrder(order.id)).unwrap();
      toast.success(`Order ${order.id} marked as delivered successfully!`);
      
      // Only refresh pending orders count (lighter operation)
      // The delivered order is already updated in Redux state by deliverOrder action
      if (user?.id) {
        dispatch(fetchSellerPendingOrders({ sellerId: user.id }));
      }
    } catch (error) {
      toast.error(error || 'Failed to deliver order');
    }
  };

  // Handle bulk upload
  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setBulkUploadLoading(true);
    setBulkUploadResults(null);

    try {
      const formData = new FormData();
      formData.append('file', bulkUploadFile);
      // Use filtered ZIP if available (contains only valid images), otherwise use original
      const zipFileToUpload = bulkUploadFilteredZipFile || bulkUploadImagesFile;
      if (zipFileToUpload) {
        formData.append('imagesZip', zipFileToUpload);
      }

      const response = await fetch(`${API_BASE_URL}/products/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setBulkUploadResults(result);
        toast.success(`Bulk upload completed! ${result.successCount} products uploaded successfully.`);
        
        // Refresh products data
        if (user?.id) {
          dispatch(fetchSellerPendingProducts({ sellerId: user.id }));
          dispatch(fetchSellerApprovedProducts({ sellerId: user.id }));
        }
      } else {
        toast.error(result.message || 'Failed to upload products');
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error('Failed to upload products. Please try again.');
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const handleBulkUploadFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a CSV or Excel file');
        return;
      }
      
      setBulkUploadFile(file);
    }
  };

  const handleBulkUploadImagesFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.zip')) {
        toast.error('Please select a ZIP file containing product images');
        return;
      }
      
      // Validate images in ZIP before setting
      setValidatingBulkImages(true);
      setBulkUploadValidationMessages([]);
      
      try {
        await validateBulkUploadImages(file);
        setBulkUploadImagesFile(file);
      } catch (error) {
        console.error('Error validating bulk upload images:', error);
        toast.error('Failed to validate images. Please try again.');
      } finally {
        setValidatingBulkImages(false);
      }
    }
  };

  // Validate images from ZIP file and create filtered ZIP with only valid images
  const validateBulkUploadImages = async (zipFile) => {
    const validationMessages = [];
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
    
    try {
      // Use JSZip to extract images from ZIP
      const zip = await JSZip.loadAsync(zipFile);
      
      const imageFiles = [];
      const nonImageFiles = []; // Store non-image files to include in filtered ZIP
      
      // Get all files from ZIP
      zip.forEach((relativePath, file) => {
        if (!file.dir) {
          const fileName = relativePath.split('/').pop();
          const ext = fileName.split('.').pop()?.toLowerCase();
          if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) {
            imageFiles.push({ relativePath, file, fileName });
          } else {
            // Keep non-image files (like videos) in the filtered ZIP
            nonImageFiles.push({ relativePath, file });
          }
        }
      });

      if (imageFiles.length === 0) {
        validationMessages.push({
          type: 'warning',
          filename: 'ZIP File',
          message: 'No image files found in ZIP. Please ensure ZIP contains image files.'
        });
        setBulkUploadValidationMessages(validationMessages);
        setBulkUploadFilteredZipFile(null);
        return;
      }

      // Create a new ZIP for valid images only
      const filteredZip = new JSZip();
      let validImageCount = 0;
      let invalidImageCount = 0;

      // Validate each image and add only valid ones to filtered ZIP
      for (const { relativePath, file: zipEntry, fileName } of imageFiles) {
        try {
          // Get image blob from ZIP
          const blob = await zipEntry.async('blob');
          const imageFile = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
          
          // Validate with AI service
          const formData = new FormData();
          formData.append('file', imageFile);
          
          try {
            const response = await axios.post(`${API_BASE_URL}/files/analyze-image`, formData);

            const aiResult = response.data;

            if (aiResult.error) {
              // Invalid image - do not add to filtered ZIP
              invalidImageCount++;
              validationMessages.push({
                type: 'error',
                filename: fileName,
                message: aiResult.error || aiResult.detected_image || 'Image is not suitable for our catalog'
              });
            } else if (aiResult.warning) {
              // Valid but low quality - add to ZIP with warning
              validImageCount++;
              filteredZip.file(relativePath, blob);
              validationMessages.push({
                type: 'warning',
                filename: fileName,
                message: aiResult.warning || 'Image quality is low'
              });
            } else if (aiResult.success || aiResult.is_stone) {
              // Valid image - add to filtered ZIP
              validImageCount++;
              filteredZip.file(relativePath, blob);
              validationMessages.push({
                type: 'success',
                filename: fileName,
                message: aiResult.success || 'Image validated successfully'
              });
            } else {
              // Unknown result - don't add to ZIP
              invalidImageCount++;
              validationMessages.push({
                type: 'warning',
                filename: fileName,
                message: 'Unable to validate image. Image will not be included.'
              });
            }
          } catch (aiError) {
            console.warn(`AI validation failed for ${fileName}:`, aiError);
            // If AI service fails, don't add image to be safe
            invalidImageCount++;
            validationMessages.push({
              type: 'warning',
              filename: fileName,
              message: 'AI validation unavailable. Image will not be included for safety.'
            });
          }
        } catch (fileError) {
          console.error(`Error processing ${fileName}:`, fileError);
          invalidImageCount++;
          validationMessages.push({
            type: 'error',
            filename: fileName,
            message: `Failed to process: ${fileError.message || 'Unknown error'}`
          });
        }
      }

      // Add non-image files (videos, etc.) to filtered ZIP
      for (const { relativePath, file: zipEntry } of nonImageFiles) {
        try {
          const blob = await zipEntry.async('blob');
          filteredZip.file(relativePath, blob);
        } catch (error) {
          console.warn(`Error adding non-image file ${relativePath}:`, error);
        }
      }

      // Generate filtered ZIP blob
      if (validImageCount > 0 || nonImageFiles.length > 0) {
        const filteredZipBlob = await filteredZip.generateAsync({ type: 'blob' });
        const filteredZipFile = new File([filteredZipBlob], `filtered_${zipFile.name}`, { type: 'application/zip' });
        setBulkUploadFilteredZipFile(filteredZipFile);
        
        if (invalidImageCount > 0) {
          validationMessages.unshift({
            type: 'info',
            filename: 'Summary',
            message: `${validImageCount} valid image(s) will be uploaded. ${invalidImageCount} invalid image(s) have been excluded.`
          });
        }
      } else {
        setBulkUploadFilteredZipFile(null);
        validationMessages.unshift({
          type: 'error',
          filename: 'Summary',
          message: 'No valid images found. Please upload images of interior design materials.'
        });
      }
      
      setBulkUploadValidationMessages(validationMessages);
    } catch (error) {
      console.error('Error extracting ZIP:', error);
      validationMessages.push({
        type: 'error',
        filename: zipFile.name,
        message: 'Failed to extract ZIP file. Please ensure it is a valid ZIP archive.'
      });
      setBulkUploadValidationMessages(validationMessages);
      setBulkUploadFilteredZipFile(null);
      throw error;
    }
  };


  const handleCloseBulkUploadDialog = () => {
    setBulkUploadDialogOpen(false);
    setBulkUploadFile(null);
    setBulkUploadImagesFile(null);
    setBulkUploadFilteredZipFile(null);
    setBulkUploadResults(null);
    setBulkUploadValidationMessages([]);
  };

  // Handle template download
  const handleDownloadTemplate = () => {
    const headers = [
      'productName',
      'description',
      'price',
      'priceUnit',
      'category',
      'stockQuantity',
      'origin',
      'finish',
      'color',
      'thickness',
      'lotLength',
      'lotWidth',
      'lotNos',
      'lotSft',
      'lotLocation',
      'tags',
      'image1',
      'image2',
      'image3'
    ];

    // Create sample data row to show format
    const sampleData = [
      'Italian Marble Premium',
      'High-quality Italian marble with beautiful veining',
      '150.00',
      'PER_SQ_FT',
      'MARBLE',
      '100',
      '10',
      'ITALY',
      'Polished',
      'White',
      '18',
      '10.5',
      '8.2',
      '5',
      '430.5',
      'Warehouse A',
      'premium;italian;marble',
      'italian-marble-main.jpg',
      'italian-marble-detail.jpg',
      ''
    ];

    // Create CSV content
    const csvContent = [
      headers.join(','),
      sampleData.join(',')
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'bulk_upload_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Template downloaded successfully!');
  };

  const sidebarItems = useMemo(() => [
    { label: "Overview", icon: <DashboardIcon />, value: 0 },
    { label: "Products", icon: <Inventory />, value: 1 },
    { label: "Orders", icon: <ShoppingCart />, value: 2 },
    { label: "Analytics", icon: <Assessment />, value: 3 },
    { label: "Reviews", icon: <RateReview />, value: 4 },
    { label: "Promotions", icon: <LocalOffer />, value: 5 },
    { label: "Visit Requests", icon: <HomeWork />, value: 6 },
    { label: "Lot Requests", icon: <Business />, value: 9 },
    { label: "Profile", icon: <Storefront />, value: 7 },
    { label: "Settings", icon: <Settings />, value: 8 },
    { label: "Wishlist", icon: <FavoriteBorder />, value: 10, path: "/wishlist" },
    { label: "My Cart", icon: <ShoppingBag />, value: 11, path: "/cart" },
  ], []);

  const topProducts = useMemo(() => [
    {
      name: "Premium Carrara Marble",
      revenue: 125000,
      orders: 45,
      rating: 4.8,
    },
    { name: "Black Galaxy Granite", revenue: 98000, orders: 32, rating: 4.6 },
    {
      name: "Italian Porcelain Tiles",
      revenue: 76000,
      orders: 28,
      rating: 4.7,
    },
    { name: "Calacatta Gold Marble", revenue: 54000, orders: 21, rating: 4.5 },
  ], []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case "active":
        return "success";
      case "pending":
        return "warning";
      case "out_of_stock":
        return "error";
      case "shipped":
        return "info";
      case "delivered":
        return "success";
      default:
        return "default";
    }
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case "active":
        return <CheckCircle />;
      case "pending":
        return <Pending />;
      case "out_of_stock":
        return <Warning />;
      case "shipped":
        return <LocalShipping />;
      case "delivered":
        return <CheckCircle />;
      default:
        return <Cancel />;
    }
  }, []);

  // Function to format time ago
  const formatTimeAgo = useCallback((dateString) => {
    if (!dateString) return 'Unknown time';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} ${Math.floor(diffInSeconds / 60) === 1 ? 'minute' : 'minutes'} ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} ${Math.floor(diffInSeconds / 3600) === 1 ? 'hour' : 'hours'} ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} ${Math.floor(diffInSeconds / 86400) === 1 ? 'day' : 'days'} ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} ${Math.floor(diffInSeconds / 604800) === 1 ? 'week' : 'weeks'} ago`;
    return `${Math.floor(diffInSeconds / 2592000)} ${Math.floor(diffInSeconds / 2592000) === 1 ? 'month' : 'months'} ago`;
  }, []);

  // Generate recent activities from real data
  const recentActivities = useMemo(() => {
    const activities = [];
    
    // Add order activities
    const allOrders = [...(orders || []), ...(pendingOrders || [])];
    allOrders.forEach(order => {
      const date = order.updatedAt || order.createdAt || order.orderDate;
      if (!date) return;
      
      const status = order.status?.toLowerCase() || 'pending';
      let activity = null;
      
      if (status === 'completed' || status === 'delivered') {
        activity = {
          type: 'order',
          icon: <CheckCircle />,
          color: 'success.main',
          primary: `Order #${order.orderNumber || order.id} completed`,
          secondary: formatTimeAgo(date),
          date: new Date(date)
        };
      } else if (status === 'shipped') {
        activity = {
          type: 'order',
          icon: <LocalShipping />,
          color: 'info.main',
          primary: `Order #${order.orderNumber || order.id} shipped`,
          secondary: formatTimeAgo(date),
          date: new Date(date)
        };
      } else if (status === 'pending' || status === 'pending_payment') {
        activity = {
          type: 'order',
          icon: <Pending />,
          color: 'warning.main',
          primary: `New order #${order.orderNumber || order.id} received`,
          secondary: formatTimeAgo(date),
          date: new Date(date)
        };
      }
      
      if (activity) activities.push(activity);
    });
    
    // Add product activities
    const allProducts = [
      ...(pendingProducts || []),
      ...(approvedProducts || []),
      ...(rejectedProducts || [])
    ];
    allProducts.forEach(product => {
      const date = product.updatedAt || product.createdAt;
      if (!date) return;
      
      const approvalStatus = product.approvalStatus?.toLowerCase();
      let activity = null;
      
      if (approvalStatus === 'approved') {
        activity = {
          type: 'product',
          icon: <CheckCircle />,
          color: 'success.main',
          primary: `Product "${product.name}" approved`,
          secondary: formatTimeAgo(date),
          date: new Date(date)
        };
      } else if (approvalStatus === 'rejected') {
        activity = {
          type: 'product',
          icon: <Cancel />,
          color: 'error.main',
          primary: `Product "${product.name}" rejected`,
          secondary: formatTimeAgo(date),
          date: new Date(date)
        };
      } else if (approvalStatus === 'pending' || !approvalStatus) {
        // Only show recently added products (within last 7 days)
        const productDate = new Date(date);
        const daysDiff = (new Date() - productDate) / (1000 * 60 * 60 * 24);
        if (daysDiff <= 7) {
          activity = {
            type: 'product',
            icon: <Add />,
            color: 'primary.main',
            primary: `New product added: ${product.name}`,
            secondary: formatTimeAgo(date),
            date: new Date(date)
          };
        }
      }
      
      if (activity) activities.push(activity);
    });
    
    // Add review activities
    if (reviews && reviews.length > 0) {
      reviews.forEach(review => {
        const date = review.createdAt || review.updatedAt;
        if (!date) return;
        
        const rating = review.rating || 0;
        const productName = review.productName || review.product?.name || 'Product';
        
        activities.push({
          type: 'review',
          icon: <Star />,
          color: rating >= 4 ? 'warning.main' : rating >= 3 ? 'info.main' : 'error.main',
          primary: `Received ${rating}-star review${rating === 5 ? ' ⭐' : ''} for ${productName}`,
          secondary: formatTimeAgo(date),
          date: new Date(date)
        });
      });
    }
    
    // Sort by date (most recent first) and limit to 10
    return activities
      .sort((a, b) => b.date - a.date)
      .slice(0, 10);
  }, [orders, pendingOrders, pendingProducts, approvedProducts, rejectedProducts, reviews, formatTimeAgo]);

  const OverviewTab = () => (
    <Box>
      {/* Charts */}
      <Box 
        sx={{ 
          width: { xs: '100%', sm: '100%' },
          maxWidth: '100%',
          mb: 4,
          px: { xs: 1, sm: 2 },
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 1 },
            height: { xs: 'auto', md: 450 },
            width: '100%'
          }}
        >
          <Card
            sx={{ 
              flex: 1,
              display: "flex", 
              flexDirection: "column",
              height: { xs: 400, md: "100%" },
              minWidth: 0
            }}
          >
            <CardContent
              sx={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  textAlign: 'center',
                  fontWeight: 600
                }}
              >
                Sales Performance
              </Typography>
              <Box sx={{ flex: 1, minHeight: 300, position: "relative" }}>
                {/* Show loading only on initial load when no cached data exists */}
                {/* During refresh (ordersLoading=true but hasInitialOrdersRef=true), show cached chart data */}
                {ordersLoading && !hasInitialOrdersRef.current && (!orders || orders.length === 0) && cachedSalesDataRef.current.length === 0 ? (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      Loading chart data...
                    </Typography>
                  </Box>
                ) : salesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={salesData}
                      margin={{ top: 16, right: 24, left: 0, bottom: 0 }}
                      syncId="dashboard-charts"
                    >
                      <defs>
                        <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#6366F1" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#6366F1" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="4 8"
                        stroke="#E2E8F0"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "#64748b", fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        yAxisId="sales"
                        tickFormatter={formatCompactCurrency}
                        tick={{ fill: "#64748b", fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        width={70}
                      />
                      <YAxis
                        yAxisId="orders"
                        orientation="right"
                        allowDecimals={false}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#94a3b8", fontSize: 12 }}
                        width={40}
                      />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: "#0f172a",
                          borderRadius: 12,
                          border: "none",
                          color: "#f8fafc",
                          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.25)",
                        }}
                        formatter={(value, name) => {
                          if (name === "Revenue") {
                            return [formatCurrency(value), "Revenue"];
                          }
                          if (name === "Orders") {
                            return [value, "Orders"];
                          }
                          return [value, name];
                        }}
                      />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        iconType="circle"
                        wrapperStyle={{ paddingTop: 8 }}
                      />
                      <Area
                        type="monotone"
                        name="Revenue"
                        dataKey="sales"
                        stroke="#6366F1"
                        strokeWidth={3}
                        fill="url(#salesGradient)"
                        yAxisId="sales"
                        dot={{ r: 4, strokeWidth: 2, stroke: "#fff", fill: "#4F46E5" }}
                        activeDot={{
                          r: 6,
                          strokeWidth: 0,
                          fill: "#4338CA",
                        }}
                      />
                      <Line
                        type="monotone"
                        name="Orders"
                        dataKey="orders"
                        yAxisId="orders"
                        stroke="#F97316"
                        strokeWidth={2.4}
                        dot={{ r: 4, stroke: "#F97316", strokeWidth: 2, fill: "#fff" }}
                        activeDot={{ r: 6 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      color: "#64748b",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      No sales activity yet
                    </Typography>
                    <Typography variant="body2">
                      Orders will appear here once customers start purchasing from you.
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card
            sx={{ 
              flex: 1,
              display: "flex", 
              flexDirection: "column",
              height: { xs: 400, md: "100%" },
              minWidth: 0
            }}
          >
            <CardContent
              sx={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  textAlign: 'center',
                  fontWeight: 600
                }}
              >
                Category Performance
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                {/* Show loading only on initial load when no cached data exists */}
                {/* During refresh (ordersLoading=true but hasInitialOrdersRef=true), show cached chart data */}
                {ordersLoading && !hasInitialOrdersRef.current && (!orders || orders.length === 0) && cachedCategoryPerformanceRef.current.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      Loading chart data...
                    </Typography>
                  </Box>
                ) : categoryPerformance.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryPerformance}
                        dataKey="sales"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={115}
                        paddingAngle={4}
                        cornerRadius={8}
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name} • ${(percent * 100).toFixed(1)}%`
                        }
                      >
                        {categoryPerformance.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            stroke="rgba(15, 23, 42, 0.08)"
                            strokeWidth={1}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: "white",
                          borderRadius: 12,
                          border: "none",
                          color: "#f8fafc",
                          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.25)",
                        }}
                        formatter={(value, name, props) => [
                          formatCurrency(value),
                          `${props.payload.name} • ${props.payload.percentage.toFixed(
                            1
                          )}%`,
                        ]}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        iconType="circle"
                        wrapperStyle={{ paddingTop: 8 }}
                        formatter={(value) => value}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      color: "white",
                      px: 2,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      No category insights yet
                    </Typography>
                    <Typography variant="body2">
                      Once your orders start flowing in, we’ll break down revenue by
                      product category here.
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => {
                        if (!isSellerApproved) {
                          // Seller approval warning - no toast needed
                          return;
                        }
                        // Reset form for new product
                        setNewProduct({
                          name: "",
                          category: "",
                          price: "",
                          priceUnit: "PER_SQ_FT",
                          stockQuantity: "",
                          minimumOrderQuantity: "",
                          description: "",
                          origin: "",
                          lotSize: "",
                          lotPercentage: "",
                          lotName: "",
                          lotLength: "",
                          lotWidth: "",
                          lotNos: "",
                          lotLocation: "",
                          tags: [],
                          images: [],
                          videoUrls: [],
                          specifications: {
                            finish: "",
                            thickness: "",
                            color: ""
                          },
                          customCategory: "",
                          customFinish: "",
                          customOrigin: "",
                          customColor: ""
                        });
                        setNewProductAdditionalLots([]);
                        setProductErrors({});
                        setAddProductDialogOpen(true);
                      }}
                      disabled={!isSellerApproved}
                    >
                      Add Product
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Upload />}
                    onClick={() => setBulkUploadDialogOpen(true)}
                  >
                    Bulk Upload
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Campaign />}
                    onClick={() => setActiveTab(5)}
                  >
                    Create Promotion
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Analytics />}
                    onClick={() => setActiveTab(3)}
                  >
                    View Analytics
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Recent Activity
              </Typography>
              {recentActivities.length > 0 ? (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {recentActivities.map((activity, index) => (
                    <ListItem 
                      key={index}
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                        '&:hover': {
                          bgcolor: 'grey.100',
                        },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: activity.color, width: 40, height: 40 }}>
                          {activity.icon}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                            {activity.primary}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {activity.secondary}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No recent activity to display
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const productTabConfig = useMemo(() => {
    // Handle null (not fetched yet) vs empty array (fetched but empty)
    const pendingList = Array.isArray(pendingProducts) ? pendingProducts : [];
    const approvedList = Array.isArray(approvedProducts) ? approvedProducts : [];
    const rejectedList = Array.isArray(rejectedProducts) ? rejectedProducts : [];

    // Filter products by date range
    const filterByDateRange = (products) => {
      // If dateRange is 'all' or not set, return all products
      if (!dateRange || dateRange === 'all') {
        return products;
      }
      
      const now = new Date();
      let daysAgo = 0;
      
      switch (dateRange) {
        case '7d':
          daysAgo = 7;
          break;
        case '30d':
          daysAgo = 30;
          break;
        case '90d':
          daysAgo = 90;
          break;
        default:
          // If dateRange doesn't match any case, return all products
          return products;
      }
      
      const cutoffDate = new Date(now);
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
      // Set time to start of day for accurate comparison
      cutoffDate.setHours(0, 0, 0, 0);
      
      return products.filter((product) => {
        // Try to get the product date from createdAt or updatedAt
        let productDate = null;
        if (product.createdAt) {
          productDate = new Date(product.createdAt);
        } else if (product.updatedAt) {
          productDate = new Date(product.updatedAt);
        }
        
        // If no date found, exclude the product
        if (!productDate || isNaN(productDate.getTime())) {
          return false;
        }
        
        // Set time to start of day for accurate comparison
        productDate.setHours(0, 0, 0, 0);
        
        // Return true if product date is on or after cutoff date
        return productDate >= cutoffDate;
      });
    };

    // Apply date range filter only (search and category are now filtered on backend)
    const applyAllFilters = (products) => {
      // Only apply date range filter client-side since backend handles search and category
      return filterByDateRange(products);
    };

    switch (productSubTab) {
      case 0:
        return {
          products: applyAllFilters(pendingList),
          loading: pendingProductsLoading,
          title: "Pending Approval Products",
          emptyMessage: "No products pending approval",
          emptyDescription: "All your products have been reviewed or you haven't added any products yet.",
          hasFetched: pendingProducts !== null // Track if we've fetched (null = not fetched, array = fetched)
        };
      case 1:
        return {
          products: applyAllFilters(approvedList),
          loading: approvedProductsLoading,
          title: "Approved Products",
          emptyMessage: "No approved products",
          emptyDescription: "You don't have any approved products yet. Add products and wait for approval.",
          hasFetched: approvedProducts !== null // Track if we've fetched (null = not fetched, array = fetched)
        };
      case 2:
      default:
        return {
          products: applyAllFilters(rejectedList),
          loading: rejectedProductsLoading,
          title: "Rejected Products",
          emptyMessage: "No rejected products",
          emptyDescription: "Great news! None of your listings have been rejected. If a product is rejected, you'll see it here with feedback.",
          hasFetched: rejectedProducts !== null // Track if we've fetched (null = not fetched, array = fetched)
        };
    }
  }, [
    productSubTab,
    pendingProducts,
    approvedProducts,
    rejectedProducts,
    pendingProductsLoading,
    approvedProductsLoading,
    rejectedProductsLoading,
    dateRange
    // Note: productSearchFilter and productCategoryFilter are filtered on backend, 
    // so they don't need to be dependencies here
  ]);

  const ProductsTab = () => {
    // Track which product cards have already been animated to prevent re-animation on re-renders
    const animatedCardsRef = useRef(new Set());
    
    // Memoize currentProducts to prevent unnecessary re-renders when productTabConfig object reference changes
    const currentProducts = useMemo(() => productTabConfig.products || [], [productTabConfig.products]);
    const currentLoading = productTabConfig.loading;
    const currentTitle = productTabConfig.title;
    const currentEmptyMessage = productTabConfig.emptyMessage;
    const currentEmptyDescription = productTabConfig.emptyDescription;
    const isRejectedTab = productSubTab === 2;
    
    // Memoize the onChange handler - update local input value (not debounced filter)
    // Use useCallback with empty deps to ensure stable reference and prevent cursor jump
    const handleSearchChange = useCallback((e) => {
      const value = e.target.value;
      setProductSearchInput(value);
    }, []);
    
    // Memoize the clear handler - clear both input and filter
    const handleClearSearch = useCallback((e) => {
      e.stopPropagation();
      setProductSearchInput('');
      setProductSearchFilter('');
    }, []);
    
    // Memoize the TextField to prevent unnecessary re-renders and cursor jumping
    const searchTextField = useMemo(() => (
      <TextField
        variant="outlined"
        placeholder="Search products..."
        value={productSearchInput}
        onChange={handleSearchChange}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: productSearchInput && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClearSearch}
                edge="end"
              >
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 200,
          flex: 1,
          maxWidth: 300,
          '& .MuiOutlinedInput-root': {
            height: '36.5px',
            '&:hover fieldset': {
              borderColor: 'primary.main',
            },
          },
        }}
      />
    ), [productSearchInput, handleSearchChange, handleClearSearch]);

    // Get unique categories from all products for filter dropdown
    const allProductsForCategories = useMemo(() => {
      const all = [
        ...(pendingProducts || []),
        ...(approvedProducts || []),
        ...(rejectedProducts || [])
      ];
      return all;
    }, [pendingProducts, approvedProducts, rejectedProducts]);

    const availableCategoriesForFilter = useMemo(() => {
      const categorySet = new Set();
      allProductsForCategories.forEach((product) => {
        if (product.category) {
          categorySet.add(product.category);
        }
      });
      return Array.from(categorySet).sort();
    }, [allProductsForCategories]);

    const renderEmptyState = () => (
      <Box
        sx={{
          textAlign: "center",
          py: 12,
          bgcolor: "white",
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
        }}
      >
        <Inventory sx={{ fontSize: 80, color: "#94a3b8", mb: 3 }} />
        <Typography variant="h5" color="#64748b" gutterBottom sx={{ fontWeight: 600 }}>
          {currentEmptyMessage}
        </Typography>
        <Typography variant="body1" color="#94a3b8" sx={{ mb: 4, maxWidth: 400, mx: "auto" }}>
          {currentEmptyDescription}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            if (!isSellerApproved) {
              return;
            }
            setAddProductDialogOpen(true);
          }}
          disabled={!isSellerApproved}
          sx={{
            bgcolor: "#3b82f6",
            "&:hover": {
              bgcolor: "#2563eb"
            },
            px: 4,
            py: 1.5,
            fontSize: "1rem"
          }}
        >
          Add Your First Product
        </Button>
      </Box>
    );

    const renderRejectedTable = () => (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: "0 16px 40px rgba(15,23,42,0.08)",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: "#1e293b" }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1e293b" }}>Cancellation Reason</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#1e293b" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentProducts.map((product) => {
              // Use cached image URL to prevent recalculation and flickering
              const imageUrl = productImageUrlCache.get(product.id) || null;
              return (
                <TableRow key={product.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        key={`product-avatar-${product.id}-${imageUrl || 'no-image'}`}
                        variant="rounded"
                        src={imageUrl || undefined}
                        alt={product.name}
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: "#f1f5f9",
                          borderRadius: 2,
                          fontWeight: 700,
                          color: "#1e293b",
                        }}
                        imgProps={{
                          loading: 'lazy',
                          decoding: 'async',
                          crossOrigin: 'anonymous',
                        }}
                      >
                        {product.name?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}
                        >
                          {product.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b", mb: 0.5 }}>
                          {product.category}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#059669", fontWeight: 600 }}>
                          ₹{product.price}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "#b91c1c", whiteSpace: "pre-wrap" }}
                    >
                      {product.approvalComments || "No reason provided."}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Refresh />}
                        onClick={() => handleResubmitProductClick(product)}
                        sx={{
                          bgcolor: "#f97316",
                          color: "white",
                          "&:hover": { bgcolor: "#ea580c" },
                        }}
                      >
                        Resubmit
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => handleProductAction("delete", product)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );

    return (
      <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", p: 3 }}>

        {/* Main Content Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            bgcolor: "white",
            p: 2,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
          }}
        >
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: "#1e293b",
                mb: 0.5,
                fontSize: "1.25rem"
              }}
            >
              Product Management
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: "#64748b",
                fontSize: "0.9rem"
              }}
            >
              Manage your products and track their performance
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              startIcon={<Upload />}
              variant="outlined"
              onClick={() => setBulkUploadDialogOpen(true)}
              sx={{
                borderColor: "#e2e8f0",
                color: "#64748b",
                "&:hover": {
                  borderColor: "#2C3E50",
                  color: "#2C3E50",
                  bgcolor: "#eff6ff"
                }
              }}
            >
              Bulk Upload
            </Button>
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={() => {
                if (!isSellerApproved) {
                  // Seller approval warning - no toast needed
                  return;
                }
                setAddProductDialogOpen(true);
              }}
              disabled={!isSellerApproved}
              sx={{
                bgcolor: "#2C3E50",
                "&:hover": {
                  bgcolor: "#2C3E50"
                }
              }}
            >
              Add Product
            </Button>
          </Box>
        </Box>

        {/* Separator Line */}
        <Box 
          sx={{ 
            height: "1px", 
            bgcolor: "#e2e8f0", 
            mb: 3,
            mx: 1
          }} 
        />

        {/* Filter Tabs */}
        <Box 
          sx={{ 
            mb: 3
          }}
        >
          <Tabs 
            value={productSubTab} 
            onChange={(event, newValue) => setProductSubTab(newValue)}
            aria-label="product status tabs"
            sx={{ 
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                color: "#64748b",
                fontSize: "0.875rem",
                py: 1.5,
                "&.Mui-selected": {
                  color: "#3b82f6",
                  fontWeight: 600
                }
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#3b82f6",
                height: 2
              }
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Pending sx={{ fontSize: "1rem" }} />
                  <span>Pending Approval</span>
                  <Box 
                    sx={{ 
                      bgcolor: "#f59e0b", 
                      color: "white", 
                      borderRadius: "50%", 
                      width: 20, 
                      height: 20, 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center", 
                      fontSize: "0.75rem",
                      fontWeight: 600
                    }}
                  >
                    {pendingProducts.length}
                  </Box>
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircle sx={{ fontSize: "1rem" }} />
                  <span>Approved</span>
                  <Box 
                    sx={{ 
                      bgcolor: "#10b981", 
                      color: "white", 
                      borderRadius: "50%", 
                      width: 20, 
                      height: 20, 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center", 
                      fontSize: "0.75rem",
                      fontWeight: 600
                    }}
                  >
                    {approvedProducts.length}
                  </Box>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Cancel sx={{ fontSize: "1rem" }} />
                  <span>Rejected</span>
                  <Box
                    sx={{
                      bgcolor: "#ef4444",
                      color: "white",
                      borderRadius: "50%",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                      fontWeight: 600
                    }}
                  >
                    {rejectedProducts.length}
                  </Box>
                </Box>
              }
            />
          </Tabs>
          
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              py: 2,
              mt: 2
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", flex: 1 }}>
              {/* Search Filter */}
              {searchTextField}

              {/* Category Filter - Multiple Selection */}
              <FormControl 
                variant="outlined" 
                sx={{ 
                  minWidth: 200,
                  height: '36.5px',
                  '& .MuiOutlinedInput-root': {
                    height: '36.5px'
                  },
                  '& .MuiInputLabel-root': {
                    transform: productCategoryFilter.length > 0 
                      ? 'translate(14px, -9px) scale(0.75)' 
                      : 'translate(14px, 9px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)'
                    }
                  }
                }}
              >
                <InputLabel id="category-filter-label">Category</InputLabel>
                <Select
                  multiple
                  labelId="category-filter-label"
                  value={productCategoryFilter}
                  label="Category"
                  onChange={(e) => setProductCategoryFilter(e.target.value)}
                  variant="outlined"
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <em style={{ color: '#999', textAlign: 'center', display: 'block' }}>All Categories</em>;
                    }
                    if (selected.length === 1) {
                      return selected[0];
                    }
                    return `${selected.length} categories selected`;
                  }}
                  sx={{
                    height: '36.5px',
                    '& .MuiSelect-select': {
                      padding: '8px 14px',
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}
                >
                  {availableCategoriesForFilter.map((category) => (
                    <MenuItem key={category} value={category}>
                      <Checkbox checked={productCategoryFilter.indexOf(category) > -1} />
                      <ListItemText primary={category} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Period Filter */}
              <FormControl 
                variant="outlined" 
                sx={{ 
                  minWidth: 200,
                  height: '36.5px',
                  '& .MuiOutlinedInput-root': {
                    height: '36.5px'
                  },
                  '& .MuiInputLabel-root': {
                    transform: dateRange && dateRange !== 'all' 
                      ? 'translate(14px, -9px) scale(0.75)' 
                      : 'translate(14px, 9px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)'
                    }
                  }
                }}
              >
                <InputLabel id="period-filter-label">Period</InputLabel>
                <Select
                  value={dateRange}
                  labelId="period-filter-label"
                  label="Period"
                  onChange={(e) => setDateRange(e.target.value)}
                  variant="outlined"
                  sx={{
                    height: '36.5px',
                    '& .MuiSelect-select': {
                      padding: '8px 14px',
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="7d">Last 7 days</MenuItem>
                  <MenuItem value="30d">Last 30 days</MenuItem>
                  <MenuItem value="90d">Last 90 days</MenuItem>
                </Select>
              </FormControl>

              {/* Clear Filters Button */}
              {(productSearchInput || (productCategoryFilter.length > 0) || (dateRange !== 'all')) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setProductSearchInput('');
                    setProductSearchFilter('');
                    setProductCategoryFilter([]);
                    setDateRange('all');
                  }}
                  startIcon={<FilterList />}
                  sx={{
                    height: '36.5px',
                    textTransform: 'none'
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
            <Typography variant="body2" sx={{ color: "#64748b", whiteSpace: 'nowrap' }}>
              {currentProducts.length} products found
            </Typography>
          </Box>
        </Box>

        {currentLoading && currentProducts.length === 0 && !productTabConfig.hasFetched ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={48} sx={{ color: "#3b82f6" }} />
          </Box>
        ) : (!currentLoading || productTabConfig.hasFetched) && currentProducts.length === 0 ? (
          renderEmptyState()
        ) : isRejectedTab ? (
          renderRejectedTable()
        ) : (
          <Grid container spacing={3}>
          {currentProducts.map((product, index) => {
            // Use cached image URL to prevent recalculation and flickering
            const imageUrl = productImageUrlCache.get(product.id) || null;
            // Create stable key that includes imageUrl to prevent unnecessary re-renders
            const stableKey = `product-card-${product.id}-${imageUrl || 'no-image'}`;
            // Check if this card has already been seen/rendered
            const hasBeenSeen = animatedCardsRef.current.has(product.id);
            
            // Mark card as seen immediately when first rendered to prevent re-animation on re-renders
            if (!hasBeenSeen) {
              animatedCardsRef.current.add(product.id);
            }
            
            // Extract Card content to avoid duplication
            const cardContent = (
                <Card
                  sx={{
                    width: '100%', // Fixed width to fill grid item
                    maxWidth: 320, // Maximum width for larger screens
                    height: 400, // Reduced height for more square appearance
                    minHeight: 410, // Ensure minimum height consistency
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    border: "2px solid #e2e8f0", // Consistent border width
                    borderRadius: 0, // Rectangular shape instead of rounded
                    overflow: "hidden",
                    position: "relative",
                    margin: '0 auto', // Center cards in grid items
                    padding: 0, // Remove default padding
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)", // Consistent shadow
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
                      borderColor: "#2C3E50",
                    },
                  }}
                >
                  <Box sx={{ position: "relative", width: "100%" }}>
                    <CardMedia
                      component="div"
                      sx={{
                        width: "100%", // Full width of card
                        height: 150, // Reduced height for better proportions
                        minHeight: 150,
                        maxHeight: 150,
                        aspectRatio: "16/9", // Fixed aspect ratio
                        bgcolor: "#f8fafc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        position: "relative",
                        border: "1px solid #e2e8f0", // Subtle border for definition
                      }}
                    >
                      {imageUrl ? (
                        <img
                          key={`product-image-${product.id}-${imageUrl}`}
                          src={imageUrl}
                          alt={product.name}
                          loading="lazy"
                          decoding="async"
                          crossOrigin="anonymous"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover", // Maintain proportions without distortion
                            objectPosition: "center", // Center the image
                            transition: "transform 0.3s ease", // Smooth hover effect
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = "scale(1)";
                          }}
                        />
                      ) : null}
                      <Box
                        sx={{
                          display: imageUrl ? "none" : "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          width: "100%",
                          flexDirection: "column",
                          color: "#94a3b8",
                          bgcolor: "#f1f5f9",
                        }}
                      >
                        <PhotoCamera sx={{ fontSize: "2.5rem", mb: 1, opacity: 0.4 }} />
                        <Typography variant="body2" sx={{ opacity: 0.6, fontSize: "0.75rem" }}>
                          No Image Available
                        </Typography>
                      </Box>
                    </CardMedia>

                    {/* Status Badge */}
                    <Chip
                      label={(product.approvalStatus || 'PENDING').replace("_", " ").toUpperCase()}
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        bgcolor: product.approvalStatus === 'APPROVED'
                          ? "#10b981"
                          : product.approvalStatus === 'REJECTED'
                            ? "#ef4444"
                            : "#f59e0b",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.75rem"
                      }}
                      size="small"
                    />

                    {/* Three Dots Menu */}
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        bgcolor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(4px)",
                        "&:hover": { 
                          bgcolor: "rgba(255,255,255,1)",
                          transform: "scale(1.1)"
                        },
                        transition: "all 0.2s ease",
                        width: 36,
                        height: 36
                      }}
                      onClick={(e) => handleProductMenuClick(e, product)}
                      title="More Actions"
                    >
                      <MoreVert sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>

                  <CardContent sx={{ 
                    flexGrow: 1, 
                    p: 2, // Consistent padding for all cards
                    px: 2.5, // Slightly more horizontal padding
                    display: 'flex', 
                    flexDirection: 'column', 
                    height: 'calc(100% - 150px)', // Adjusted for new image height
                    justifyContent: 'space-between', // Ensure consistent spacing
                    gap: 1.5, // Consistent spacing between elements
                    '&:last-child': {
                      paddingBottom: 2, // Override MUI default last-child padding
                    }
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 1.5, // Increased margin for better spacing
                        color: "#1e293b",
                        fontSize: "1.1rem", // Slightly larger for better readability
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        minHeight: '3rem', // Increased for consistent height
                        maxHeight: '3rem'
                      }}
                    >
                      {product.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ 
                        mb: 2,
                        color: "#64748b",
                        fontSize: "0.875rem",
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minHeight: '1.25rem', // Consistent height
                        fontWeight: 500
                      }}
                    >
                      {product.category}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: "#059669",
                          fontWeight: 700,
                          fontSize: "1.25rem",
                          minHeight: '1.75rem' // Consistent height
                        }}
                      >
                        ₹{product.price}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: "#64748b",
                          fontSize: "0.875rem"
                        }}
                      >
                        Stock: {product.stockQuantity || product.stock}
                      </Typography>
                    </Box>

                    {product.approvalStatus === 'REJECTED' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Alert
                          severity="error"
                          sx={{
                            borderRadius: 2,
                            bgcolor: '#fef2f2',
                            color: '#b91c1c',
                            alignItems: 'flex-start'
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#b91c1c', mb: 0.5 }}>
                            Rejection Reason
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                            {product.approvalComments || 'No reason provided.'}
                          </Typography>
                        </Alert>
                        <Button
                          variant="contained"
                          onClick={() => handleResubmitProductClick(product)}
                          startIcon={<Refresh />}
                          sx={{
                            bgcolor: '#f97316',
                            color: 'white',
                            '&:hover': {
                              bgcolor: '#ea580c'
                            }
                          }}
                        >
                          Edit & Resubmit
                        </Button>
                      </Box>
                    )}

                    {/* Statistics Row */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1.5,
                        bgcolor: "#f8fafc",
                        borderRadius: 8, // Consistent with card border radius
                        border: "1px solid #e2e8f0",
                        minHeight: '56px', // Fixed height for consistency
                        maxHeight: '56px',
                        alignItems: 'center',
                        marginTop: 'auto', // Push to bottom
                        gap: 1, // Consistent spacing between stat items
                      }}
                    >
                      <Box sx={{ textAlign: "center", flex: 1 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: "#64748b",
                            fontSize: "0.75rem",
                            fontWeight: 500
                          }}
                        >
                          Views
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 700,
                            color: "#1e293b",
                            fontSize: "0.875rem"
                          }}
                        >
                          {getProductStats(product.id).totalViews || product.analytics?.totalViews || product.views || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "center", flex: 1 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: "#64748b",
                            fontSize: "0.75rem",
                            fontWeight: 500
                          }}
                        >
                          Orders
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 700,
                            color: "#1e293b",
                            fontSize: "0.875rem"
                          }}
                        >
                          {getProductOrderCount(product.id)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "center", flex: 1 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: "#64748b",
                            fontSize: "0.75rem",
                            fontWeight: 500
                          }}
                        >
                          Rating
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Star
                            sx={{ fontSize: 14, color: "#f59e0b", mr: 0.5 }}
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 700,
                              color: "#1e293b",
                              fontSize: "0.875rem"
                            }}
                          >
                            {getProductStats(product.id).averageRating?.toFixed(1) || productAnalytics?.[product.id]?.averageRating?.toFixed(1) || "0.0"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
            );
            
            return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={stableKey}>
              {hasBeenSeen ? (
                // Use regular div for already-seen cards to prevent re-animation
                <div>
                  {cardContent}
                </div>
              ) : (
                // Use motion.div only for cards that haven't been seen yet (first render)
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  layout={false}
                >
                  {cardContent}
              </motion.div>
              )}
            </Grid>
            );
          })}
          </Grid>
        )}

        {/* Product Actions Menu */}
        <Menu
          anchorEl={productMenuAnchorEl}
          open={Boolean(productMenuAnchorEl)}
          onClose={handleProductMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorReference={menuAnchorPosition ? "anchorPosition" : "anchorEl"}
          anchorPosition={menuAnchorPosition ? menuAnchorPosition : undefined}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 160,
              maxWidth: 200,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 2,
              border: "1px solid #e2e8f0",
              zIndex: 1400, // Higher than Drawer (1200) and other components
              overflow: 'visible'
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          disablePortal={false}
          disableScrollLock={true}
          MenuListProps={{
            'aria-labelledby': 'product-actions-menu',
            sx: {
              padding: '8px 0'
            }
          }}
        >
          <MenuItem 
            onClick={() => handleProductMenuAction("view")}
            sx={{ 
              py: 1.5, 
              px: 2,
              "&:hover": { bgcolor: "#f8fafc" }
            }}
          >
            <Visibility sx={{ mr: 2, fontSize: 20, color: "#64748b" }} />
            <Typography variant="body2">View Product</Typography>
          </MenuItem>
          <MenuItem 
            onClick={() => handleProductMenuAction("edit")}
            sx={{ 
              py: 1.5, 
              px: 2,
              "&:hover": { bgcolor: "#f8fafc" }
            }}
          >
            <Edit sx={{ mr: 2, fontSize: 20, color: "#3b82f6" }} />
            <Typography variant="body2">Edit Product</Typography>
          </MenuItem>
          {selectedProductForMenu?.approvalStatus === 'REJECTED' && (
            <MenuItem
              onClick={() => handleProductMenuAction("resubmit")}
              sx={{
                py: 1.5,
                px: 2,
                "&:hover": { bgcolor: "#fdf3e7" }
              }}
            >
              <Refresh sx={{ mr: 2, fontSize: 20, color: "#f97316" }} />
              <Typography variant="body2" sx={{ color: "#f97316" }}>Resubmit for Approval</Typography>
            </MenuItem>
          )}
          <MenuItem 
            onClick={() => handleProductMenuAction("duplicate")}
            sx={{ 
              py: 1.5, 
              px: 2,
              "&:hover": { bgcolor: "#f8fafc" }
            }}
          >
            <ContentCopy sx={{ mr: 2, fontSize: 20, color: "#10b981" }} />
            <Typography variant="body2">Duplicate Product</Typography>
          </MenuItem>
          <MenuItem 
            onClick={() => handleProductMenuAction("delete")}
            sx={{ 
              py: 1.5, 
              px: 2,
              "&:hover": { bgcolor: "#fef2f2" },
              color: "#ef4444"
            }}
          >
            <Delete sx={{ mr: 2, fontSize: 20, color: "#ef4444" }} />
            <Typography variant="body2" sx={{ color: "#ef4444" }}>Delete Product</Typography>
          </MenuItem>
        </Menu>

      </Box>
    );
  };

  const OrdersTab = () => (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
      overflowX: 'hidden',
      overflowY: 'visible',
      boxSizing: 'border-box'
    }}>
      {/* Orders Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2
        }}
      >
        <Typography variant="h6">
          Orders ({recentOrders.length})
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={orderStatusFilter}
              label="Status Filter"
              onChange={(e) => setOrderStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Orders</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="shipped">Shipped</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<Download />} variant="outlined">
            Export
          </Button>
          <Button
            startIcon={<Refresh />}
            variant="outlined"
            onClick={handleRefreshData}
            disabled={loading || statsLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Orders Table */}
      <Card sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <CardContent sx={{ p: 0, width: '100%', maxWidth: '100%' }}>
          <TableContainer sx={{ 
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            overflowY: 'visible',
            display: 'block'
          }}>
            <Table sx={{ width: '100%', tableLayout: 'auto' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 120 }}>Order ID</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Customer</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Product</TableCell>
                  <TableCell sx={{ minWidth: 80 }}>Quantity</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Amount</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Date</TableCell>
                  <TableCell sx={{ minWidth: 180, maxWidth: 200 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOrders.length > 0 ? (
                  recentOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{order.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{order.customer}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.shippingAddress}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.product}</TableCell>
                      <TableCell>{order.quantity}</TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" color="primary">
                          ₹{order.amount.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status.toUpperCase()}
                          color={getStatusColor(order.status)}
                          size="small"
                          icon={getStatusIcon(order.status)}
                        />
                      </TableCell>
                      <TableCell>{order.date}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => handleOrderAction("view", order)}
                          >
                            View
                          </Button>
                          {order.status === "pending" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<LocalShipping />}
                              onClick={() => handleOrderAction("ship", order)}
                            >
                              Ship
                            </Button>
                          )}
                          {order.status === "shipped" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() => handleDeliverOrder(order)}
                            >
                              Deliver
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ py: 4 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No Active Orders
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          You currently have no active orders to process.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  // Calculate real stats from database data
  // Use useMemo with deferred dependencies to prevent blocking chart rendering
  // Stats are shown in AnalyticsTab, not OverviewTab, so they can load separately
  const calculatedStats = useMemo(() => {
    // Total Products: Count all products (approved + pending + rejected)
    // Don't block if products are still loading - use 0 as fallback
    const totalProducts = (approvedProducts?.length || 0) + 
                         (pendingProducts?.length || 0) + 
                         (rejectedProducts?.length || 0);
    
    // Total Orders: Count all orders (fast - just array length)
    const totalOrders = orders?.length || 0;
    
    // Monthly Revenue: Calculate from orders in current month
    // Use cached orders if available for instant calculation
    if (!orders || orders.length === 0) {
      return {
        totalProducts: 0,
        totalOrders: 0,
        monthlyRevenue: 0,
        averageRating: "0.0",
        revenueTrend: 0,
        orderTrend: 0,
        ratingTrend: 0,
        productTrend: 0
      };
    }
    
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    // Optimize: use for loop instead of reduce for better performance
    let currentMonthRevenue = 0;
    let previousMonthRevenue = 0;
    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    
    // Single pass through orders for better performance
    // Calculate both revenue and order counts in one loop
    let currentMonthOrders = 0;
    let previousMonthOrders = 0;
    
    for (const order of orders) {
      if (!order.orderDate) continue;
      
      const orderDate = new Date(order.orderDate);
      const orderMonth = orderDate.getMonth();
      const orderYear = orderDate.getFullYear();
      const orderAmount = order.totalAmount || 0;
      
      if (orderMonth === currentMonth && orderYear === currentYear) {
        currentMonthRevenue += orderAmount;
        currentMonthOrders++;
      } else if (orderMonth === previousMonth && orderYear === previousYear) {
        previousMonthRevenue += orderAmount;
        previousMonthOrders++;
      }
    }
    
    // Calculate revenue trend
    const revenueTrend = previousMonthRevenue > 0 
      ? ((currentMonthRevenue - previousMonthRevenue) / previousMonthRevenue * 100).toFixed(1)
      : currentMonthRevenue > 0 ? 100 : 0;
    
    // Average Rating: Calculate from product statistics (stored in product_statistics collection)
    // This is more efficient than calculating from reviews on the fly
    let averageRating = 0;
    let totalRatings = 0;
    
    // Get average rating from product statistics (preferred method)
    const allProductIds = [
      ...(approvedProducts || []),
      ...(pendingProducts || []),
      ...(rejectedProducts || [])
    ].map(p => p?.id).filter(Boolean);
    
    allProductIds.forEach(productId => {
      const stats = getProductStats(productId);
      if (stats.averageRating && stats.averageRating > 0) {
        averageRating += stats.averageRating;
        totalRatings += 1;
      }
    });
    
    averageRating = totalRatings > 0 ? (averageRating / totalRatings) : 0;
    
    // Fallback to reviews if product statistics are not available
    if (averageRating === 0 && reviews && reviews.length > 0) {
      const totalRating = reviews.reduce((sum, review) => sum + (review.rating || 0), 0);
      averageRating = totalRating / reviews.length;
    }
    
    // Calculate rating trend (compare with previous period - simplified)
    // For now, we'll show a neutral trend if rating exists, or 0 if not
    const ratingTrend = averageRating > 0 ? 0 : 0; // Can be enhanced with historical data
    
    // Calculate product trend (compare current vs previous period)
    // For now, we'll show a neutral trend - can be enhanced with historical data
    const productTrend = 0; // Can be enhanced with historical data
    
    const orderTrend = previousMonthOrders > 0
      ? ((currentMonthOrders - previousMonthOrders) / previousMonthOrders * 100).toFixed(1)
      : currentMonthOrders > 0 ? 100 : 0;
    
    return {
      totalProducts,
      totalOrders,
      monthlyRevenue: currentMonthRevenue,
      averageRating: averageRating.toFixed(1),
      revenueTrend: parseFloat(revenueTrend),
      orderTrend: parseFloat(orderTrend),
      ratingTrend: parseFloat(ratingTrend),
      productTrend: parseFloat(productTrend)
    };
  }, [approvedProducts, pendingProducts, rejectedProducts, orders, productStatistics, reviews]);

  const AnalyticsTab = () => (
    <Box>
      {/* Analytics Header */}
      <Typography variant="h6" sx={{ mb: 3 }}>
        Sales Analytics & Performance
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Products"
            value={calculatedStats.totalProducts}
            icon={<Inventory />}
            trend={calculatedStats.productTrend >= 0 ? "up" : "down"}
            trendValue={Math.abs(calculatedStats.productTrend)}
            color="primary"
            loading={!hasInitialOrdersRef.current && (statsLoading || productsLoading || approvedProductsLoading || pendingProductsLoading || rejectedProductsLoading)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Orders"
            value={calculatedStats.totalOrders}
            icon={<ShoppingCart />}
            trend={calculatedStats.orderTrend >= 0 ? "up" : "down"}
            trendValue={Math.abs(calculatedStats.orderTrend)}
            color="secondary"
            loading={!hasInitialOrdersRef.current && (statsLoading || ordersLoading)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Revenue"
            value={`₹${(calculatedStats.monthlyRevenue / 100000).toFixed(1)}L`}
            icon={<AttachMoney />}
            trend={calculatedStats.revenueTrend >= 0 ? "up" : "down"}
            trendValue={Math.abs(calculatedStats.revenueTrend)}
            color="success"
            loading={!hasInitialOrdersRef.current && (statsLoading || ordersLoading)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Average Rating"
            value={calculatedStats.averageRating}
            icon={<Star />}
            trend={calculatedStats.ratingTrend >= 0 ? "up" : "down"}
            trendValue={Math.abs(calculatedStats.ratingTrend)}
            color="text.secondary"

            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Performance Metrics */}
      <Box 
        sx={{ 
          width: { xs: '100%', sm: '100%' },
          maxWidth: '100%',
          mb: 4,
          px: { xs: 1, sm: 2 },
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 1 },
            height: { xs: 'auto', md: 450 },
            width: '100%'
          }}
        >
          <Card
            sx={{ 
              flex: 1,
              display: "flex", 
              flexDirection: "column",
              height: { xs: 400, md: "100%" },
              minWidth: 0
            }}
          >
            <CardContent
              sx={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  textAlign: 'center',
                  fontWeight: 600
                }}
              >
                Monthly Sales Trend (Last 6 Months)
              </Typography>
              <Box sx={{ flex: 1, minHeight: 300 }}>
                {sellerAnalytics.monthlySalesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sellerAnalytics.monthlySalesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="sales"
                        stroke="#8884d8"
                        name="Sales (₹)"
                      />
                      <Line
                        type="monotone"
                        dataKey="orders"
                        stroke="#82ca9d"
                        name="Orders"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">
                      No sales data available for the last 6 months
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card
            sx={{ 
              flex: 1,
              display: "flex", 
              flexDirection: "column",
              height: { xs: 400, md: "100%" },
              minWidth: 0
            }}
          >
            <CardContent
              sx={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  textAlign: 'center',
                  fontWeight: 600
                }}
              >
                Top Performing Products
              </Typography>
              <Box sx={{ flex: 1, minHeight: 300, overflow: 'auto' }}>
                {productsWithOrderCounts && productsWithOrderCounts.length > 0 ? (
                  <List>
                    {[...productsWithOrderCounts]
                      .sort((a, b) => (b.orderCount || 0) - (a.orderCount || 0))
                      .slice(0, 5)
                      .map((product, index) => (
                        <ListItem key={product.id}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: "primary.main" }}>
                              {index + 1}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={product.name}
                            secondary={`${product.orderCount || 0} order${(product.orderCount || 0) !== 1 ? 's' : ''} • ₹${
                              product.price
                            }`}
                          />
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Star
                              sx={{ fontSize: 16, color: "warning.main", mr: 0.5 }}
                            />
                            <Typography variant="body2">
                              {getProductStats(product.id).averageRating?.toFixed(1) || "0.0"}
                            </Typography>
                          </Box>
                        </ListItem>
                      ))}
                  </List>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No product data available
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Additional Analytics */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Conversion Rate
              </Typography>
              <Typography variant="h4" color="primary">
                {sellerAnalytics.conversionRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {sellerAnalytics.totalViews} views to {orders?.length || 0} orders
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={Math.min(sellerAnalytics.conversionRate * 10, 100)} 
                sx={{ mt: 2 }} 
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Customer Satisfaction
              </Typography>
              <Typography variant="h4" color="success.main">
                {sellerStats?.averageRating?.value ?? 0}/5
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {sellerStats?.totalReviews?.value ?? 0} reviews
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(sellerStats?.averageRating?.value ?? 0) * 20}
                sx={{ mt: 2 }}
                color="success"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Cancellation Rate
              </Typography>
              <Typography variant="h4" color={sellerAnalytics.returnRate > 5 ? "error.main" : "success.main"}>
                {sellerAnalytics.returnRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {orders?.filter(o => o.status?.toLowerCase() === 'cancelled').length || 0} of {orders?.length || 0} orders cancelled
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(sellerAnalytics.returnRate * 10, 100)}
                sx={{ mt: 2 }}
                color={sellerAnalytics.returnRate > 5 ? "error" : "success"}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // Reviews Management Tab
  const ReviewsTab = () => {
    const [minReviewRating, setMinReviewRating] = useState(0);
    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [reportReason, setReportReason] = useState('');
    const [reviewActionSubmitting, setReviewActionSubmitting] = useState(false);

    // Older reviews may only carry sellerResponse (no replied/replyContent yet)
    const getReplyContent = (review) => review?.replyContent || review?.sellerResponse || '';
    const getApiErrorMessage = (error) =>
      (typeof error === 'string' ? error : error?.message) || 'Unknown error';

    // Memoize filtered reviews to prevent unnecessary recalculations
    const filteredReviews = useMemo(() => {
      return (reviews || []).filter(
        (r) => (r?.rating ?? 0) >= (minReviewRating || 0)
      );
    }, [reviews, minReviewRating]);

    // Lazy load reviews when Reviews tab is accessed - fetch all reviews only once
    useEffect(() => {
      // Only fetch if:
      // 1. Reviews tab is active (activeTab === 4)
      // 2. User ID exists
      // 3. Reviews haven't been fetched yet for this session
      // 4. Not currently fetching
      if (activeTab === 4 && user?.id && !reviewsFetchedRef.current && !isFetchingReviewsRef.current) {
        isFetchingReviewsRef.current = true;
        dispatch(fetchSellerReviews({ sellerId: user.id, size: 1000, approvedOnly: false }))
          .finally(() => {
            isFetchingReviewsRef.current = false;
            reviewsFetchedRef.current = true;
          });
      }
    }, [dispatch, user?.id, activeTab]); // Only re-run when these dependencies change

    // Handler functions
    const handleReplyClick = (review) => {
      setSelectedReview(review);
      setReplyText(getReplyContent(review));
      setReplyDialogOpen(true);
    };

    const handleReportClick = (review) => {
      setSelectedReview(review);
      setReportReason('');
      setReportDialogOpen(true);
    };

    const handleSubmitReply = async () => {
      if (!selectedReview || !replyText.trim()) {
        toast.error('Please enter a reply message');
        return;
      }

      setReviewActionSubmitting(true);
      try {
        // The seller slice merges the updated review from the response into state
        await dispatch(addSellerResponse({
          reviewId: selectedReview.id,
          response: replyText.trim()
        })).unwrap();

        toast.success('Reply added successfully');
        setReplyDialogOpen(false);
        setReplyText('');
        setSelectedReview(null);
      } catch (error) {
        toast.error('Failed to add reply: ' + getApiErrorMessage(error));
      } finally {
        setReviewActionSubmitting(false);
      }
    };

    const handleSubmitReport = async () => {
      if (!selectedReview || !reportReason.trim()) {
        toast.error('Please select a reason for reporting');
        return;
      }

      setReviewActionSubmitting(true);
      try {
        // The seller slice merges the updated review from the response into state
        await dispatch(flagReview({
          reviewId: selectedReview.id,
          reason: reportReason
        })).unwrap();

        toast.success('Review reported successfully');
        setReportDialogOpen(false);
        setReportReason('');
        setSelectedReview(null);
      } catch (error) {
        toast.error('Failed to report review: ' + getApiErrorMessage(error));
      } finally {
        setReviewActionSubmitting(false);
      }
    };

    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Customer Reviews</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2">Min rating:</Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          sx={{
                            fontSize: 20,
                            color:
                              star <= minReviewRating
                                ? "warning.main"
                                : "grey.300",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setMinReviewRating(
                              star === minReviewRating ? 0 : star
                            )
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
                {filteredReviews.length === 0 ? (
                  <Alert severity="info">No reviews to display.</Alert>
                ) : (
                  <List>
                    {filteredReviews.map((rev, idx) => (
                      <ListItem
                        key={rev.id || idx}
                        alignItems="flex-start"
                        divider
                      >
                        <ListItemAvatar>
                          <Avatar>
                            {(rev?.customerName || rev?.userName || "U")[0]}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="subtitle1">
                                {rev?.customerName ||
                                  rev?.userName ||
                                  "Customer"}
                              </Typography>
                              <Box sx={{ display: "flex" }}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    sx={{
                                      fontSize: 16,
                                      color:
                                        star <= (rev?.rating ?? 0)
                                          ? "warning.main"
                                          : "grey.300",
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          }
                          secondary={
                            <>
                              {/* Product Name */}
                              {(rev?.attributes?.productName || rev?.productName || rev?.product?.name) && (
                                <Typography
                                  variant="body2"
                                  color="primary"
                                  sx={{ 
                                    fontWeight: 600, 
                                    mb: 0.5,
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  Product: {rev?.attributes?.productName || rev?.productName || rev?.product?.name}
                                </Typography>
                              )}
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {rev?.comment ||
                                  rev?.review ||
                                  "No comment provided."}
                              </Typography>
                              {getReplyContent(rev) && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                                    Your Reply:
                                  </Typography>
                                  <Typography variant="body2" color="text.primary">
                                    {getReplyContent(rev)}
                                  </Typography>
                                </Box>
                              )}
                              {rev?.reported && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                  Reported{rev.reportReason ? `: ${rev.reportReason}` : ''}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {rev?.date || rev?.createdAt || ""}
                              </Typography>
                            </>
                          }
                        />
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            onClick={() => handleReplyClick(rev)}
                            variant={getReplyContent(rev) ? "outlined" : "contained"}
                          >
                            {getReplyContent(rev) ? "Edit Reply" : "Reply"}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleReportClick(rev)}
                            variant="outlined"
                            disabled={!!rev.reported}
                          >
                            {rev.reported ? "Reported" : "Report"}
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Review Summary
                </Typography>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <Box sx={{ display: "flex" }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        sx={{
                          fontSize: 20,
                          color:
                            star <= (sellerStats?.averageRating?.value ?? 0)
                              ? "warning.main"
                              : "grey.300",
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="h5">
                    {(sellerStats?.averageRating?.value ?? 0).toFixed(1)}/5
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {sellerStats?.totalReviews?.value ?? 0} total reviews
                </Typography>
                {[5, 4, 3, 2, 1].map((star) => (
                  <Box key={star} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2">{star} star</Typography>
                      <Typography variant="body2">
                        {Math.round(
                          (reviews?.filter(
                            (r) => Math.floor(r?.rating ?? 0) === star
                          ).length /
                            Math.max(1, reviews?.length || 1)) *
                            100
                        )}
                        %
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(
                        0,
                        Math.min(
                          100,
                          (reviews?.filter(
                            (r) => Math.floor(r?.rating ?? 0) === star
                          ).length /
                            Math.max(1, reviews?.length || 1)) *
                            100
                        )
                      )}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Reply Dialog */}
        <Dialog 
          open={replyDialogOpen} 
          onClose={() => setReplyDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {getReplyContent(selectedReview) ? 'Edit Reply' : 'Reply to Review'}
          </DialogTitle>
          <DialogContent>
            {selectedReview && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Review by {selectedReview.customerName || selectedReview.userName || 'Customer'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      sx={{
                        fontSize: 16,
                        color: star <= (selectedReview.rating || 0) ? "warning.main" : "grey.300"
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  "{selectedReview.comment}"
                </Typography>
              </Box>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Your Reply"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write your response to this review..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReplyDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmitReply}
              variant="contained"
              disabled={!replyText.trim() || reviewActionSubmitting}
            >
              {getReplyContent(selectedReview) ? 'Update Reply' : 'Submit Reply'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Report Dialog */}
        <Dialog 
          open={reportDialogOpen} 
          onClose={() => setReportDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Report Review</DialogTitle>
          <DialogContent>
            {selectedReview && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Review by {selectedReview.customerName || selectedReview.userName || 'Customer'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  "{selectedReview.comment}"
                </Typography>
              </Box>
            )}
            <FormControl fullWidth margin="dense">
              <InputLabel>Reason for reporting</InputLabel>
              <Select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                label="Reason for reporting"
              >
                <MenuItem value="Inappropriate content">Inappropriate content</MenuItem>
                <MenuItem value="Spam">Spam</MenuItem>
                <MenuItem value="Fake review">Fake review</MenuItem>
                <MenuItem value="Offensive language">Offensive language</MenuItem>
                <MenuItem value="Harassment">Harassment</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReportDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmitReport}
              variant="contained"
              color="error"
              disabled={!reportReason || reviewActionSubmitting}
            >
              Report Review
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Promotions & Marketing Tab
  const PromotionsTab = () => {
    const sellerPromotions = useSelector((state) => state.seller.promotions);
    const promotionsLoading = useSelector((state) => state.seller.promotionsLoading);
    const promotionsError = useSelector((state) => state.seller.promotionsError);
    
    // Helper function to combine date and time into datetime-local format
    const combineDateTime = (date, time) => {
      if (!date) return '';
      if (!time) return date + 'T00:00';
      return date + 'T' + time;
    };

    // Helper function to split datetime-local into date and time
    const splitDateTime = (dateTime) => {
      if (!dateTime) return { date: '', time: '' };
      const [date, time] = dateTime.split('T');
      return { date: date || '', time: time || '' };
    };

    const initialFormState = {
      title: '',
      discountType: 'percentage',
      discount: '',
      discountAmount: '',
      startDateTime: '',
      endDateTime: '',
      productIds: [],
    };
    const [form, setForm] = useState(initialFormState);
    const [submitting, setSubmitting] = useState(false);
    const [deletingPromotionId, setDeletingPromotionId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedPromotionForMenu, setSelectedPromotionForMenu] = useState(null);
    
    // Product selector state
    const [productSearchQuery, setProductSearchQuery] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]); // For filtering products in dropdown
    const [selectedPromotionCategories, setSelectedPromotionCategories] = useState([]); // For category-based promotion
    const [productSelectorOpen, setProductSelectorOpen] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingPromotion, setEditingPromotion] = useState(null);
    const [editForm, setEditForm] = useState(initialFormState);
    const [editProductSearchQuery, setEditProductSearchQuery] = useState('');
    const [editSelectedCategories, setEditSelectedCategories] = useState([]); // For filtering products in dropdown
    const [editSelectedPromotionCategories, setEditSelectedPromotionCategories] = useState([]); // For category-based promotion

    const handleChange = (event) => {
      const { name, value } = event.target;
      setForm((prev) => ({ ...prev, [name]: value }));
    };

    const resetForm = useCallback(() => {
      setForm(initialFormState);
      setProductSearchQuery('');
      setSelectedCategories([]);
      setSelectedPromotionCategories([]);
      setProductSelectorOpen(false);
    }, []);

    const handlePromotionSubmit = async () => {
      if (!form.title.trim()) {
        toast.error('Please fill in title');
        return;
      }
      if (form.discountType === 'percentage' && (!form.discount || Number(form.discount) <= 0 || Number(form.discount) > 100)) {
        toast.error('Discount percentage must be between 1 and 100');
        return;
      }
      if (form.discountType === 'amount' && (!form.discountAmount || Number(form.discountAmount) <= 0)) {
        toast.error('Discount amount must be greater than 0');
        return;
      }
      // Check if either products or categories are selected
      const hasProducts = form.productIds && form.productIds.length > 0;
      const hasCategories = selectedPromotionCategories && selectedPromotionCategories.length > 0;
      
      if (!hasProducts && !hasCategories) {
        toast.error('Please select at least one product or category');
        return;
      }
      if (!user?.id) {
        toast.error('Unable to determine seller information');
        return;
      }

      // Split datetime values for backend
      const startSplit = splitDateTime(form.startDateTime);
      const endSplit = splitDateTime(form.endDateTime);

      // Expand selected categories to productIds
      let finalProductIds = [...(form.productIds || [])];
      if (selectedPromotionCategories.length > 0) {
        const categoryProductIds = availableProducts
          .filter((product) => {
            if (!product.category) return false;
            const productCategory = product.category.toLowerCase();
            return selectedPromotionCategories.some((selectedCat) => 
              productCategory === selectedCat.toLowerCase()
            );
          })
          .map((product) => String(product.id ?? product._id));
        
        // Combine with existing productIds and remove duplicates
        finalProductIds = [...new Set([...finalProductIds, ...categoryProductIds])];
      }

      try {
        setSubmitting(true);
        await dispatch(
          applyPromotion({
            sellerId: user.id,
            promotionId: undefined,
            title: form.title.trim(),
            discountType: form.discountType,
            discount: form.discountType === 'percentage' ? Number(form.discount) : null,
            discountAmount: form.discountType === 'amount' ? Number(form.discountAmount) : null,
            startDate: startSplit.date || null,
            startTime: startSplit.time || null,
            endDate: endSplit.date || null,
            endTime: endSplit.time || null,
            productIds: finalProductIds,
          })
        ).unwrap();

        toast.success('Promotion created successfully');
        resetForm();
        setCreateDialogOpen(false);
        dispatch(fetchSellerProducts({ sellerId: user.id, status: 'ACTIVE', approvalStatus: 'APPROVED' }));
        dispatch(loadSellerPromotions(user.id));
      } catch (error) {
        console.error('Promotion error:', error);
        toast.error(error?.message || 'Failed to save promotion');
      } finally {
        setSubmitting(false);
      }
    };

    const handleEditPromotion = (promotion) => {
      setEditingPromotion(promotion);
      // Combine date and time for datetime-local input
      const startDateTime = combineDateTime(promotion.startDate || '', promotion.startTime || '');
      const endDateTime = combineDateTime(promotion.endDate || '', promotion.endTime || '');
      const productIds = (promotion.productIds || []).map((id) => String(id));
      
      setEditForm({
        title: promotion.title || '',
        discountType: promotion.discountType || 'percentage',
        discount: promotion.discount != null ? String(promotion.discount) : '',
        discountAmount: promotion.discountAmount != null ? String(promotion.discountAmount) : '',
        startDateTime: startDateTime,
        endDateTime: endDateTime,
        productIds: productIds,
      });
      
      // Try to detect if all products belong to specific categories
      // This helps pre-select categories when editing
      if (productIds.length > 0 && availableProducts.length > 0) {
        const selectedProducts = availableProducts.filter((product) =>
          productIds.includes(String(product.id ?? product._id))
        );
        
        // Group products by category
        const categoryCounts = new Map();
        selectedProducts.forEach((product) => {
          if (product.category) {
            const categoryLower = product.category.toLowerCase();
            const formattedCategory = availableCategories.find((cat) =>
              cat.toLowerCase() === categoryLower
            );
            if (formattedCategory) {
              categoryCounts.set(
                formattedCategory,
                (categoryCounts.get(formattedCategory) || 0) + 1
              );
            }
          }
        });
        
        // If all products belong to the same category(ies), pre-select those categories
        // This is a heuristic - if all products in a category are selected, likely the category was selected
        const detectedCategories = [];
        categoryCounts.forEach((count, category) => {
          const totalInCategory = availableProducts.filter(
            (p) => p.category && p.category.toLowerCase() === category.toLowerCase()
          ).length;
          // If all products in a category are selected, likely the category was selected
          if (count === totalInCategory && totalInCategory > 0) {
            detectedCategories.push(category);
          }
        });
        
        // Only pre-select if we detected categories (to avoid false positives)
        if (detectedCategories.length > 0) {
          setEditSelectedPromotionCategories(detectedCategories);
        } else {
          setEditSelectedPromotionCategories([]);
        }
      } else {
        setEditSelectedPromotionCategories([]);
      }
      
      setEditDialogOpen(true);
      handleMenuClose();
    };

    const handleEditFormChange = (event) => {
      const { name, value } = event.target;
      setEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditDialogClose = () => {
      setEditDialogOpen(false);
      setEditingPromotion(null);
      setEditForm(initialFormState);
      setEditProductSearchQuery('');
      setEditSelectedCategories([]);
      setEditSelectedPromotionCategories([]);
    };

    const handleEditPromotionSubmit = async () => {
      if (!editForm.title.trim()) {
        toast.error('Please fill in title');
        return;
      }
      if (editForm.discountType === 'percentage' && (!editForm.discount || Number(editForm.discount) <= 0 || Number(editForm.discount) > 100)) {
        toast.error('Discount percentage must be between 1 and 100');
        return;
      }
      if (editForm.discountType === 'amount' && (!editForm.discountAmount || Number(editForm.discountAmount) <= 0)) {
        toast.error('Discount amount must be greater than 0');
        return;
      }
      // Check if either products or categories are selected
      const hasProducts = editForm.productIds && editForm.productIds.length > 0;
      const hasCategories = editSelectedPromotionCategories && editSelectedPromotionCategories.length > 0;
      
      if (!hasProducts && !hasCategories) {
        toast.error('Please select at least one product or category');
        return;
      }
      if (!user?.id || !editingPromotion?.id) {
        toast.error('Unable to determine promotion information');
        return;
      }

      // Split datetime values for backend
      const startSplit = splitDateTime(editForm.startDateTime);
      const endSplit = splitDateTime(editForm.endDateTime);

      // Expand selected categories to productIds
      let finalProductIds = [...(editForm.productIds || [])];
      if (editSelectedPromotionCategories.length > 0) {
        const categoryProductIds = availableProducts
          .filter((product) => {
            if (!product.category) return false;
            const productCategory = product.category.toLowerCase();
            return editSelectedPromotionCategories.some((selectedCat) => 
              productCategory === selectedCat.toLowerCase()
            );
          })
          .map((product) => String(product.id ?? product._id));
        
        // Combine with existing productIds and remove duplicates
        finalProductIds = [...new Set([...finalProductIds, ...categoryProductIds])];
      }

      try {
        setSubmitting(true);
        await dispatch(
          applyPromotion({
            sellerId: user.id,
            promotionId: editingPromotion.id,
            title: editForm.title.trim(),
            discountType: editForm.discountType,
            discount: editForm.discountType === 'percentage' ? Number(editForm.discount) : null,
            discountAmount: editForm.discountType === 'amount' ? Number(editForm.discountAmount) : null,
            startDate: startSplit.date || null,
            startTime: startSplit.time || null,
            endDate: endSplit.date || null,
            endTime: endSplit.time || null,
            productIds: finalProductIds,
          })
        ).unwrap();

        toast.success('Promotion updated successfully');
        handleEditDialogClose();
        dispatch(fetchSellerProducts({ sellerId: user.id, status: 'ACTIVE', approvalStatus: 'APPROVED' }));
        dispatch(loadSellerPromotions(user.id));
      } catch (error) {
        console.error('Promotion error:', error);
        toast.error(error?.message || 'Failed to update promotion');
      } finally {
        setSubmitting(false);
      }
    };

    const handleMenuOpen = (event, promotion) => {
      setAnchorEl(event.currentTarget);
      setSelectedPromotionForMenu(promotion);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setSelectedPromotionForMenu(null);
    };

    const handleToggleStatus = async (promotion, newStatus) => {
      if (!user?.id) {
        toast.error('Unable to determine seller information');
        return;
      }

      try {
        await dispatch(
          updatePromotionStatus({
            sellerId: user.id,
            promotionId: promotion.id,
            status: newStatus,
          })
        ).unwrap();
        toast.success(`Promotion ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        dispatch(loadSellerPromotions(user.id));
      } catch (error) {
        console.error('Status update error:', error);
        toast.error(error?.message || 'Failed to update promotion status');
      } finally {
        handleMenuClose();
      }
    };

    const handleDeletePromotion = async (promotion) => {
      if (!promotion?.id) {
        toast.error('Invalid promotion selected');
        return;
      }
      if (!user?.id) {
        toast.error('Unable to determine seller information');
        return;
      }

      try {
        setDeletingPromotionId(promotion.id);
        await dispatch(
          deletePromotion({ sellerId: user.id, promotionId: promotion.id })
        ).unwrap();
        toast.success('Promotion deleted successfully');
        // Close edit dialog if the deleted promotion was being edited
        if (editingPromotion && editingPromotion.id === promotion.id) {
          handleEditDialogClose();
        }
        // Note: loadSellerPromotions is already called in deletePromotion.fulfilled
        // No need to call it again here to avoid race conditions
      } catch (error) {
        console.error('Delete promotion error:', error);
        toast.error(error?.message || 'Failed to delete promotion');
      } finally {
        setDeletingPromotionId(null);
      }
    };

    const productLookup = useMemo(() => {
      const map = new Map();
      const aggregate = [
        ...(products || []),
        ...(approvedProducts || []),
        ...(pendingProducts || []),
        ...(rejectedProducts || []),
      ];
      aggregate.forEach((product) => {
        const key = String(product.id ?? product._id);
        if (!map.has(key)) {
          map.set(key, product);
        }
      });
      return map;
    }, [products, approvedProducts, pendingProducts, rejectedProducts]);

    const resolveProductNames = useCallback(
      (ids = []) => {
        if (!ids.length) return '—';
        const names = ids
          .map((id) => productLookup.get(String(id))?.name)
          .filter(Boolean);
        return names.length ? names.join(', ') : '—';
      },
      [productLookup]
    );

    // Get available products for selector (only approved)
    const availableProducts = useMemo(() => {
      if (!myProducts || myProducts.length === 0) return [];
      return myProducts.filter((product) => product.approvalStatus === 'APPROVED');
    }, [myProducts]);

    // Get unique categories from products (case-insensitive deduplication)
    const availableCategories = useMemo(() => {
      const categoryMap = new Map(); // Map of lowercase -> original format
      availableProducts.forEach((product) => {
        if (product.category) {
          const lowerKey = product.category.toLowerCase();
          // Only add if we haven't seen this category (case-insensitive) before
          // Or if the current one is better formatted (has proper capitalization)
          if (!categoryMap.has(lowerKey)) {
            // Format category name nicely: capitalize first letter of each word
            const formatted = product.category
              .split(/[\s_]+/)
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
            categoryMap.set(lowerKey, formatted);
          }
        }
      });
      // Return sorted array of formatted category names
      return Array.from(categoryMap.values()).sort();
    }, [availableProducts]);

    // Filter products based on search and category (case-insensitive)
    const filteredProducts = useMemo(() => {
      let filtered = availableProducts;

      // Filter by category (case-insensitive)
      if (selectedCategories.length > 0) {
        // Convert selected formatted categories to lowercase for comparison
        const selectedCategoriesLower = selectedCategories.map(cat => cat.toLowerCase());
        
        filtered = filtered.filter((product) => {
          if (!product.category) return false;
          const productCategory = product.category.toLowerCase();
          // Check if product category matches any selected category (case-insensitive)
          return selectedCategoriesLower.some(selectedCat => 
            productCategory === selectedCat
          );
        });
      }

      // Filter by search query
      if (productSearchQuery.trim()) {
        const query = productSearchQuery.toLowerCase();
        filtered = filtered.filter(
          (product) =>
            product.name?.toLowerCase().includes(query) ||
            product.category?.toLowerCase().includes(query) ||
            String(product.price || '').includes(query)
        );
      }

      return filtered;
    }, [availableProducts, selectedCategories, productSearchQuery]);

    // Filter products for edit dialog (case-insensitive)
    const filteredEditProducts = useMemo(() => {
      let filtered = availableProducts;

      // Filter by category (case-insensitive)
      if (editSelectedCategories.length > 0) {
        const selectedCategoriesLower = editSelectedCategories.map(cat => cat.toLowerCase());
        
        filtered = filtered.filter((product) => {
          if (!product.category) return false;
          const productCategory = product.category.toLowerCase();
          return selectedCategoriesLower.some(selectedCat => 
            productCategory === selectedCat
          );
        });
      }

      // Filter by search query
      if (editProductSearchQuery.trim()) {
        const query = editProductSearchQuery.toLowerCase();
        filtered = filtered.filter(
          (product) =>
            product.name?.toLowerCase().includes(query) ||
            product.category?.toLowerCase().includes(query) ||
            String(product.price || '').includes(query)
        );
      }

      return filtered;
    }, [availableProducts, editSelectedCategories, editProductSearchQuery]);

    const isPromotionActive = (promotion) => {
      // Use database status first - if it's expired or inactive, return false
      if (promotion.status === 'inactive' || promotion.status === 'expired') {
        return false;
      }

      // If status is active, check dates to ensure it's still valid
      if (promotion.status === 'active') {
        const now = new Date();
        
        // Check start datetime
        if (promotion.startDateTime) {
          const startDate = new Date(promotion.startDateTime);
          if (now < startDate) {
            return false;
          }
        } else if (promotion.startDate) {
          const startDate = new Date(promotion.startDate + 'T00:00:00');
          if (now < startDate) {
            return false;
          }
        }

        // Check end datetime
        if (promotion.endDateTime) {
          const endDate = new Date(promotion.endDateTime);
          if (now > endDate) {
            return false;
          }
        } else if (promotion.endDate) {
          const endDate = new Date(promotion.endDate + 'T23:59:59');
          if (now > endDate) {
            return false;
          }
        }
      }

      return promotion.status === 'active';
    };

    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#2C3E50' }}>
              Promotions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Create and manage promotional discounts for your products
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              bgcolor: '#2C3E50',
              background: 'linear-gradient(135deg, #2C3E50 0%, #34495e 100%)',
              boxShadow: '0 4px 15px rgba(44, 62, 80, 0.2)',
              '&:hover': { 
                background: 'linear-gradient(135deg, #1a252f 0%, #2C3E50 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(44, 62, 80, 0.3)',
              },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
            }}
          >
            Create Promotion
          </Button>
        </Box>

        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Active Promotions
              </Typography>
              {promotionsLoading && <CircularProgress size={20} />}
            </Box>
            {promotionsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {promotionsError}
              </Alert>
            )}
            {(!sellerPromotions || sellerPromotions.length === 0) && !promotionsLoading ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  bgcolor: '#f8f9fa',
                  borderRadius: 2,
                  border: '1px dashed #dee2e6'
                }}
              >
                <LocalOffer sx={{ fontSize: 48, color: '#adb5bd', mb: 2 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No promotions yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create your first promotion to attract customers
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Discount</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Start Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>End Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Products</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }} align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sellerPromotions.map((promotion) => {
                      const active = isPromotionActive(promotion);
                      return (
                        <TableRow key={promotion.id} sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {promotion.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                promotion.discountType === 'amount'
                                  ? `₹${promotion.discountAmount || 0} OFF`
                                  : `${promotion.discount || 0}% OFF`
                              }
                              size="small"
                              color="success"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {promotion.startDate || '—'}
                              {promotion.startTime && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {promotion.startTime}
                                </Typography>
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {promotion.endDate || '—'}
                              {promotion.endTime && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {promotion.endTime}
                                </Typography>
                              )}
                            </Typography>
                          </TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography variant="body2">
                          {resolveProductNames(promotion.productIds)}
                        </Typography>
                      </TableCell>
                          <TableCell>
                            <Chip
                              label={active ? 'Active' : (promotion.status === 'inactive' ? 'Inactive' : 'Expired')}
                              size="small"
                              color={active ? 'success' : 'default'}
                              variant={active ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, promotion)}
                              sx={{ color: 'text.secondary' }}
                            >
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {selectedPromotionForMenu && (
            <>
              <MenuItem onClick={() => handleEditPromotion(selectedPromotionForMenu)}>
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => handleToggleStatus(selectedPromotionForMenu, selectedPromotionForMenu.status === 'active' ? 'inactive' : 'active')}
              >
                <ListItemIcon>
                  {selectedPromotionForMenu.status === 'active' ? (
                    <Cancel fontSize="small" />
                  ) : (
                    <CheckCircle fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText>
                  {selectedPromotionForMenu.status === 'active' ? 'Set Inactive' : 'Set Active'}
                </ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleDeletePromotion(selectedPromotionForMenu);
                }}
                disabled={deletingPromotionId === selectedPromotionForMenu.id}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <Delete fontSize="small" sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText>
                  {deletingPromotionId === selectedPromotionForMenu.id ? 'Deleting...' : 'Delete'}
                </ListItemText>
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Create Promotion Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => {
            setCreateDialogOpen(false);
            resetForm();
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            },
          }}
        >
          <DialogTitle sx={{ 
            pb: 2,
            borderBottom: '2px solid rgba(44, 62, 80, 0.1)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: '#2C3E50',
                background: 'linear-gradient(135deg, #2C3E50 0%, #34495e 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Create Promotion
              </Typography>
              <IconButton
                onClick={() => {
                  setCreateDialogOpen(false);
                  resetForm();
                }}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3}>
              {/* Title Field */}
              <TextField
                fullWidth
                label="Promotion Title"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g., Summer Sale, Black Friday Deal"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2C3E50',
                    fontWeight: 600,
                  },
                }}
              />

              {/* Discount Type and Value */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'white',
                      '&:hover fieldset': {
                        borderColor: '#2C3E50',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2C3E50',
                        borderWidth: 2,
                      },
                    },
                  }}
                >
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    name="discountType"
                    value={form.discountType}
                    onChange={handleChange}
                    label="Discount Type"
                  >
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="amount">Fixed Amount</MenuItem>
                  </Select>
                </FormControl>
                
                {form.discountType === 'percentage' ? (
                  <TextField
                    fullWidth
                    label="Discount Percentage"
                    name="discount"
                    value={form.discount}
                    onChange={handleChange}
                    type="number"
                    placeholder="0-100"
                    inputProps={{ min: 1, max: 100 }}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'white',
                        '&:hover fieldset': {
                          borderColor: '#2C3E50',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2C3E50',
                          borderWidth: 2,
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Percent sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Discount Amount"
                    name="discountAmount"
                    value={form.discountAmount}
                    onChange={handleChange}
                    type="number"
                    placeholder="0.00"
                    inputProps={{ min: 0.01, step: 0.01 }}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'white',
                        '&:hover fieldset': {
                          borderColor: '#2C3E50',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2C3E50',
                          borderWidth: 2,
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            ₹
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Box>

              {/* Start DateTime */}
              <TextField
                fullWidth
                label="Start Date & Time"
                name="startDateTime"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.startDateTime}
                onChange={handleChange}
                variant="outlined"
                inputProps={{ step: 60 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2C3E50',
                    fontWeight: 600,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* End DateTime */}
              <TextField
                fullWidth
                label="End Date & Time"
                name="endDateTime"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.endDateTime}
                onChange={handleChange}
                variant="outlined"
                inputProps={{ step: 60 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2C3E50',
                    fontWeight: 600,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Category Selector (Optional - for whole category promotion) */}
              {availableCategories.length > 0 && (
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover fieldset': {
                        borderColor: '#2C3E50',
                        borderWidth: 2,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2C3E50',
                        borderWidth: 2,
                        boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2C3E50',
                      fontWeight: 600,
                    },
                  }}
                >
                  <InputLabel id="category-select-label" shrink>Select Whole Category (Optional)</InputLabel>
                  <Select
                    multiple
                    labelId="category-select-label"
                    label="Select Whole Category (Optional)"
                    value={selectedPromotionCategories}
                    onChange={(event) => {
                      setSelectedPromotionCategories(event.target.value);
                    }}
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) {
                        return <em style={{ color: '#999' }}>No category selected</em>;
                      }
                      if (selected.length === 1) {
                        return selected[0];
                      }
                      return `${selected.length} categories selected`;
                    }}
                    displayEmpty
                    notched
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          mt: 1,
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        },
                      },
                    }}
                  >
                    {availableCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        <Checkbox checked={selectedPromotionCategories.includes(category)} />
                        <ListItemText primary={category} />
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Select categories to apply promotion to all products in those categories. You can also select individual products below.
                  </Typography>
                </FormControl>
              )}

              {/* Enhanced Product Selector */}
              <FormControl 
                fullWidth 
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2C3E50',
                    fontWeight: 600,
                  },
                }}
              >
                <InputLabel id="products-select-label" shrink>Select Products (Optional)</InputLabel>
                <Select
                  multiple
                  labelId="products-select-label"
                  label="Select Products (Optional)"
                  name="productIds"
                  value={form.productIds}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      productIds: event.target.value,
                    }))
                  }
                  onOpen={() => setProductSelectorOpen(true)}
                  onClose={() => {
                    setProductSelectorOpen(false);
                    setProductSearchQuery('');
                    setSelectedCategories([]);
                  }}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return <em style={{ color: '#999' }}>No products selected</em>;
                    }
                    // Get product names from productLookup
                    const productNames = selected
                      .map((id) => {
                        const product = productLookup.get(String(id));
                        return product?.name || `Product ${id}`;
                      })
                      .filter(Boolean);
                    
                    // Show first 2 product names, then count if more
                    if (productNames.length <= 2) {
                      return productNames.join(', ');
                    }
                    return `${productNames.slice(0, 2).join(', ')} +${productNames.length - 2} more`;
                  }}
                  displayEmpty
                  notched
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 500,
                        mt: 1,
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      },
                    },
                  }}
                >
                  {/* Search Bar */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search products by name, category, or price..."
                      value={productSearchQuery}
                      onChange={(e) => setProductSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                        },
                      }}
                    />
                  </Box>

                  {/* Category Filter */}
                  {availableCategories.length > 0 && (
                    <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', color: 'text.secondary' }}>
                        Filter by Category:
                      </Typography>
                      <FormGroup row>
                        {availableCategories.map((category) => (
                          <FormControlLabel
                            key={category}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedCategories.includes(category)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    setSelectedCategories([...selectedCategories, category]);
                                  } else {
                                    setSelectedCategories(selectedCategories.filter((c) => c !== category));
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {category}
                              </Typography>
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  )}

                  {/* Product List */}
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => {
                      const productId = String(product.id ?? product._id);
                      const isSelected = form.productIds.includes(productId);
                      return (
                        <MenuItem 
                          key={productId} 
                          value={productId}
                          sx={{
                            py: 1.5,
                            '&:hover': {
                              bgcolor: '#f5f5f5',
                            },
                          }}
                        >
                          <Checkbox checked={isSelected} sx={{ mr: 1 }} />
                          <ListItemIcon>
                            <Inventory fontSize="small" sx={{ color: isSelected ? 'primary.main' : 'text.secondary' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                {product.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                ₹{product.price} • {product.category || 'Uncategorized'}
                              </Typography>
                            }
                          />
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled>
                      <ListItemText 
                        primary={
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            {productSearchQuery || selectedCategories.length > 0
                              ? 'No products match your filters'
                              : 'No approved products available'}
                          </Typography>
                        } 
                      />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
            <Button
              onClick={() => {
                setCreateDialogOpen(false);
                resetForm();
              }}
              variant="outlined"
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<LocalOffer />}
              onClick={handlePromotionSubmit}
              disabled={submitting}
              sx={{
                bgcolor: '#2C3E50',
                background: 'linear-gradient(135deg, #2C3E50 0%, #34495e 100%)',
                boxShadow: '0 4px 15px rgba(44, 62, 80, 0.2)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #1a252f 0%, #2C3E50 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(44, 62, 80, 0.3)',
                },
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              {submitting ? 'Creating...' : 'Create Promotion'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Promotion Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            },
          }}
        >
          <DialogTitle sx={{ 
            pb: 2,
            borderBottom: '2px solid rgba(44, 62, 80, 0.1)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: '#2C3E50',
                background: 'linear-gradient(135deg, #2C3E50 0%, #34495e 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Edit Promotion
              </Typography>
              <IconButton
                onClick={handleEditDialogClose}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3}>
              {/* Title Field */}
              <TextField
                fullWidth
                label="Promotion Title"
                name="title"
                value={editForm.title}
                onChange={handleEditFormChange}
                placeholder="e.g., Summer Sale, Black Friday Deal"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2C3E50',
                    fontWeight: 600,
                  },
                }}
              />

              {/* Discount Type and Value */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover fieldset': {
                        borderColor: '#2C3E50',
                        borderWidth: 2,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2C3E50',
                        borderWidth: 2,
                        boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2C3E50',
                      fontWeight: 600,
                    },
                  }}
                >
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    name="discountType"
                    value={editForm.discountType}
                    onChange={handleEditFormChange}
                    label="Discount Type"
                  >
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="amount">Fixed Amount</MenuItem>
                  </Select>
                </FormControl>
                
                {editForm.discountType === 'percentage' ? (
                  <TextField
                    fullWidth
                    label="Discount Percentage"
                    name="discount"
                    value={editForm.discount}
                    onChange={handleEditFormChange}
                    type="number"
                    placeholder="0-100"
                    inputProps={{ min: 1, max: 100 }}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2.5,
                        bgcolor: 'white',
                        transition: 'all 0.3s ease',
                        '&:hover fieldset': {
                          borderColor: '#2C3E50',
                          borderWidth: 2,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2C3E50',
                          borderWidth: 2,
                          boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#2C3E50',
                        fontWeight: 600,
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Percent sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Discount Amount"
                    name="discountAmount"
                    value={editForm.discountAmount}
                    onChange={handleEditFormChange}
                    type="number"
                    placeholder="0.00"
                    inputProps={{ min: 0.01, step: 0.01 }}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2.5,
                        bgcolor: 'white',
                        transition: 'all 0.3s ease',
                        '&:hover fieldset': {
                          borderColor: '#2C3E50',
                          borderWidth: 2,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2C3E50',
                          borderWidth: 2,
                          boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#2C3E50',
                        fontWeight: 600,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            ₹
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Box>

              {/* Start DateTime */}
              <TextField
                fullWidth
                label="Start Date & Time"
                name="startDateTime"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editForm.startDateTime}
                onChange={handleEditFormChange}
                variant="outlined"
                inputProps={{ step: 60 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2C3E50',
                    fontWeight: 600,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* End DateTime */}
              <TextField
                fullWidth
                label="End Date & Time"
                name="endDateTime"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editForm.endDateTime}
                onChange={handleEditFormChange}
                variant="outlined"
                inputProps={{ step: 60 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2C3E50',
                    fontWeight: 600,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Category Selector (Optional - for whole category promotion) */}
              {availableCategories.length > 0 && (
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover fieldset': {
                        borderColor: '#2C3E50',
                        borderWidth: 2,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2C3E50',
                        borderWidth: 2,
                        boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2C3E50',
                      fontWeight: 600,
                    },
                  }}
                >
                  <InputLabel id="edit-category-select-label" shrink>Select Whole Category (Optional)</InputLabel>
                  <Select
                    multiple
                    labelId="edit-category-select-label"
                    label="Select Whole Category (Optional)"
                    value={editSelectedPromotionCategories}
                    onChange={(event) => {
                      setEditSelectedPromotionCategories(event.target.value);
                    }}
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) {
                        return <em style={{ color: '#999' }}>No category selected</em>;
                      }
                      if (selected.length === 1) {
                        return selected[0];
                      }
                      return `${selected.length} categories selected`;
                    }}
                    displayEmpty
                    notched
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          mt: 1,
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        },
                      },
                    }}
                  >
                    {availableCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        <Checkbox checked={editSelectedPromotionCategories.includes(category)} />
                        <ListItemText primary={category} />
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Select categories to apply promotion to all products in those categories. You can also select individual products below.
                  </Typography>
                </FormControl>
              )}

              {/* Enhanced Product Selector */}
              <FormControl 
                fullWidth 
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2C3E50',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(44, 62, 80, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2C3E50',
                    fontWeight: 600,
                  },
                }}
              >
                <InputLabel id="edit-products-select-label" shrink>Select Products (Optional)</InputLabel>
                <Select
                  multiple
                  labelId="edit-products-select-label"
                  label="Select Products (Optional)"
                  name="productIds"
                  value={editForm.productIds}
                  onChange={(event) =>
                    setEditForm((prev) => ({
                      ...prev,
                      productIds: event.target.value,
                    }))
                  }
                  onOpen={() => setProductSelectorOpen(true)}
                  onClose={() => {
                    setProductSelectorOpen(false);
                    setEditProductSearchQuery('');
                    setEditSelectedCategories([]);
                  }}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return <em style={{ color: '#999' }}>No products selected</em>;
                    }
                    // Get product names from productLookup
                    const productNames = selected
                      .map((id) => {
                        const product = productLookup.get(String(id));
                        return product?.name || `Product ${id}`;
                      })
                      .filter(Boolean);
                    
                    // Show first 2 product names, then count if more
                    if (productNames.length <= 2) {
                      return productNames.join(', ');
                    }
                    return `${productNames.slice(0, 2).join(', ')} +${productNames.length - 2} more`;
                  }}
                  displayEmpty
                  notched
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 500,
                        mt: 1,
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      },
                    },
                  }}
                >
                  {/* Search Bar */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search products by name, category, or price..."
                      value={editProductSearchQuery}
                      onChange={(e) => setEditProductSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                        },
                      }}
                    />
                  </Box>

                  {/* Category Filter */}
                  {availableCategories.length > 0 && (
                    <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', color: 'text.secondary' }}>
                        Filter by Category:
                      </Typography>
                      <FormGroup row>
                        {availableCategories.map((category) => (
                          <FormControlLabel
                            key={category}
                            control={
                              <Checkbox
                                size="small"
                                checked={editSelectedCategories.includes(category)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    setEditSelectedCategories([...editSelectedCategories, category]);
                                  } else {
                                    setEditSelectedCategories(editSelectedCategories.filter((c) => c !== category));
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {category}
                              </Typography>
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  )}

                  {/* Product List */}
                  {filteredEditProducts.length > 0 ? (
                    filteredEditProducts.map((product) => {
                      const productId = String(product.id ?? product._id);
                      const isSelected = editForm.productIds.includes(productId);
                      return (
                        <MenuItem 
                          key={productId} 
                          value={productId}
                          sx={{
                            py: 1.5,
                            '&:hover': {
                              bgcolor: '#f5f5f5',
                            },
                          }}
                        >
                          <Checkbox checked={isSelected} sx={{ mr: 1 }} />
                          <ListItemIcon>
                            <Inventory fontSize="small" sx={{ color: isSelected ? 'primary.main' : 'text.secondary' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                {product.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                ₹{product.price} • {product.category || 'Uncategorized'}
                              </Typography>
                            }
                          />
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled>
                      <ListItemText 
                        primary={
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            {editProductSearchQuery || editSelectedCategories.length > 0
                              ? 'No products match your filters'
                              : 'No approved products available'}
                          </Typography>
                        } 
                      />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid rgba(44, 62, 80, 0.1)' }}>
            <Button
              onClick={handleEditDialogClose}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditPromotionSubmit}
              disabled={submitting}
              startIcon={<LocalOffer />}
              sx={{
                textTransform: 'none',
                borderRadius: 2.5,
                px: 4,
                py: 1,
                bgcolor: '#2C3E50',
                background: 'linear-gradient(135deg, #2C3E50 0%, #34495e 100%)',
                boxShadow: '0 4px 15px rgba(44, 62, 80, 0.2)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #1a252f 0%, #2C3E50 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(44, 62, 80, 0.3)',
                },
                fontWeight: 600,
                transition: 'all 0.3s ease',
              }}
            >
              {submitting ? 'Updating...' : 'Update Promotion'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Profile Tab
  const ProfileTab = () => {
    const [form, setForm] = useState(() => mapUserToProfileForm(user));
    const [logoPreview, setLogoPreview] = useState(() =>
      user?.businessProfile?.logoUrl ? getFullImageUrl(user.businessProfile.logoUrl) : null
    );
    const [logoUploading, setLogoUploading] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const logoFileInputRef = useRef(null);
    const autoSaveTimeoutRef = useRef(null);

    useEffect(() => {
      setForm(mapUserToProfileForm(user));
      setLogoPreview(
        user?.businessProfile?.logoUrl ? getFullImageUrl(user.businessProfile.logoUrl) : null
      );
    }, [user]);

    useEffect(() => () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    }, []);

    const saveProfile = useCallback(
      async (currentForm) => {
        if (!user?.id) return;

        const normalize = (value) => (value ?? "").trim();
        const original = mapUserToProfileForm(user);

        const trimmedStoreName = normalize(currentForm.storeName);
        const originalStoreName = normalize(original.storeName);

        const trimmedEmail = normalize(currentForm.email);
        const originalEmail = normalize(original.email);

        const trimmedPhone = normalize(currentForm.phone);
        const originalPhone = normalize(original.phone);

        const trimmedDescription = normalize(currentForm.description);
        const originalDescription = normalize(original.description);

        const trimmedBusinessType = normalize(currentForm.businessType);
        const originalBusinessType = normalize(original.businessType);

        const businessProfileUpdate = {};
        if (trimmedStoreName && trimmedStoreName !== originalStoreName) {
          businessProfileUpdate.businessName = trimmedStoreName;
        }
        if (trimmedBusinessType && trimmedBusinessType !== originalBusinessType) {
          businessProfileUpdate.businessType = trimmedBusinessType;
        }
        if (trimmedEmail !== originalEmail) {
          businessProfileUpdate.businessEmail = trimmedEmail;
        }
        if (trimmedDescription !== originalDescription) {
          businessProfileUpdate.description = trimmedDescription;
        }

        const profileUpdate = {};
        if (trimmedPhone && trimmedPhone !== originalPhone) {
          profileUpdate.phoneNumber = trimmedPhone;
        }
        if (Object.keys(businessProfileUpdate).length > 0) {
          profileUpdate.businessProfile = businessProfileUpdate;
        }

        if (Object.keys(profileUpdate).length === 0) {
          return;
        }

        setProfileSaving(true);
        try {
          await dispatch(updateSellerProfile({ userId: user.id, profileData: profileUpdate })).unwrap();
          toast.success('Profile updated', { toastId: 'seller-profile-updated' });
        } catch (error) {
          const message =
            typeof error === 'string'
              ? error
              : error?.message || 'Failed to save profile';
          toast.error(message, { toastId: 'seller-profile-error' });
        } finally {
          setProfileSaving(false);
        }
      },
      [dispatch, user]
    );

    const scheduleProfileSave = useCallback(
      (nextForm) => {
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
        autoSaveTimeoutRef.current = setTimeout(() => {
          saveProfile(nextForm);
        }, 1200);
      },
      [saveProfile]
    );

    const handleChange = (e) => {
      const { name, value } = e.target;
      setForm((prev) => {
        const updated = { ...prev, [name]: value };
        scheduleProfileSave(updated);
        return updated;
      });
    };

    const handleLogoUploadClick = () => {
      logoFileInputRef.current?.click();
    };

    const handleLogoFileChange = async (event) => {
      const file = event.target.files?.[0];
      if (!file || !user?.id) {
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      setLogoUploading(true);
      try {
        const uploadResponse = await axios.post(`${API_BASE_URL}/files/upload/logo`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const { logoUrl, fullUrl } = uploadResponse.data;
        await dispatch(updateSellerLogo({ userId: user.id, logoUrl })).unwrap();
        setLogoPreview(fullUrl || getFullImageUrl(logoUrl));
        toast.success("Logo updated successfully!");
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to upload logo");
      } finally {
        setLogoUploading(false);
        if (event.target) {
          event.target.value = "";
        }
      }
    };

    const handleRemoveLogo = async () => {
      if (!user?.id || (!logoPreview && !user?.businessProfile?.logoUrl)) {
        return;
      }
      setLogoUploading(true);
      try {
        await dispatch(updateSellerLogo({ userId: user.id, logoUrl: null })).unwrap();
        setLogoPreview(null);
        toast.success("Logo removed successfully!");
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to remove logo");
      } finally {
        setLogoUploading(false);
      }
    };

    return (
      <Box>
        {/* Header Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 1, 
            mb: 1.7, 
            background: 'linear-gradient(135deg, #2C3E50,#2C3E50)',
            borderRadius: 3,
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              src={logoPreview || undefined}
              sx={{
                width: 100,
                height: 100,
                bgcolor: 'white',
                color: 'primary.main',
                fontSize: '2.5rem',
                fontWeight: 700,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
              }}
            >
              {!logoPreview && form.storeName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ color:'white',fontWeight: 700, mb: 1 }}>
                {form.storeName}
          </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Chip 
                  icon={<Verified sx={{ color: 'white !important' }} />}
                  label={user?.businessProfile?.approvalStatus || "Verified Seller"}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)'
                  }}
                />
                <Chip 
                  icon={<Store sx={{ color: 'white !important' }} />}
                  label={form.businessType}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)'
                  }}
                />
              </Box>
              <Typography variant="body1" sx={{color:'white', opacity: 0.95, maxWidth: 600 }}>
                {form.description}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={
                logoUploading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PhotoCamera />
                )
              }
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                },
                transition: 'all 0.3s ease'
              }}
              onClick={handleLogoUploadClick}
              disabled={logoUploading}
            >
              {logoUploading ? 'Uploading...' : 'Upload Logo'}
            </Button>
            <input
              type="file"
              accept="image/*"
              ref={logoFileInputRef}
              style={{ display: "none" }}
              onChange={handleLogoFileChange}
            />
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleRemoveLogo}
              disabled={logoUploading || (!logoPreview && !user?.businessProfile?.logoUrl)}
              sx={{
                borderRadius: 999,
              }}
            >
              Remove Logo
            </Button>
            {profileSaving && (
              <Chip
                label="Saving..."
                icon={<CircularProgress size={12} sx={{ color: 'inherit !important' }} />}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: 999,
                  '& .MuiChip-icon': {
                    marginLeft: 0.5,
                  },
                }}
              />
            )}
          </Box>
        </Paper>

        {/* Profile Information Cards */}
        <Grid container spacing={3}>
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={0}
              sx={{ 
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <Phone />
                  </Avatar>
                }
                title={
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Contact Information
                  </Typography>
                }
                subheader="Manage your contact details"
              />
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Store Name"
                name="storeName"
                value={form.storeName}
                onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <Store sx={{ mr: 1, color: 'action.active' }} />
                    }}
              />
              <TextField
                fullWidth
                label="Contact Email"
                name="email"
                    type="email"
                value={form.email}
                onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                    }}
              />
              <TextField
                fullWidth
                    label="Phone Number"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
                    }}
              />
                </Box>
              </CardContent>
            </Card>
            </Grid>

          {/* Business Information */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={0}
              sx={{ 
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <Business />
                  </Avatar>
                }
                title={
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Business Information
                  </Typography>
                }
                subheader="Your business details"
              />
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Business Type"
                    name="businessType"
                    value={form.businessType}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <Category sx={{ mr: 1, color: 'action.active' }} />
                    }}
                  />
              <TextField
                fullWidth
                    label="Business Address"
                name="address"
                value={form.address}
                onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={3}
                    InputProps={{
                      startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active', alignSelf: 'flex-start', mt: 1 }} />
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
            </Grid>

          {/* Store Description */}
            <Grid item xs={12}>
            <Card 
              elevation={0}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <Edit />
                  </Avatar>
                }
                title={
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Store Description
                  </Typography>
                }
                subheader="Tell customers about your business"
              />
              <CardContent>
              <TextField
                fullWidth
                multiline
                  rows={4}
                  label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                  variant="outlined"
                  placeholder="Describe your business, products, and what makes you unique..."
              />
              </CardContent>
            </Card>
            </Grid>
          </Grid>
      </Box>
    );
  };

  // Visit Requests Tab
  const VisitRequestsTab = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { 
      requests: visitRequests, 
      loading, 
      error, 
      stats 
    } = useSelector((state) => state.visitRequests);
    const [statusFilter, setStatusFilter] = useState('all');
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [productDetails, setProductDetails] = useState(null);
    const [productLoading, setProductLoading] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Refs to prevent duplicate API calls
    const requestsFetchedRef = useRef(false);
    const statsFetchedRef = useRef(false);
    const isFetchingRequestsRef = useRef(false);
    const isFetchingStatsRef = useRef(false);
    const previousUserIdRef = useRef(null);

    const handleViewRequest = (request) => {
      setSelectedRequest(request);
      setViewDialogOpen(true);
      setCurrentImageIndex(0);
    };

    const handleCloseViewDialog = () => {
      setViewDialogOpen(false);
      setSelectedRequest(null);
      setProductDetails(null);
      setCurrentImageIndex(0);
    };

    // Fetch product details when selectedRequest changes
    useEffect(() => {
      const fetchProductDetails = async () => {
        if (selectedRequest && selectedRequest.productId) {
          setProductLoading(true);
          try {
            const product = await productService.getProductById(selectedRequest.productId);
            setProductDetails(product.product || product);
          } catch (error) {
            console.error('Error fetching product details:', error);
            setProductDetails(null);
          } finally {
            setProductLoading(false);
          }
        } else {
          setProductDetails(null);
        }
      };

      fetchProductDetails();
    }, [selectedRequest]);

    // Get all product images and videos (primary first, then others)
    const getProductMedia = () => {
      if (!productDetails) return { images: [], videos: [], allMedia: [] };
      
      const images = [];
      const videos = [];
      
      // Add primary image first if available
      if (productDetails.primaryImageUrl) {
        images.push(productDetails.primaryImageUrl);
      }
      
      // Add images from imageUrls array (if exists)
      if (productDetails.imageUrls && Array.isArray(productDetails.imageUrls)) {
        productDetails.imageUrls.forEach(mediaUrl => {
          if (mediaUrl) {
            const isVideo = mediaUrl.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);
            if (isVideo && mediaUrl !== productDetails.primaryImageUrl) {
              videos.push(mediaUrl);
            } else if (!isVideo && mediaUrl !== productDetails.primaryImageUrl) {
              images.push(mediaUrl);
            }
          }
        });
      }
      
      // Add other images/videos from images array
      if (productDetails.images && Array.isArray(productDetails.images)) {
        productDetails.images.forEach(img => {
          const mediaUrl = typeof img === 'string' ? img : (img.url || img.imageUrl || img.videoUrl);
          if (mediaUrl) {
            const isVideo = mediaUrl.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/) || img.type === 'video';
            if (isVideo && mediaUrl !== productDetails.primaryImageUrl && !videos.includes(mediaUrl)) {
              videos.push(mediaUrl);
            } else if (!isVideo && mediaUrl !== productDetails.primaryImageUrl && !images.includes(mediaUrl)) {
              images.push(mediaUrl);
            }
          }
        });
      }
      
      // Combine images and videos for display
      const allMedia = [...images, ...videos];
      
      return { images, videos, allMedia };
    };

    const { images: productImages, videos: productVideos, allMedia: productMedia } = getProductMedia();

    const handleNextMedia = () => {
      if (productMedia.length > 0) {
        setCurrentImageIndex((prev) => (prev + 1) % productMedia.length);
      }
    };

    const handlePreviousMedia = () => {
      if (productMedia.length > 0) {
        setCurrentImageIndex((prev) => (prev - 1 + productMedia.length) % productMedia.length);
      }
    };

    const isCurrentMediaVideo = () => {
      if (productMedia.length === 0) return false;
      const currentMedia = productMedia[currentImageIndex];
      return currentMedia && currentMedia.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);
    };

    // Fetch visit requests and stats on component mount or when user changes
    useEffect(() => {
      const sellerId = user?.id;
      if (!sellerId) return;

      // Reset refs if user changed
      if (previousUserIdRef.current !== null && previousUserIdRef.current !== sellerId) {
        requestsFetchedRef.current = false;
        statsFetchedRef.current = false;
      }
      
      // Only fetch if not already fetched for this user and not currently fetching
      const shouldFetchRequests = !requestsFetchedRef.current || previousUserIdRef.current !== sellerId;
      const shouldFetchStats = !statsFetchedRef.current || previousUserIdRef.current !== sellerId;
      
      if (shouldFetchRequests && !isFetchingRequestsRef.current) {
        previousUserIdRef.current = sellerId;
        isFetchingRequestsRef.current = true;
        requestsFetchedRef.current = true;
        dispatch(fetchVisitRequests({ sellerId })).finally(() => {
          isFetchingRequestsRef.current = false;
        });
      }
      
      if (shouldFetchStats && !isFetchingStatsRef.current) {
        isFetchingStatsRef.current = true;
        statsFetchedRef.current = true;
        dispatch(getVisitRequestStats({ sellerId })).finally(() => {
          isFetchingStatsRef.current = false;
        });
      }
    }, [dispatch, user?.id]);

    const statusConfig = {
      PENDING: { 
        label: "Pending Inspection", 
        color: "#FFA726", 
        bgColor: "#FFF3E0", 
        icon: <AccessTime sx={{ color: "#FFA726" }} /> 
      },
      APPROVED: { 
        label: "Approved", 
        color: "#66BB6A", 
        bgColor: "#E8F5E9", 
        icon: <CheckCircle sx={{ color: "#66BB6A" }} /> 
      },
      COMPLETED: { 
        label: "Completed", 
        color: "#4CAF50", 
        bgColor: "#E8F5E9", 
        icon: <CheckCircle sx={{ color: "#4CAF50" }} /> 
      },
      CANCELLED: { 
        label: "Cancelled", 
        color: "#EF5350", 
        bgColor: "#FFEBEE", 
        icon: <Close sx={{ color: "#EF5350" }} /> 
      },
      VISIT_DELAYED: { 
        label: "Visit Delayed", 
        color: "#FF7043", 
        bgColor: "#FFF3E0", 
        icon: <AccessTime sx={{ color: "#FF7043" }} /> 
      }
    };

    // Calculate stats from actual visit requests data
    const statusCounts = {
      PENDING: (visitRequests || []).filter(req => req.status === 'PENDING').length,
      APPROVED: (visitRequests || []).filter(req => req.status === 'APPROVED').length,
      COMPLETED: (visitRequests || []).filter(req => req.status === 'COMPLETED').length,
      CANCELLED: (visitRequests || []).filter(req => req.status === 'CANCELLED').length,
      VISIT_DELAYED: (visitRequests || []).filter(req => req.status === 'VISIT_DELAYED').length
    };

    const filteredRequests = statusFilter === 'all' 
      ? (visitRequests || []).filter(request => request.status !== 'REJECTED')
      : (visitRequests || []).filter(request => request.status === statusFilter && request.status !== 'REJECTED');

    const handleStatusUpdate = async (requestId, newStatus) => {
      try {
        await dispatch(updateVisitRequestStatus({ 
          requestId, 
          status: newStatus 
        })).unwrap();
        
        // Refresh stats after status update (only if not already fetching)
        if (user?.id && !isFetchingStatsRef.current) {
          isFetchingStatsRef.current = true;
          dispatch(getVisitRequestStats({ sellerId: user.id })).finally(() => {
            isFetchingStatsRef.current = false;
          });
        }
        
        toast.success('Visit status updated successfully!');
      } catch (error) {
        console.error('Failed to update visit status:', error);
        toast.error('Failed to update visit status');
      }
    };



    return (
      <Box>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Visit Request Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track customer visit requests for your properties and products
          </Typography>
        </Box>

        {/* Status Cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              border: '2px solid #1e3a8a', 
              borderRadius: 2,
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <AccessTime />
                  </Avatar>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Total Requests
                    </Typography>
                    <Typography variant="h4">
                      {visitRequests?.length || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              border: '2px solid #1e3a8a', 
              borderRadius: 2,
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                    <AccessTime />
                  </Avatar>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Pending Inspection
                    </Typography>
                    <Typography variant="h4">
                      {statusCounts.PENDING || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              border: '2px solid #1e3a8a', 
              borderRadius: 2,
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                    <Close />
                  </Avatar>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Cancelled
                    </Typography>
                    <Typography variant="h4">
                      {statusCounts.CANCELLED || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              border: '2px solid #1e3a8a', 
              borderRadius: 2,
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                    <CheckCircle />
                  </Avatar>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Completed
                    </Typography>
                    <Typography variant="h4">
                      {statusCounts.COMPLETED || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter Dropdown */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={statusFilter}
              label="Filter by Status"
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>All Requests</Typography>
                </Box>
              </MenuItem>
              {Object.entries(statusConfig).map(([status, config]) => (
                <MenuItem key={status} value={status}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Box 
                      sx={{ 
                        p: 0.5, 
                        borderRadius: 1, 
                        backgroundColor: `${config.color}15`,
                        border: `1px solid ${config.color}30`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 24,
                        minHeight: 24
                      }}
                    >
                      {React.cloneElement(config.icon, { sx: { fontSize: 16 } })}
                    </Box>
                    <Typography>{config.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Data Table */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Visit Requests
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  label={`${filteredRequests.length} requests`}
                  color="primary"
                  variant="outlined"
                />
                {statusFilter !== "all" && (
                  <Button 
                    size="small" 
                    onClick={() => setStatusFilter("all")}
                    startIcon={<FilterList />}
                  >
                    Clear Filter
                  </Button>
                )}
              </Box>
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => dispatch(clearError())}
                  >
                    Dismiss
                  </Button>
                }
              >
                {error}
              </Alert>
            )}

            {filteredRequests.length === 0 && !loading && !error && (
              <Card sx={{ textAlign: 'center', py: 4 }}>
                <CardContent>
                  <ErrorOutline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No visit requests found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {statusFilter === 'all' 
                      ? 'There are no visit requests at the moment.'
                      : `No visit requests with status "${statusConfig[statusFilter]?.label || statusFilter}".`
                    }
                  </Typography>
                </CardContent>
              </Card>
            )}

            {(filteredRequests.length > 0 || loading) && (
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person sx={{ fontSize: 18 }} />
                          Visit Request ID
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationOn sx={{ fontSize: 18 }} />
                          Product & Location
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday sx={{ fontSize: 18 }} />
                          Visit Details
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      // Loading skeleton rows
                      Array.from({ length: 3 }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                          <TableCell><Skeleton variant="rectangular" width={80} height={24} /></TableCell>
                        </TableRow>
                      ))
                    ) : (
                      filteredRequests.map((request) => (
                        <TableRow 
                          key={request.id}
                          sx={{ 
                            '&:hover': { backgroundColor: '#f9f9f9' },
                            '&:last-child td, &:last-child th': { border: 0 }
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {request.id || request.visitRequestId || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {request.productName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {request.location}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {request.preferredDate 
                                  ? new Date(request.preferredDate).toLocaleDateString('en-IN', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })
                                  : 'Not specified'
                                }
                                {request.preferredTime && (
                                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    at {request.preferredTime}
                                  </Typography>
                                )}
                              </Typography>
                              {request.visitDate && (
                                <Typography variant="caption" color="text.secondary">
                                  Visit: {new Date(request.visitDate).toLocaleDateString('en-IN')}
                                </Typography>
                              )}
                              {request.notes && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                  "{request.notes}"
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statusConfig[request.status]?.label || request.status}
                              sx={{
                                backgroundColor: statusConfig[request.status]?.bgColor || '#f5f5f5',
                                color: statusConfig[request.status]?.color || '#666',
                                fontWeight: 500,
                                fontSize: '0.75rem'
                              }}
                              icon={statusConfig[request.status]?.icon}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleViewRequest(request)}
                              sx={{ color: 'primary.main' }}
                            >
                              <Visibility />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* View Request Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={handleCloseViewDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility sx={{ color: 'primary.main' }} />
            Visit Request Details
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedRequest && (
              <Box sx={{ pt: 1 }}>
                {/* Customer Information */}
                <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      <Person fontSize="small" />
                    </Avatar>
                    Customer Information
                          </Typography>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Visit Request ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedRequest.id || selectedRequest.visitRequestId || 'N/A'}
                          </Typography>
                        </Box>
                </Paper>

                {/* Request Information */}
                <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                      <Inventory fontSize="small" />
                    </Avatar>
                    Request Information
                          </Typography>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Product</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedRequest.productName}
                          </Typography>
                        </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={statusConfig[selectedRequest.status]?.label || selectedRequest.status}
                            sx={{
                              backgroundColor: statusConfig[selectedRequest.status]?.bgColor || '#f5f5f5',
                              color: statusConfig[selectedRequest.status]?.color || '#666',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                            }}
                            icon={statusConfig[selectedRequest.status]?.icon}
                          />
                    </Box>
                        </Box>
                        <Box>
                    <Typography variant="body2" color="text.secondary">Visit Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedRequest.preferredDate 
                              ? new Date(selectedRequest.preferredDate).toLocaleDateString('en-IN', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                              : 'Not specified'
                            }
                          </Typography>
                        </Box>
                </Paper>

                {/* Product Images/Videos */}
                {selectedRequest.productId && (
                  <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                        <Inventory fontSize="small" />
                      </Avatar>
                      Product Images & Videos
                    </Typography>
                    {productLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">Loading product media...</Typography>
                      </Box>
                    ) : productMedia.length > 0 ? (
                      <Box>
                        <Box sx={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: 500 }}>
                          {isCurrentMediaVideo() ? (
                            <Box
                              component="video"
                              src={productMedia[currentImageIndex]?.startsWith('http') 
                                ? productMedia[currentImageIndex] 
                                : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8083'}${productMedia[currentImageIndex]}`}
                              controls
                              sx={{
                                width: '100%',
                                maxWidth: 500,
                                height: 'auto',
                                maxHeight: 400,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.300',
                                display: 'block',
                                bgcolor: '#000'
                              }}
                              onError={(e) => {
                                console.error('Error loading video:', e);
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={productMedia[currentImageIndex]?.startsWith('http') 
                                ? productMedia[currentImageIndex] 
                                : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8083'}${productMedia[currentImageIndex]}`}
                              alt={`Product ${isCurrentMediaVideo() ? 'video' : 'image'} ${currentImageIndex + 1}`}
                              sx={{
                                width: '100%',
                                maxWidth: 500,
                                height: 'auto',
                                maxHeight: 400,
                                objectFit: 'contain',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.300',
                                display: 'block',
                                bgcolor: '#f5f5f5'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          {productMedia.length > 1 && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center', alignItems: 'center' }}>
                              <IconButton
                                onClick={handlePreviousMedia}
                                size="small"
                                sx={{
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'primary.dark' }
                                }}
                              >
                                <ChevronLeft />
                              </IconButton>
                              <Typography variant="body2" sx={{ px: 2, fontWeight: 600 }}>
                                {currentImageIndex + 1} / {productMedia.length}
                              </Typography>
                              <IconButton
                                onClick={handleNextMedia}
                                size="small"
                                sx={{
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'primary.dark' }
                                }}
                              >
                                <ChevronRight />
                              </IconButton>
                            </Box>
                          )}
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                            {productImages.length > 0 && (
                              <Chip 
                                label={`${productImages.length} Image${productImages.length !== 1 ? 's' : ''}`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            )}
                            {productVideos.length > 0 && (
                              <Chip 
                                label={`${productVideos.length} Video${productVideos.length !== 1 ? 's' : ''}`} 
                                size="small" 
                                color="secondary" 
                                variant="outlined"
                                icon={<PlayArrow />}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                        No product images or videos available
                      </Typography>
                    )}
                  </Paper>
                )}

                {/* Additional Details */}
                {selectedRequest.location && (
                  <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                        <LocationOn fontSize="small" />
                      </Avatar>
                      Additional Details
                          </Typography>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Location</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedRequest.location}
                          </Typography>
                        </Box>
                  </Paper>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseViewDialog} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Lot Requests Tab
  const LotRequestsTab = () => {
    const [lotRequests, setLotRequests] = useState([]);
    const [lotRequestsLoading, setLotRequestsLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [productDetails, setProductDetails] = useState(null);
    const [productLoading, setProductLoading] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Fetch lot requests for the seller (only approved ones)
    useEffect(() => {
      const fetchLotRequests = async () => {
        if (!user?.id) return;
        
        setLotRequestsLoading(true);
        try {
          const response = await lotRequestService.getLotRequestsBySeller(user.id);
          // Filter to only show LOT_APPROVED requests and exclude LOT_REJECTED
          const approvedRequests = (response.content || response || []).filter(
            req => req.status === 'LOT_APPROVED' || 
                   req.status === 'READY_FOR_DELIVERY' || 
                   req.status === 'DELIVERED' || 
                   req.status === 'CANCELLED'
          );
          setLotRequests(approvedRequests);
        } catch (error) {
          console.error('Error fetching lot requests:', error);
          toast.error('Failed to fetch lot requests');
          // Initialize demo data if no requests found
          try {
            await lotRequestService.initializeDemoData();
            const demoResponse = await lotRequestService.getLotRequestsBySeller(user.id);
            const approvedRequests = (demoResponse.content || demoResponse || []).filter(
              req => req.status === 'LOT_APPROVED' || 
                     req.status === 'READY_FOR_DELIVERY' || 
                     req.status === 'DELIVERED' || 
                     req.status === 'CANCELLED'
            );
            setLotRequests(approvedRequests);
          } catch (demoError) {
            console.error('Error initializing demo data:', demoError);
          }
        } finally {
          setLotRequestsLoading(false);
        }
      };

      fetchLotRequests();
    }, [user?.id]);

    const statusConfig = {
      LOT_APPROVED: { 
        label: "Lot Approved", 
        color: "#4CAF50", 
        bgColor: "#E8F5E9", 
        icon: <CheckCircle sx={{ color: "#4CAF50" }} /> 
      },
      CANCELLED: { 
        label: "Cancelled", 
        color: "#9E9E9E", 
        bgColor: "#F5F5F5", 
        icon: <Cancel sx={{ color: "#9E9E9E" }} /> 
      },
      DELIVERED: { 
        label: "Delivered", 
        color: "#4CAF50", 
        bgColor: "#E8F5E9", 
        icon: <CheckCircle sx={{ color: "#4CAF50" }} /> 
      },
      READY_FOR_DELIVERY: { 
        label: "Ready for Delivery", 
        color: "#2196F3", 
        bgColor: "#E3F2FD", 
        icon: <TrendingUp sx={{ color: "#2196F3" }} /> 
      }
    };

    const getStatusCounts = () => {
      return {
        LOT_APPROVED: lotRequests.filter(req => req.status === "LOT_APPROVED").length,
        CANCELLED: lotRequests.filter(req => req.status === "CANCELLED").length,
        DELIVERED: lotRequests.filter(req => req.status === "DELIVERED").length,
        READY_FOR_DELIVERY: lotRequests.filter(req => req.status === "READY_FOR_DELIVERY").length
      };
    };

    const filteredRequests = statusFilter === "all" 
      ? lotRequests 
      : lotRequests.filter(req => req.status === statusFilter);

    const handleStatusUpdate = async (requestId, newStatus) => {
      try {
        await lotRequestService.updateStatus(requestId, newStatus, user?.id || 'system', 'Status updated from seller dashboard');
        setLotRequests(prev => 
          prev.map(req => 
            req.id === requestId ? { ...req, status: newStatus } : req
          )
        );
        toast.success('Lot request status updated successfully!');
      } catch (error) {
        console.error('Error updating status:', error);
        toast.error('Failed to update lot request status');
      }
    };

    const handleStatusCardClick = (status) => {
      setStatusFilter(statusFilter === status ? "all" : status);
    };

    const handleViewRequest = (request) => {
      setSelectedRequest(request);
      setViewDialogOpen(true);
      setCurrentImageIndex(0);
    };

    const handleCloseViewDialog = () => {
      setViewDialogOpen(false);
      setSelectedRequest(null);
      setProductDetails(null);
      setCurrentImageIndex(0);
    };

    // Fetch product details when selectedRequest changes
    useEffect(() => {
      const fetchProductDetails = async () => {
        if (selectedRequest && selectedRequest.productId) {
          setProductLoading(true);
          try {
            const product = await productService.getProductById(selectedRequest.productId);
            setProductDetails(product.product || product);
          } catch (error) {
            console.error('Error fetching product details:', error);
            setProductDetails(null);
          } finally {
            setProductLoading(false);
          }
        } else {
          setProductDetails(null);
        }
      };

      fetchProductDetails();
    }, [selectedRequest]);

    // Get all product images and videos (primary first, then others)
    const getProductMedia = () => {
      if (!productDetails) return { images: [], videos: [], allMedia: [] };
      
      const images = [];
      const videos = [];
      
      // Add primary image first if available
      if (productDetails.primaryImageUrl) {
        images.push(productDetails.primaryImageUrl);
      }
      
      // Add images from imageUrls array (if exists)
      if (productDetails.imageUrls && Array.isArray(productDetails.imageUrls)) {
        productDetails.imageUrls.forEach(mediaUrl => {
          if (mediaUrl) {
            const isVideo = mediaUrl.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);
            if (isVideo && mediaUrl !== productDetails.primaryImageUrl) {
              videos.push(mediaUrl);
            } else if (!isVideo && mediaUrl !== productDetails.primaryImageUrl) {
              images.push(mediaUrl);
            }
          }
        });
      }
      
      // Add other images/videos from images array
      if (productDetails.images && Array.isArray(productDetails.images)) {
        productDetails.images.forEach(img => {
          const mediaUrl = typeof img === 'string' ? img : (img.url || img.imageUrl || img.videoUrl);
          if (mediaUrl) {
            const isVideo = mediaUrl.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/) || img.type === 'video';
            if (isVideo && mediaUrl !== productDetails.primaryImageUrl && !videos.includes(mediaUrl)) {
              videos.push(mediaUrl);
            } else if (!isVideo && mediaUrl !== productDetails.primaryImageUrl && !images.includes(mediaUrl)) {
              images.push(mediaUrl);
            }
          }
        });
      }
      
      // Combine images and videos for display
      const allMedia = [...images, ...videos];
      
      return { images, videos, allMedia };
    };

    const { images: productImages, videos: productVideos, allMedia: productMedia } = getProductMedia();

    const handleNextMedia = () => {
      if (productMedia.length > 0) {
        setCurrentImageIndex((prev) => (prev + 1) % productMedia.length);
      }
    };

    const handlePreviousMedia = () => {
      if (productMedia.length > 0) {
        setCurrentImageIndex((prev) => (prev - 1 + productMedia.length) % productMedia.length);
      }
    };

    const isCurrentMediaVideo = () => {
      if (productMedia.length === 0) return false;
      const currentMedia = productMedia[currentImageIndex];
      return currentMedia && currentMedia.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);
    };

    const statusCounts = getStatusCounts();

    return (
      <Box>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Lot Requests
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track lot inspection requests and approvals for your products
          </Typography>
        </Box>

        {/* Status Cards Grid - 5 cards with equal dimensions in one row */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4}>
            <Card sx={{ 
              border: '2px solid #1e3a8a', 
              borderRadius: 2,
              height: 120,
              minHeight: 120,
              maxHeight: 120,
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}>
              <CardContent sx={{ py: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 40, height: 40 }}>
                    <ShoppingCart />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                      Total Requests
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
                      {lotRequests.length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4}>
            <Card sx={{ 
              border: '2px solid #1e3a8a', 
              borderRadius: 2,
              height: 120,
              minHeight: 120,
              maxHeight: 120,
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}>
              <CardContent sx={{ py: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Avatar sx={{ bgcolor: 'error.main', mr: 2, width: 40, height: 40 }}>
                    <Cancel />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                      Cancelled
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
                      {statusCounts.CANCELLED}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4}>
            <Card sx={{ 
              border: '2px solid #1e3a8a', 
              borderRadius: 2,
              height: 120,
              minHeight: 120,
              maxHeight: 120,
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}>
              <CardContent sx={{ py: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 40, height: 40 }}>
                    <CheckCircle />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                      Lot Approved
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
                      {statusCounts.LOT_APPROVED}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4}>
            <Card sx={{ 
              border: '2px solid #1e3a8a', 
              borderRadius: 2,
              height: 120,
              minHeight: 120,
              maxHeight: 120,
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}>
              <CardContent sx={{ py: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 40, height: 40 }}>
                    <CheckCircle />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                      Delivered
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
                      {statusCounts.DELIVERED}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4}>
            <Card sx={{ 
              border: '2px solid #1e3a8a', 
              borderRadius: 2,
              height: 120,
              minHeight: 120,
              maxHeight: 120,
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}>
              <CardContent sx={{ py: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Avatar sx={{ bgcolor: 'info.main', mr: 2, width: 40, height: 40 }}>
                    <LocalShipping />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                      Ready for Delivery
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
                      {statusCounts.READY_FOR_DELIVERY}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter Dropdown */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={statusFilter}
              label="Filter by Status"
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>All Requests</Typography>
                </Box>
              </MenuItem>
              {Object.entries(statusConfig).map(([status, config]) => (
                <MenuItem key={status} value={status}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Box 
                      sx={{ 
                        p: 0.5, 
                        borderRadius: 1, 
                        backgroundColor: `${config.bgColor}`,
                        border: `1px solid ${config.color}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 24,
                        minHeight: 24
                      }}
                    >
                      {React.cloneElement(config.icon, { sx: { fontSize: 16 } })}
                    </Box>
                    <Typography>{config.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Data Table */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Lot Requests
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  label={`${filteredRequests.length} requests`}
                  color="primary"
                  variant="outlined"
                />
                {statusFilter !== "all" && (
                  <Button 
                    size="small" 
                    onClick={() => setStatusFilter("all")}
                    startIcon={<FilterList />}
                  >
                    Clear Filter
                  </Button>
                )}
              </Box>
            </Box>

            {filteredRequests.length > 0 ? (
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person sx={{ fontSize: 18 }} />
                          Lot Request ID
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Inventory sx={{ fontSize: 18 }} />
                          Product Details
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lotRequestsLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Loading lot requests...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRequests.map((request) => (
                        <TableRow key={request.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {request.id || request.lotRequestId || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {request.productName || request.product?.name || 'Unknown Product'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {request.productDetails || `${request.lotSize || 'N/A'} | ${request.product?.category || 'N/A'}`}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statusConfig[request.status]?.label || request.status}
                              sx={{
                                backgroundColor: statusConfig[request.status]?.bgColor || '#f5f5f5',
                                color: statusConfig[request.status]?.color || '#666',
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                              icon={statusConfig[request.status]?.icon}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => handleViewRequest(request)}
                              sx={{ mr: 1 }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Business sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No lot requests found for the selected filter.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* View Request Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={handleCloseViewDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility sx={{ color: 'primary.main' }} />
              Lot Request Details
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedRequest && (
              <Box sx={{ pt: 1 }}>
                {/* Customer Information */}
                <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    Lot Request ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedRequest.id || selectedRequest.lotRequestId || 'N/A'}
                  </Typography>
                </Paper>

                {/* Product Details */}
                <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    Product Details
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                    {selectedRequest.productName || selectedRequest.product?.name || 'Unknown Product'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {selectedRequest.productDetails || `${selectedRequest.lotSize || 'N/A'} | ${selectedRequest.product?.category || 'N/A'}`}
                  </Typography>
                </Paper>

                {/* Product Images/Videos */}
                {selectedRequest.productId && (
                  <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                        <Inventory fontSize="small" />
                      </Avatar>
                      Product Images & Videos
                    </Typography>
                    {productLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">Loading product media...</Typography>
                      </Box>
                    ) : productMedia.length > 0 ? (
                      <Box>
                        <Box sx={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: 500 }}>
                          {isCurrentMediaVideo() ? (
                            <Box
                              component="video"
                              src={productMedia[currentImageIndex]?.startsWith('http') 
                                ? productMedia[currentImageIndex] 
                                : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8083'}${productMedia[currentImageIndex]}`}
                              controls
                              sx={{
                                width: '100%',
                                maxWidth: 500,
                                height: 'auto',
                                maxHeight: 400,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.300',
                                display: 'block',
                                bgcolor: '#000'
                              }}
                              onError={(e) => {
                                console.error('Error loading video:', e);
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={productMedia[currentImageIndex]?.startsWith('http') 
                                ? productMedia[currentImageIndex] 
                                : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8083'}${productMedia[currentImageIndex]}`}
                              alt={`Product ${isCurrentMediaVideo() ? 'video' : 'image'} ${currentImageIndex + 1}`}
                              sx={{
                                width: '100%',
                                maxWidth: 500,
                                height: 'auto',
                                maxHeight: 400,
                                objectFit: 'contain',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.300',
                                display: 'block',
                                bgcolor: '#f5f5f5'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          {productMedia.length > 1 && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center', alignItems: 'center' }}>
                              <IconButton
                                onClick={handlePreviousMedia}
                                size="small"
                                sx={{
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'primary.dark' }
                                }}
                              >
                                <ChevronLeft />
                              </IconButton>
                              <Typography variant="body2" sx={{ px: 2, fontWeight: 600 }}>
                                {currentImageIndex + 1} / {productMedia.length}
                              </Typography>
                              <IconButton
                                onClick={handleNextMedia}
                                size="small"
                                sx={{
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'primary.dark' }
                                }}
                              >
                                <ChevronRight />
                              </IconButton>
                            </Box>
                          )}
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                            {productImages.length > 0 && (
                              <Chip 
                                label={`${productImages.length} Image${productImages.length !== 1 ? 's' : ''}`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            )}
                            {productVideos.length > 0 && (
                              <Chip 
                                label={`${productVideos.length} Video${productVideos.length !== 1 ? 's' : ''}`} 
                                size="small" 
                                color="secondary" 
                                variant="outlined"
                                icon={<PlayArrow />}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                        No product images or videos available
                      </Typography>
                    )}
                  </Paper>
                )}

                {/* Status */}
                <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    Current Status
                  </Typography>
                  <Chip
                    label={statusConfig[selectedRequest.status]?.label || selectedRequest.status}
                    sx={{
                      backgroundColor: statusConfig[selectedRequest.status]?.bgColor || '#f5f5f5',
                      color: statusConfig[selectedRequest.status]?.color || '#666',
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                    icon={statusConfig[selectedRequest.status]?.icon}
                  />
                </Paper>

                {/* Payment Message */}
                <Paper sx={{ p: 2, backgroundColor: '#e8f5e8', border: '1px solid #4caf50' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircle sx={{ color: '#4caf50' }} />
                    <Typography variant="h6" sx={{ color: '#2e7d32' }}>
                      Payment Confirmation
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#2e7d32' }}>
                    We are pleased to inform you that the customer has successfully paid the advance amount of 10% 
                    for this lot request. You can proceed with confidence knowing that the customer is committed 
                    to this purchase.
                  </Typography>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseViewDialog} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Settings Tab
  const SettingsTab = () => {
    const [settings, setSettings] = useState({
      emailNotifications: true,
      orderAlerts: true,
      lowStockAlerts: true,
      autoApproveOrders: false,
      publicProfile: true,
    });

    const handleSettingChange = (setting) => (event) => {
      setSettings((prev) => ({ ...prev, [setting]: event.target.checked }));
    };

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Settings & Preferences
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Notifications
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={handleSettingChange("emailNotifications")}
                  />
                }
                label="Email notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.orderAlerts}
                    onChange={handleSettingChange("orderAlerts")}
                  />
                }
                label="Order alerts"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.lowStockAlerts}
                    onChange={handleSettingChange("lowStockAlerts")}
                  />
                }
                label="Low stock alerts"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Business Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoApproveOrders}
                    onChange={handleSettingChange("autoApproveOrders")}
                  />
                }
                label="Auto-approve orders under ₹10,000"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.publicProfile}
                    onChange={handleSettingChange("publicProfile")}
                  />
                }
                label="Public store profile"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => toast.success("Settings saved successfully!")}
                >
                  Save Settings
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    // Account deletion feature - no toast needed
                  }}
                >
                  Delete Account
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const drawerWidth = sidebarCollapsed ? 70 : 280;

  // Enhanced Combined Logo and Welcome Component
  const CombinedLogoWelcomeSection = ({ collapsed }) => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      width: '100%',
      textAlign: 'center',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      {/* Clickable Logo Image linking to home */}
      <Box
        component={RouterLink}
        to="/"
        sx={{
          display: 'block',
          textDecoration: 'none',
          mb: collapsed ? 0 : 2
        }}
      >
        <Box
          component="img"
          src="/mos-logo.jpg"
          alt="MOS Logo"
          sx={{
            width: collapsed ? 35 : 50,
            height: collapsed ? 35 : 50,
            borderRadius: '12px',
            objectFit: 'cover',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '2px solid rgba(255,255,255,0.2)',
            // Fixed logo scaling: 2% larger when expanded
            transform: collapsed ? 'scale(1)' : 'scale(1.4)',
            cursor: 'pointer',
            '&:hover': {
              transform: collapsed ? 'scale(1.05)' : 'scale(1.07)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
              border: '2px solid rgba(255,255,255,0.4)',
            }
          }}
        />
      </Box>
      
      {/* Logo Text and Welcome Section */}
      {!collapsed && (
        <Box sx={{
          opacity: 1,
          transform: 'translateY(0)',
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.1s",
          textAlign: 'center'
        }}>
          {/* Brand Name */}
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: "black",
            letterSpacing: "0.5px",
            lineHeight: 1.2,
            fontSize: '1.25rem',
            mb: 0.5
          }}>
            Mall of Stone
          </Typography>
          
          {/* Seller Portal Subtitle */}
          <Typography variant="caption" sx={{ 
            color: "rgba(0,0,0,0.7)",
            fontWeight: 500,
            letterSpacing: "1px",
            textTransform: 'uppercase',
            display: 'block',
            fontSize: '0.75rem',
            mb: 2
          }}>
            SELLER PORTAL
          </Typography>

          {/* Welcome Text */}
          <Typography
            variant="body2"
            sx={{ 
              color: "rgba(0,0,0,0.9)", 
              fontWeight: 600,
              fontSize: '0.9rem',
              mb: 1
            }}
          >
            {/* Welcome, {user?.firstName || 'Seller'}! */}
            Welcome, {user ? `${user.firstName} ${user.lastName}` : 'Seller'}!

          </Typography>
          
          {/* Business Name Display */}
          {user?.businessProfile?.businessName && (
            <Typography
              variant="body2"
              sx={{ 
                color: "#FFD700", 
                fontWeight: 600,
                fontSize: '0.85rem',
                textAlign: 'center',
                lineHeight: 1.3,
                px: 1
              }}
            >
              {user.businessProfile.businessName}
            </Typography>
          )}
        </Box>
      )}

      {/* Collapsed state - show only logo text */}
      {collapsed && (
        <Typography variant="caption" sx={{ 
          fontWeight: 600, 
          color: "black",
          letterSpacing: "0.3px",
          fontSize: '0.7rem',
          mt: 0.5,
          textAlign: 'center'
        }}>
          MOS
        </Typography>
      )}
    </Box>
  );

  const Sidebar = () => (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      open={sidebarOpen}
      onClose={() => setSidebarOpen(false)}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          background: "white",
          color: "black",
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "4px 0 20px rgba(0,0,0,0.1)",
          borderRight: "1px solid #e0e0e0",
        },
      }}
    >
      {/* Combined Logo and Welcome Section */}
      <Box sx={{ 
        position: 'relative',
        p: sidebarCollapsed ? 1.5 : 3, 
        pt: sidebarCollapsed ? 2 : 3,
        pb: sidebarCollapsed ? 2 : 3,
        borderBottom: '1px solid #e0e0e0',
        minHeight: sidebarCollapsed ? '80px' : '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CombinedLogoWelcomeSection collapsed={sidebarCollapsed} />
      </Box>

      {/* Vertically Centered Toggle Button */}
      <IconButton
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        sx={{ 
          position: 'absolute',
          right: sidebarCollapsed ? 8 : 12,
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'black',
          backgroundColor: 'rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.2)',
          width: sidebarCollapsed ? 32 : 36,
          height: sidebarCollapsed ? 32 : 36,
          zIndex: 1000,
          '&:hover': { 
            backgroundColor: 'rgba(0,0,0,0.2)',
            transform: 'translateY(-50%) scale(1.1)',
            border: '1px solid rgba(0,0,0,0.4)',
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {sidebarCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
      </IconButton>



      {/* Compact Navigation List */}
      <List sx={{ 
        pt: 1, 
        px: sidebarCollapsed ? 0.5 : 1,
        pb: 10, // Add padding at bottom to prevent overlap with footer
        overflowY: 'auto',
        overflowX: 'hidden',
        flex: 1,
        '& .MuiListItemButton-root': {
          minHeight: '40px'
        }
      }}>
        {sidebarItems.map((item, index) => (
          <Tooltip
            key={item.value}
            title={sidebarCollapsed ? item.label : ""}
            placement="right"
            arrow
            sx={{
              "& .MuiTooltip-tooltip": {
                bgcolor: "rgba(0,0,0,0.9)",
                color: "white",
                borderRadius: "8px",
                fontSize: "0.8rem",
                fontWeight: 500,
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                backdropFilter: "blur(10px)"
              },
              "& .MuiTooltip-arrow": {
                color: "rgba(0,0,0,0.9)"
              }
            }}
          >
            <ListItemButton
              onClick={() => item.path ? navigate(item.path) : setActiveTab(item.value)}
              sx={{
                mb: 0.25,
                mx: sidebarCollapsed ? 0.5 : 0,
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                minHeight: '40px',
                maxHeight: '40px',
                backgroundColor: activeTab === item.value
                  ? "rgba(0,0,0,0.15)"
                  : "transparent",
                border: activeTab === item.value 
                  ? "1px solid rgba(0,0,0,0.2)" 
                  : "1px solid transparent",
                boxShadow: activeTab === item.value 
                  ? "0 2px 8px rgba(0,0,0,0.1), inset 0 1px 0 rgba(0,0,0,0.2)" 
                  : "none",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                px: sidebarCollapsed ? 1 : '8px 20px',
                py: '8px',
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                // Active indicator line
                "&::before": {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: activeTab === item.value ? '3px' : '0px',
                  background: 'linear-gradient(180deg, #000000 0%, rgba(0,0,0,0.8) 100%)',
                  borderRadius: '0 3px 3px 0',
                  transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }
              }}
            >
              <ListItemIcon sx={{ 
                color: activeTab === item.value 
                  ? "#000000" 
                  : "rgba(0,0,0,0.6)",
                minWidth: sidebarCollapsed ? 'auto' : 36,
                justifyContent: 'center',
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: activeTab === item.value ? 'scale(1.05)' : 'scale(1)',
                filter: activeTab === item.value 
                  ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' 
                  : 'none',
                '&:hover': {
                  color: "rgba(0,0,0,0.9)",
                  transform: 'scale(1.02)',
                },
                '& svg': {
                  fontSize: '20px'
                }
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: activeTab === item.value ? 600 : 500,
                  fontSize: "0.875rem",
                  lineHeight: 1.2,
                  color: activeTab === item.value 
                    ? "#000000" 
                    : "rgba(0,0,0,0.8)",
                }}
                sx={{
                  opacity: sidebarCollapsed ? 0 : 1,
                  transform: sidebarCollapsed 
                    ? 'translateX(-20px)' 
                    : 'translateX(0)',
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  ml: sidebarCollapsed ? 0 : 0.5,
                  '& .MuiTypography-root': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </ListItemButton>
          </Tooltip>
        ))}
      </List>

      {/* Footer Section */}
      <Box sx={{ 
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        borderTop: '1px solid #e0e0e0',
        bgcolor: 'white',
        p: sidebarCollapsed ? 1 : 2,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden'
      }}>
        {!sidebarCollapsed ? (
          <Box>
            <Typography variant="caption" sx={{ 
              display: 'block',
              color: 'rgba(0,0,0,0.6)',
              fontSize: '0.75rem',
              textAlign: 'center',
              mb: 0.5,
              fontWeight: 500
            }}>
              Mall of Stone
            </Typography>
            <Typography variant="caption" sx={{ 
              display: 'block',
              color: 'rgba(0,0,0,0.4)',
              fontSize: '0.7rem',
              textAlign: 'center',
              fontWeight: 400
            }}>
              © 2024 All rights reserved
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption" sx={{ 
            display: 'block',
            color: 'rgba(0,0,0,0.5)',
            fontSize: '0.65rem',
            textAlign: 'center',
            fontWeight: 600,
            transform: 'rotate(-90deg)',
            whiteSpace: 'nowrap'
          }}>
            © 2024
          </Typography>
        )}
      </Box>
    </Drawer>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <Sidebar />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: isMobile ? '100%' : sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
          transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: isMobile ? 0 : sidebarOpen ? 0 : `-${drawerWidth}px`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        {/* Top App Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: "white",
            color: "text.primary",
            borderBottom: "1px solid #e0e0e0",
            ml: 4,
          }}
        >
          <Toolbar sx={{ pl: 4 }}>
            <Breadcrumbs sx={{ flexGrow: 1 }}>
              <Link component={RouterLink} to="/" color="inherit">
                <Home sx={{ mr: 0.5 }} fontSize="inherit" />
                Home
              </Link>
              <Typography color="text.primary">
                {sidebarItems.find((item) => item.value === activeTab)?.label ||
                  "Dashboard"}
              </Typography>
            </Breadcrumbs>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <IconButton
                onClick={handleRefreshData}
                disabled={loading || statsLoading}
                title="Refresh Data"
              >
                <Refresh />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ py: 4, pl: 4, pr: 4, width: '100%', overflowX: 'hidden' }}>
          {/* Quick Stats Cards - Hidden for Visit Requests tab */}
          {activeTab !== 6 && activeTab !== 9 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(44, 62, 80, 0.15)',
                    borderColor: '#2C3E50'
                  },
                  bgcolor: 'white',
                  border: '2px solid #2C3E50',
                  borderRadius: 1,
                  height: 130,
                  minHeight: 130,
                  maxHeight: 130,
                  width: '100%'
                }}
                onClick={() => setActiveTab(2)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2, px: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <ShoppingCart sx={{ fontSize: 28, color: '#2C3E50', mr: 1.5 }} />
                      {sellerStats?.pendingOrders?.value > 0 && (
                        <Box 
                          sx={{ 
                            bgcolor: '#2C3E50', 
                            color: 'white',
                            borderRadius: '50%',
                            width: 26,
                            height: 26,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            border: '2px solid white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          }}
                        >
                          {sellerStats.pendingOrders.value}
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#2C3E50', fontSize: '1rem', letterSpacing: '0.3px' }}>
                      Pending Orders
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: '2px solid #2C3E50',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(44, 62, 80, 0.15)',
                    borderColor: '#2C3E50'
                  },
                  bgcolor: 'white',
                  border: '2px solid #2C3E50',
                  borderRadius: 1,
                  height: 130,
                  minHeight: 130,
                  maxHeight: 130,
                  width: '100%'
                }}
                onClick={() => {
                  setActiveTab(1);
                  setProductSubTab(0);
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 2, px: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <Pending sx={{ fontSize: 28, color: '#f59e0b', mr: 1.5 }} />
                      {pendingProducts?.length > 0 && (
                        <Box 
                          sx={{ 
                            bgcolor: '#f59e0b', 
                            color: 'white',
                            borderRadius: '50%',
                            width: 26,
                            height: 26,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            border: '2px solid white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          }}
                        >
                          {pendingProducts.length}
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#2C3E50', fontSize: '1rem', letterSpacing: '0.3px' }}>
                      Pending Approval
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: '2px solid #2C3E50',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(44, 62, 80, 0.15)',
                    borderColor: '#2C3E50'
                  },
                  bgcolor: 'white',
                  border: '2px solid #2C3E50',
                  borderRadius: 1,
                  height: 130,
                  minHeight: 130,
                  maxHeight: 130,
                  width: '100%'
                }}
                onClick={() => {
                  setActiveTab(1);
                  setProductSubTab(1);
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 2, px: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <CheckCircle sx={{ fontSize: 28, color: '#10b981', mr: 1.5 }} />
                      {approvedProducts?.length > 0 && (
                        <Box 
                          sx={{ 
                            bgcolor: '#10b981', 
                            color: 'white',
                            borderRadius: '50%',
                            width: 26,
                            height: 26,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            border: '2px solid white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          }}
                        >
                          {approvedProducts.length}
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#2C3E50', fontSize: '1rem', letterSpacing: '0.3px' }}>
                      Approved Products
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: '2px solid #2C3E50',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(44, 62, 80, 0.15)',
                    borderColor: '#2C3E50'
                  },
                  bgcolor: 'white',
                  border: '2px solid #2C3E50',
                  borderRadius: 1,
                  height: 130,
                  minHeight: 130,
                  maxHeight: 130,
                  width: '100%'
                }}
                onClick={() => setActiveTab(3)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2, px: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Analytics sx={{ fontSize: 28, color: '#2C3E50', mr: 1.5 }} />
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#2C3E50', fontSize: '1rem', letterSpacing: '0.3px' }}>
                      View Reports
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: '2px solid #2C3E50',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(44, 62, 80, 0.15)',
                    borderColor: '#2C3E50'
                  },
                  bgcolor: 'white',
                  border: '2px solid #2C3E50',
                  borderRadius: 1,
                  height: 130,
                  minHeight: 130,
                  maxHeight: 130,
                  width: '100%'
                }}
                onClick={() => setActiveTab(5)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2, px: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <Campaign sx={{ fontSize: 28, color: '#2C3E50', mr: 1.5 }} />
                      <Box 
                        sx={{ 
                          bgcolor: activePromotionsCount > 0 ? '#2C3E50' : '#94a3b8', 
                          color: 'white',
                          borderRadius: '50%',
                          width: 26,
                          height: 26,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          border: '2px solid white',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                        }}
                      >
                        {activePromotionsCount}
                      </Box>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#2C3E50', fontSize: '1rem', letterSpacing: '0.3px' }}>
                      Promotions
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          )}

          {/* Main Content */}
          {/* Removed animation wrapper for better UX - no animation on tab switches */}
          <Box>
            {activeTab === 0 && <OverviewTab />}
            {activeTab === 1 && <ProductsTab />}
            {activeTab === 2 && <OrdersTab />}
            {activeTab === 3 && <AnalyticsTab />}
            {activeTab === 4 && <ReviewsTab />}
            {activeTab === 5 && <PromotionsTab />}
            {activeTab === 6 && <VisitRequestsTab />}
            {activeTab === 7 && <ProfileTab />}
            {activeTab === 8 && <SettingsTab />}
            {activeTab === 9 && <LotRequestsTab />}
          </Box>
        </Container>
      </Box>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add product"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
        onClick={() => {
          if (!isSellerApproved) {
            // Seller approval warning - no toast needed
            return;
          }
          setAddProductDialogOpen(true);
        }}
        disabled={!isSellerApproved}
      >
        <Add />
      </Fab>

        {/* Add Product Dialog */}
      <Dialog
        open={addProductDialogOpen}
        onClose={() => {
          setAddProductDialogOpen(false);
          setImageValidationMessages([]);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: '#f8f9fa',
            boxShadow: 'none',
            border: 'none'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            textAlign: 'center',
            fontSize: '32px',
            fontWeight: 500,
            color: '#2d3748',
            pb: 2,
            pt: 6,
            mb: 4,
            border: 'none',
            backgroundColor: '#f8f9fa'
          }}
        >
          Add New Product
        </DialogTitle>
        <DialogContent 
          sx={{ 
            p: 6, 
            pt: 0, 
            backgroundColor: '#f8f9fa',
            // Prevent flickering by stabilizing the container
            willChange: 'auto',
            transform: 'translateZ(0)', // Force GPU acceleration
            backfaceVisibility: 'hidden', // Prevent flickering
          }}
        >
          {/* Basic Information Section */}
          <Box 
            sx={{ 
              mb: 4,
              // Prevent unnecessary re-renders
              willChange: 'auto',
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '18px',
                fontWeight: 600,
                color: '#2d3748',
                mb: 3,
                pb: 1,
                borderBottom: '2px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                // Prevent re-rendering flicker
                willChange: 'auto',
              }}
            >
              <Box sx={{ 
                width: 4, 
                height: 20, 
                backgroundColor: '#667eea', 
                mr: 2, 
                borderRadius: 1,
                flexShrink: 0, // Prevent layout shift
              }} />
              Basic Information
            </Typography>
            <Grid 
              container 
              spacing={3}
              sx={commonGridContainerSx}
            >
              {/* First Row: Product Name, Category */}
              <Grid item xs={12} sm={6}>
                <Typography 
                  variant="subtitle2" 
                  sx={commonTypographySx}
                >
                  Product Name <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter product name"
                  value={newProduct.name || ''}
                  onChange={(e) =>
                    handleProductInputChange("name", e.target.value)
                  }
                  error={!!productErrors.name}
                  helperText={productErrors.name}
                  sx={commonTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography 
                  variant="subtitle2" 
                  sx={commonTypographySx}
                >
                  Category <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                {newProduct.category === 'Others' ? (
                  <TextField
                    fullWidth
                    placeholder="Please specify category"
                    value={newProduct.customCategory}
                    onChange={(e) =>
                      handleProductInputChange("customCategory", e.target.value)
                    }
                    error={!!productErrors.category}
                    helperText={productErrors.category}
                    InputProps={{
                      endAdornment: (
                        <Button
                          size="small"
                          onClick={() => {
                            handleProductInputChange("category", "");
                            handleProductInputChange("customCategory", "");
                          }}
                          sx={{ minWidth: 'auto', p: 0.5 }}
                        >
                          ↩
                        </Button>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '48px',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        color: '#374151',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover': {
                          borderColor: '#9ca3af'
                        },
                        '&.Mui-focused': {
                          borderColor: '#667eea',
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      }
                    }}
                  />
                ) : (
                  <FormControl fullWidth error={!!productErrors.category}>
                    <Select
                      value={newProduct.category}
                      onChange={(e) =>
                        handleProductInputChange("category", e.target.value)
                      }
                      displayEmpty
                      sx={{
                        height: '48px',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        color: '#374151',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover': {
                          borderColor: '#9ca3af'
                        },
                        '&.Mui-focused': {
                          borderColor: '#667eea',
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>Select Category</MenuItem>
                      <MenuItem value="Imported Marble">Imported Marble</MenuItem>
                      <MenuItem value="Granite">Granite</MenuItem>
                      <MenuItem value="Tiles">Tiles</MenuItem>
                      <MenuItem value="Sandstone">Sandstone</MenuItem>
                      <MenuItem value="Indian Marble">Indian Marble</MenuItem>
                      <MenuItem value="Quartz">Quartz</MenuItem>
                      <MenuItem value="Slate">Slate</MenuItem>
                      <MenuItem value="Kota stone">Kota stone</MenuItem>
                      <MenuItem value="Quartzite">Quartzite</MenuItem>
                      <MenuItem value="Others">Others (Please specify)</MenuItem>
                    </Select>
                    {productErrors.category && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '12px' }}>
                        {productErrors.category}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </Grid>

              {/* Second Row: Finish, Origin */}
              <Grid item xs={12} sm={6}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: '#000000',
                    fontWeight: 600,
                    mb: 1,
                    fontSize: '14px'
                  }}
                >
                  Finish <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                {newProduct.specifications.finish === 'Others' ? (
                  <TextField
                    fullWidth
                    placeholder="Please specify finish"
                    value={newProduct.customFinish}
                    onChange={(e) =>
                      handleProductInputChange("customFinish", e.target.value)
                    }
                    error={!!productErrors.finish}
                    helperText={productErrors.finish}
                    InputProps={{
                      endAdornment: (
                        <Button
                          size="small"
                          onClick={() => {
                            handleProductInputChange("specifications", {
                              ...newProduct.specifications,
                              finish: ""
                            });
                            handleProductInputChange("customFinish", "");
                          }}
                          sx={{ minWidth: 'auto', p: 0.5 }}
                        >
                          ↩
                        </Button>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '48px',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        color: '#374151',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover': {
                          borderColor: '#9ca3af'
                        },
                        '&.Mui-focused': {
                          borderColor: '#667eea',
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      }
                    }}
                  />
                ) : (
                  <FormControl fullWidth error={!!productErrors.finish}>
                    <Select
                      value={newProduct.specifications.finish}
                      onChange={(e) =>
                        handleProductInputChange("specifications", {
                          ...newProduct.specifications,
                          finish: e.target.value,
                        })
                      }
                      displayEmpty
                      sx={{
                        height: '48px',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        color: '#374151',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover': {
                          borderColor: '#9ca3af'
                        },
                        '&.Mui-focused': {
                          borderColor: '#667eea',
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>Select Finish</MenuItem>
                      <MenuItem value="Polish">Polish</MenuItem>
                      <MenuItem value="Unpolish">Unpolish</MenuItem>
                      <MenuItem value="Leather">Leather</MenuItem>
                      <MenuItem value="Honed">Honed</MenuItem>
                      <MenuItem value="Shot blast">Shot blast</MenuItem>
                      <MenuItem value="Flamed">Flamed</MenuItem>
                      <MenuItem value="Bush hammered">Bush hammered</MenuItem>
                      <MenuItem value="Ribbed">Ribbed</MenuItem>
                      <MenuItem value="Rivera">Rivera</MenuItem>
                      <MenuItem value="Hydro">Hydro</MenuItem>
                      <MenuItem value="Flute">Flute</MenuItem>
                      <MenuItem value="Rustic">Rustic</MenuItem>
                      <MenuItem value="Carving">Carving</MenuItem>
                      <MenuItem value="Matt">Matt</MenuItem>
                      <MenuItem value="CNC">CNC</MenuItem>
                      <MenuItem value="Others">Others (Please specify)</MenuItem>
                    </Select>
                    {productErrors.finish && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '12px' }}>
                        {productErrors.finish}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography 
                  variant="subtitle2" 
                  sx={commonTypographySx}
                >
                  Origin <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                {newProduct.origin === 'Others' ? (
                  <TextField
                    fullWidth
                    placeholder="Please specify origin"
                    value={newProduct.customOrigin || ''}
                    onChange={(e) =>
                      handleProductInputChange("customOrigin", e.target.value)
                    }
                    error={!!productErrors.origin}
                    helperText={productErrors.origin}
                    InputProps={{
                      endAdornment: (
                        <Button
                          size="small"
                          onClick={() => {
                            handleProductInputChange("origin", "");
                            handleProductInputChange("customOrigin", "");
                          }}
                          sx={{ minWidth: 'auto', p: 0.5 }}
                        >
                          ↩
                        </Button>
                      )
                    }}
                    sx={commonTextFieldSx}
                  />
                ) : (
                  <FormControl fullWidth error={!!productErrors.origin}>
                    <Select
                      value={newProduct.origin || ''}
                      onChange={(e) =>
                        handleProductInputChange("origin", e.target.value)
                      }
                      displayEmpty
                      sx={{
                        height: '48px',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        color: '#374151',
                        willChange: 'auto', // Prevent flickering
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover': {
                          borderColor: '#9ca3af'
                        },
                        '&.Mui-focused': {
                          borderColor: '#667eea',
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>Select Origin</MenuItem>
                      <MenuItem value="India">India</MenuItem>
                      <MenuItem value="Turkey">Turkey</MenuItem>
                      <MenuItem value="Italy">Italy</MenuItem>
                      <MenuItem value="Spain">Spain</MenuItem>
                      <MenuItem value="Portugal">Portugal</MenuItem>
                      <MenuItem value="Greece">Greece</MenuItem>
                      <MenuItem value="Namibia">Namibia</MenuItem>
                      <MenuItem value="Iran">Iran</MenuItem>
                      <MenuItem value="China">China</MenuItem>
                      <MenuItem value="Brazil">Brazil</MenuItem>
                      <MenuItem value="Norway">Norway</MenuItem>
                      <MenuItem value="Others">Others (Please specify)</MenuItem>
                    </Select>
                    {productErrors.origin && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '12px' }}>
                        {productErrors.origin}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </Grid>

              {/* Third Row: Color */}
              <Grid item xs={12} sm={6}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: '#000000',
                    fontWeight: 600,
                    mb: 1,
                    fontSize: '14px'
                  }}
                >
                  Color <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                {newProduct.specifications.color === 'Others' ? (
                  <TextField
                    fullWidth
                    placeholder="Please specify color"
                    value={newProduct.customColor}
                    onChange={(e) =>
                      handleProductInputChange("customColor", e.target.value)
                    }
                    error={!!productErrors.color}
                    helperText={productErrors.color}
                    InputProps={{
                      endAdornment: (
                        <Button
                          size="small"
                          onClick={() => {
                            handleProductInputChange("specifications", {
                              ...newProduct.specifications,
                              color: ""
                            });
                            handleProductInputChange("customColor", "");
                          }}
                          sx={{ minWidth: 'auto', p: 0.5 }}
                        >
                          ↩
                        </Button>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '48px',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        color: '#374151',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover': {
                          borderColor: '#9ca3af'
                        },
                        '&.Mui-focused': {
                          borderColor: '#667eea',
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      }
                    }}
                  />
                ) : (
                  <FormControl fullWidth error={!!productErrors.color}>
                    <Select
                      value={newProduct.specifications.color || ''}
                      onChange={(e) =>
                        handleProductInputChange("specifications", {
                          ...newProduct.specifications,
                          color: e.target.value,
                        })
                      }
                      displayEmpty
                      sx={{
                        height: '48px',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        color: '#374151',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover': {
                          borderColor: '#9ca3af'
                        },
                        '&.Mui-focused': {
                          borderColor: '#667eea',
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>Select Color</MenuItem>
                      <MenuItem value="White">White</MenuItem>
                      <MenuItem value="Beige">Beige</MenuItem>
                      <MenuItem value="Grey">Grey</MenuItem>
                      <MenuItem value="Red">Red</MenuItem>
                      <MenuItem value="Blue">Blue</MenuItem>
                      <MenuItem value="Green">Green</MenuItem>
                      <MenuItem value="Black">Black</MenuItem>
                      <MenuItem value="Brown">Brown</MenuItem>
                      <MenuItem value="Pink">Pink</MenuItem>
                      <MenuItem value="Others">Others (Please specify)</MenuItem>
                    </Select>
                    {productErrors.color && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '12px' }}>
                        {productErrors.color}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </Grid>
            </Grid>
          </Box>



          {/* Pricing & Stock Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '18px',
                fontWeight: 600,
                color: '#2d3748',
                mb: 3,
                pb: 1,
                borderBottom: '2px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box sx={{ 
                width: 4, 
                height: 20, 
                backgroundColor: '#667eea', 
                mr: 2, 
                borderRadius: 1 
              }} />
              Pricing & Stock
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: '#000000',
                    fontWeight: 600,
                    mb: 1,
                    fontSize: '14px'
                  }}
                >
                  Price per sft<span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="0.00"
                  value={newProduct.price}
                  onChange={(e) =>
                    handleProductInputChange("price", e.target.value)
                  }
                  error={!!productErrors.price}
                  helperText={productErrors.price}
                  InputProps={{
                    startAdornment: <Typography sx={{ color: '#6b7280', mr: 1 }}>₹</Typography>
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      color: '#374151',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      },
                      '&:hover': {
                        borderColor: '#9ca3af'
                      },
                      '&.Mui-focused': {
                        borderColor: '#667eea',
                        boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                      }
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#9ca3af',
                      opacity: 1
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '12px',
                      color: '#ef4444'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: '#000000', fontWeight: 600, mb: 1, fontSize: '14px' }}
                >
                  Minimum Order SFT<span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="1"
                  type="number"
                  value={newProduct.minimumOrderQuantity}
                  onChange={(e) => {
                    const raw = parseFloat(e.target.value) || 0;
                    const maxAllowed = computedNewProductTotalSft > 0 ? computedNewProductTotalSft : raw;
                    const v = Math.min(Math.max(0.001, raw), maxAllowed);
                    handleProductInputChange("minimumOrderQuantity", String(v));
                  }}
                  inputProps={{ min: 0.001, step: 0.001, max: computedNewProductTotalSft > 0 ? computedNewProductTotalSft : undefined }}
                  error={!!productErrors.minimumOrderQuantity}
                  helperText={productErrors.minimumOrderQuantity || `Minimum order in SFT${computedNewProductTotalSft > 0 ? ` (max ${computedNewProductTotalSft.toFixed(3)} SFT)` : ''}`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px', borderRadius: '8px', backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db', fontSize: '14px', color: '#374151',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover': { borderColor: '#9ca3af' },
                      '&.Mui-focused': { borderColor: '#667eea', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)' }
                    },
                    '& .MuiInputBase-input::placeholder': { color: '#9ca3af', opacity: 1 },
                    '& .MuiFormHelperText-root': { fontSize: '12px' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: '#000000', fontWeight: 600, mb: 1, fontSize: '14px' }}
                >
                  Stock Quantity
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={computedNewProductStockQty}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-calculated from lot Nos"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px', borderRadius: '8px', backgroundColor: '#f9fafb',
                      border: '1px solid #d1d5db', fontSize: '14px', color: '#6b7280',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                    },
                    '& .MuiFormHelperText-root': { fontSize: '12px' }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Dimensions Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '18px',
                fontWeight: 600,
                color: '#2d3748',
                mb: 3,
                pb: 1,
                borderBottom: '2px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box sx={{ 
                width: 4, 
                height: 20, 
                backgroundColor: '#667eea', 
                mr: 2, 
                borderRadius: 1 
              }} />
              Dimensions
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: '#000000',
                    fontWeight: 600,
                    mb: 1,
                    fontSize: '14px'
                  }}
                >
                  Thickness <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="e.g., 18mm"
                  value={newProduct.specifications?.thickness || ''}
                  onChange={(e) =>
                    handleProductInputChange("specifications", {
                      ...newProduct.specifications,
                      thickness: e.target.value
                    })
                  }
                  error={!!productErrors.thickness}
                  helperText={productErrors.thickness}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      color: '#374151',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      },
                      '&:hover': {
                        borderColor: '#9ca3af'
                      },
                      '&.Mui-focused': {
                        borderColor: '#667eea',
                        boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                      }
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#9ca3af',
                      opacity: 1
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Lot Size Details Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '18px',
                fontWeight: 600,
                color: '#2d3748',
                mb: 3,
                pb: 1,
                borderBottom: '2px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box sx={{ 
                width: 4, 
                height: 20, 
                backgroundColor: '#667eea', 
                mr: 2, 
                borderRadius: 1 
              }} />
              Lot Size Details
            </Typography>

            {/* ── Default Lot ── */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#4b5563', fontWeight: 600, mb: 0.5, fontSize: '13px' }}>
                Default Lot
              </Typography>
            </Box>

            {/* Lot Name and Lot Location */}
            <Grid container spacing={3} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#000000', fontWeight: 600, mb: 1, fontSize: '14px' }}>
                  Lot Name
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Default Lot"
                  value={newProduct.lotName || ''}
                  onChange={(e) => handleProductInputChange("lotName", e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px', borderRadius: '8px', backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db', fontSize: '14px', color: '#374151',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover': { borderColor: '#9ca3af' },
                      '&.Mui-focused': { borderColor: '#667eea', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#000000', fontWeight: 600, mb: 1, fontSize: '14px' }}>
                  Lot Location
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter lot location"
                  value={newProduct.lotLocation || ''}
                  onChange={(e) => handleProductInputChange("lotLocation", e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px', borderRadius: '8px', backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db', fontSize: '14px', color: '#374151',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover': { borderColor: '#9ca3af' },
                      '&.Mui-focused': { borderColor: '#667eea', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)' }
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Default dimension row: Length (inch) | Width (inch) | Nos | + */}
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={4} sm={3}>
                <Typography variant="subtitle2" sx={{ color: '#000000', fontWeight: 600, mb: 1, fontSize: '14px' }}>
                  Length (inch) <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="Length"
                  value={newProduct.lotLength || ''}
                  inputProps={{ min: 0 }}
                  onChange={(e) => handleProductInputChange("lotLength", String(Math.max(0, parseFloat(e.target.value) || 0) || e.target.value))}
                  error={!!productErrors.lotLength}
                  helperText={productErrors.lotLength}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px', borderRadius: '8px', backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db', fontSize: '14px', color: '#374151',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover': { borderColor: '#9ca3af' },
                      '&.Mui-focused': { borderColor: '#667eea', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <Typography variant="subtitle2" sx={{ color: '#000000', fontWeight: 600, mb: 1, fontSize: '14px' }}>
                  Width (inch) <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="Width"
                  value={newProduct.lotWidth || ''}
                  inputProps={{ min: 0 }}
                  onChange={(e) => handleProductInputChange("lotWidth", String(Math.max(0, parseFloat(e.target.value) || 0) || e.target.value))}
                  error={!!productErrors.lotWidth}
                  helperText={productErrors.lotWidth}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px', borderRadius: '8px', backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db', fontSize: '14px', color: '#374151',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover': { borderColor: '#9ca3af' },
                      '&.Mui-focused': { borderColor: '#667eea', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={3} sm={3}>
                <Typography variant="subtitle2" sx={{ color: '#000000', fontWeight: 600, mb: 1, fontSize: '14px' }}>
                  Nos <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="Nos"
                  value={newProduct.lotNos || ''}
                  inputProps={{ min: 0 }}
                  onChange={(e) => handleProductInputChange("lotNos", String(Math.max(0, parseInt(e.target.value) || 0) || e.target.value))}
                  error={!!productErrors.lotNos}
                  helperText={productErrors.lotNos}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px', borderRadius: '8px', backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db', fontSize: '14px', color: '#374151',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover': { borderColor: '#9ca3af' },
                      '&.Mui-focused': { borderColor: '#667eea', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={1} sm={3} sx={{ pb: productErrors.lotNos ? 3 : 0 }}>
                <Box
                  component="button"
                  onClick={() => setNewProductAdditionalLots(prev => [...prev, emptyAdditionalLot()])}
                  sx={{
                    width: 40, height: 40, borderRadius: '8px', border: '2px solid #667eea',
                    backgroundColor: 'transparent', color: '#667eea', fontSize: '20px', fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    '&:hover': { backgroundColor: 'rgba(102,126,234,0.08)' }
                  }}
                >
                  +
                </Box>
              </Grid>
            </Grid>

            {/* Additional dimension rows */}
            {newProductAdditionalLots.map((lot, idx) => (
              <Grid key={lot.lotId} container spacing={2} alignItems="flex-end" sx={{ mt: 1 }}>
                <Grid item xs={4} sm={3}>
                  <Typography variant="subtitle2" sx={{ color: '#000000', fontWeight: 600, mb: 1, fontSize: '14px' }}>
                    Length (inch)
                  </Typography>
                  <TextField
                    fullWidth type="number" placeholder="Length"
                    value={lot.lotLength || ''}
                    inputProps={{ min: 0 }}
                    onChange={(e) => handleNewProductAdditionalLotChange(idx, 'lotLength', String(Math.max(0, parseFloat(e.target.value) || 0) || e.target.value))}
                    error={!!productErrors[`lot${idx+2}Length`]}
                    helperText={productErrors[`lot${idx+2}Length`]}
                    sx={{ '& .MuiOutlinedInput-root': { height: '48px', borderRadius: '8px', backgroundColor: '#ffffff', border: '1px solid #d1d5db', fontSize: '14px', color: '#374151', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '&:hover': { borderColor: '#9ca3af' }, '&.Mui-focused': { borderColor: '#667eea', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)' } } }}
                  />
                </Grid>
                <Grid item xs={4} sm={3}>
                  <Typography variant="subtitle2" sx={{ color: '#000000', fontWeight: 600, mb: 1, fontSize: '14px' }}>
                    Width (inch)
                  </Typography>
                  <TextField
                    fullWidth type="number" placeholder="Width"
                    value={lot.lotWidth || ''}
                    inputProps={{ min: 0 }}
                    onChange={(e) => handleNewProductAdditionalLotChange(idx, 'lotWidth', String(Math.max(0, parseFloat(e.target.value) || 0) || e.target.value))}
                    error={!!productErrors[`lot${idx+2}Width`]}
                    helperText={productErrors[`lot${idx+2}Width`]}
                    sx={{ '& .MuiOutlinedInput-root': { height: '48px', borderRadius: '8px', backgroundColor: '#ffffff', border: '1px solid #d1d5db', fontSize: '14px', color: '#374151', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '&:hover': { borderColor: '#9ca3af' }, '&.Mui-focused': { borderColor: '#667eea', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)' } } }}
                  />
                </Grid>
                <Grid item xs={3} sm={3}>
                  <Typography variant="subtitle2" sx={{ color: '#000000', fontWeight: 600, mb: 1, fontSize: '14px' }}>
                    Nos
                  </Typography>
                  <TextField
                    fullWidth type="number" placeholder="Nos"
                    value={lot.lotNos || ''}
                    inputProps={{ min: 0 }}
                    onChange={(e) => handleNewProductAdditionalLotChange(idx, 'lotNos', String(Math.max(0, parseInt(e.target.value) || 0) || e.target.value))}
                    error={!!productErrors[`lot${idx+2}Nos`]}
                    helperText={productErrors[`lot${idx+2}Nos`]}
                    sx={{ '& .MuiOutlinedInput-root': { height: '48px', borderRadius: '8px', backgroundColor: '#ffffff', border: '1px solid #d1d5db', fontSize: '14px', color: '#374151', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '&:hover': { borderColor: '#9ca3af' }, '&.Mui-focused': { borderColor: '#667eea', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)' } } }}
                  />
                </Grid>
                <Grid item xs={1} sm={3}>
                  <Button size="small" color="error" variant="text"
                    onClick={() => setNewProductAdditionalLots(prev => prev.filter((_, i) => i !== idx))}>
                    Remove
                  </Button>
                </Grid>
              </Grid>
            ))}

            {/* Total SFT — single value at bottom of lot section */}
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f0f4ff', borderRadius: 2, border: '1px solid #c7d2fe' }}>
              <Typography variant="subtitle2" sx={{ color: '#4b5563', fontWeight: 600, mb: 0.5, fontSize: '13px' }}>
                Total SFT (Auto-calculated)
              </Typography>
              <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700 }}>
                {computedNewProductTotalSft > 0 ? computedNewProductTotalSft.toFixed(3) : '0.000'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '11px' }}>
                = sum(Length × Width × Nos) ÷ 144
              </Typography>
            </Box>

          </Box>

          {/* Product Description and Image Upload Section */}
          <Grid 
            container 
            spacing={3} 
            sx={{ 
              mb: 4,
              // Prevent flickering on re-renders
              willChange: 'auto',
            }}
          >
            {/* Product Description Section */}
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2d3748',
                  mb: 3,
                  pb: 1,
                  borderBottom: '2px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  // Prevent re-rendering flicker
                  willChange: 'auto',
                }}
              >
                <Box sx={{ 
                  width: 4, 
                  height: 20, 
                  backgroundColor: '#667eea', 
                  mr: 2, 
                  borderRadius: 1,
                  flexShrink: 0, // Prevent layout shift
                }} />
                Product Description
              </Typography>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: '#000000',
                  fontWeight: 600,
                  mb: 1,
                  fontSize: '14px',
                  // Prevent re-rendering flicker
                  willChange: 'auto',
                }}
              >
                Description <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Detailed Description"
                value={newProduct.description || ''}
                onChange={(e) =>
                  handleProductInputChange("description", e.target.value)
                }
                error={!!productErrors.description}
                helperText={productErrors.description}
                // Prevent unnecessary re-renders
                key="product-description-field"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    color: '#374151',
                    // Prevent flickering
                    willChange: 'auto',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&:hover': {
                      borderColor: '#9ca3af'
                    },
                    '&.Mui-focused': {
                      borderColor: '#667eea',
                      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                    }
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#9ca3af',
                    opacity: 1
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '12px',
                    color: '#ef4444'
                  }
                }}
              />
            </Grid>

            {/* Image Upload Section */}
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2d3748',
                  mb: 3,
                  pb: 1,
                  borderBottom: '2px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box sx={{ 
                  width: 4, 
                  height: 20, 
                  backgroundColor: '#667eea', 
                  mr: 2, 
                  borderRadius: 1 
                }} />
                Product Images <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
                <Typography variant="body2">
                  <strong>Thumbnail Selection:</strong> Click the star icon (<Star sx={{ fontSize: 14, verticalAlign: 'middle', ml: 0.5, mr: 0.5 }} />) on any image to set it as the thumbnail. 
                  The thumbnail will be used across all admin and seller pages (orders, visit requests, etc.). 
                  Only one image can be selected as thumbnail.
                </Typography>
              </Alert>
              
              {/* Image Validation Messages - Enhanced UI */}
              {imageValidationMessages.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {imageValidationMessages.map((msg, index) => {
                    const getIcon = () => {
                      if (msg.type === 'error') return <ErrorOutline sx={{ fontSize: 20 }} />;
                      if (msg.type === 'warning') return <Warning sx={{ fontSize: 20 }} />;
                      return <CheckCircle sx={{ fontSize: 20 }} />;
                    };
                    
                    const getColor = () => {
                      if (msg.type === 'error') return '#ef4444';
                      if (msg.type === 'warning') return '#f59e0b';
                      return '#10b981';
                    };
                    
                    const getBgColor = () => {
                      if (msg.type === 'error') return '#fef2f2';
                      if (msg.type === 'warning') return '#fffbeb';
                      return '#f0fdf4';
                    };
                    
                    return (
                      <Box
                        key={index}
                        sx={{
                          mb: 1.5,
                          p: 2,
                          borderRadius: '12px',
                          backgroundColor: getBgColor(),
                          border: `1.5px solid ${getColor()}20`,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            color: getColor(),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 32,
                            height: 32,
                            borderRadius: '8px',
                            backgroundColor: `${getColor()}15`,
                            flexShrink: 0
                          }}
                        >
                          {getIcon()}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: '#1f2937',
                              mb: 0.5,
                              fontSize: '0.875rem'
                            }}
                          >
                            {msg.filename}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6b7280',
                              fontSize: '0.8125rem',
                              lineHeight: 1.5,
                              whiteSpace: 'pre-line'
                            }}
                          >
                            {msg.message}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              
              <Box 
                sx={{ 
                  mb: 2,
                  p: 3,
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f3f4f6'
                  }
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="image-upload"
                  multiple
                  type="file"
                  onChange={(e) => handleImageUpload(e.target.files)}
                />
                <label htmlFor="image-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CloudUpload sx={{ fontSize: 40, color: '#9ca3af' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#374151' }}>
                      {uploadingImages ? "Uploading..." : "Upload Images"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Click to browse or drag and drop your images here
                    </Typography>
                  </Box>
                </label>
              </Box>

              {/* Image Preview */}
              {useMemo(() => {
                if (!newProduct.images || newProduct.images.length === 0) return null;
                
                return (
                  <Grid container spacing={2}>
                    {newProduct.images.map((image, index) => {
                      const imageSrc =
                        image.url ||
                        image.previewUrl ||
                        image.imageUrl ||
                        image.thumbnailUrl ||
                        '';
                      // Use stable key based on image URL or file name to prevent re-renders
                      const imageKey = image.url || image.imageUrl || image.fileName || image.name || `new-image-${index}`;

                      return (
                        <Grid item xs={6} sm={4} md={3} key={imageKey}>
                        <Box
                          sx={{
                            position: 'relative',
                            borderRadius: '8px',
                            overflow: 'visible',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            cursor: imageSrc ? 'zoom-in' : 'default',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 1,
                            backgroundColor: '#f9fafb',
                          }}
                          onClick={() =>
                            imageSrc &&
                            openImagePreview(
                              imageSrc,
                              `${newProduct.name || 'Product'} image ${index + 1}`
                            )
                          }
                        >
                          <Box
                            component="img"
                            src={imageSrc || '/placeholder-image.jpg'}
                            alt={`Product ${index + 1}`}
                            loading="lazy"
                            sx={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: '200px',
                              objectFit: 'contain',
                              display: 'block',
                              transition: imageSrc ? 'transform 0.3s ease' : 'none',
                              transformOrigin: 'center',
                            }}
                            onError={(event) => {
                              event.currentTarget.src = '/placeholder-image.jpg';
                            }}
                          />
                          <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              sx={{
                                bgcolor: newProduct.thumbnailImageIndex === index 
                                  ? 'rgba(34, 197, 94, 0.9)' 
                                  : 'rgba(255,255,255,0.9)',
                                color: newProduct.thumbnailImageIndex === index ? '#ffffff' : '#64748b',
                                '&:hover': {
                                  bgcolor: newProduct.thumbnailImageIndex === index 
                                    ? 'rgba(34, 197, 94, 1)' 
                                    : 'rgba(255,255,255,1)',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                                setThumbnailImage(index);
                              }}
                              aria-label={newProduct.thumbnailImageIndex === index ? "Remove thumbnail" : "Set as thumbnail"}
                              title={newProduct.thumbnailImageIndex === index ? "Thumbnail selected" : "Set as thumbnail"}
                            >
                              {newProduct.thumbnailImageIndex === index ? (
                                <CheckCircle fontSize="small" />
                              ) : (
                                <Star fontSize="small" />
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.9)',
                                color: '#ef4444',
                                '&:hover': {
                                  bgcolor: 'rgba(255,255,255,1)',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                                removeImage(index);
                              }}
                              aria-label="Remove product image"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                  </Grid>
                );
              }, [newProduct.images, newProduct.thumbnailImageIndex])}
              
              {/* Image Upload Error */}
              {productErrors.images && (
                <Typography variant="caption" color="error" sx={{ mt: 1, fontSize: '12px', display: 'block' }}>
                  {productErrors.images}
                </Typography>
              )}
            </Grid>

            {/* Video Upload Section */}
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2d3748',
                  mb: 3,
                  pb: 1,
                  borderBottom: '2px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box sx={{ 
                  width: 4, 
                  height: 20, 
                  backgroundColor: '#667eea', 
                  mr: 2, 
                  borderRadius: 1 
                }} />
                Product Videos
              </Typography>
              <Box 
                sx={{ 
                  mb: 2,
                  p: 3,
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f3f4f6'
                  }
                }}
              >
                <input
                  accept="video/*"
                  style={{ display: "none" }}
                  id="video-upload"
                  multiple
                  type="file"
                  onChange={(e) => handleVideoUpload(e.target.files)}
                />
                <label htmlFor="video-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CloudUpload sx={{ fontSize: 40, color: '#9ca3af' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#374151' }}>
                      {uploadingVideo ? "Uploading..." : "Upload Videos"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Click to browse or drag and drop your videos here (any size)
                    </Typography>
                  </Box>
                </label>
              </Box>

              {/* Video Preview */}
              {(newProduct.videoUrls || []).length > 0 && (
                <Grid container spacing={2}>
                  {newProduct.videoUrls.map((video, index) => {
                    const videoSrc =
                      video.url ||
                      video.previewUrl ||
                      video.videoUrl ||
                      '';

                    return (
                      <Grid item xs={12} sm={6} md={6} key={index}>
                        <Box
                          sx={{
                            position: 'relative',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}
                        >
                          <Box
                            component="video"
                            src={videoSrc}
                            controls
                            sx={{
                              width: '100%',
                              maxHeight: 200,
                              objectFit: 'contain',
                              display: 'block',
                              bgcolor: '#000',
                            }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(255,255,255,0.9)',
                              color: '#ef4444',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,1)',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                            onClick={() => removeVideo(index)}
                            aria-label="Remove product video"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Grid>
          </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, gap: 2, justifyContent: 'flex-end' }}>
          <Button 
            onClick={() => setAddProductDialogOpen(false)}
            variant="outlined"
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              borderColor: '#E2E8F0',
              color: '#4A5568',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#CBD5E0',
                backgroundColor: '#F7FAFC'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddProduct}
            disabled={loading || uploadingImages || uploadingVideo || createProductLoading}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              backgroundColor: '#2C3E50',
              fontWeight: 500,
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                backgroundColor: '#2C3E50',
                boxShadow: '#2C3E50'
              },
              '&:disabled': {
                backgroundColor: '#A0AEC0',
                boxShadow: 'none'
              }
            }}
          >
            {createProductLoading ? "Adding..." : "Add Product"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Product Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            maxHeight: "90vh"
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: "1px solid #e2e8f0",
          background: "linear-gradient(135deg,#2C3E50 100%)",
          color: "white",
          fontSize: "1.5rem",
          fontWeight: 600
        }}>
          Product Details
        </DialogTitle>
        <DialogContent sx={{ p: 4, overflow: "auto" }}>
          {selectedProduct && (
            <Box>
              {/* Basic Information Section */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#2d3748',
                    mb: 3,
                    pb: 1,
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Box sx={{ 
                    width: 4, 
                    height: 20, 
                    backgroundColor: '#667eea', 
                    mr: 2, 
                    borderRadius: 1 
                  }} />
                  Basic Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Product Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedProduct.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Category
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedProduct.category}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Finish
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedProduct.specifications?.finish || "Not specified"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Origin
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedProduct.origin || "Not specified"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Color
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedProduct.specifications?.color || "Not specified"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Status
                    </Typography>
                    <Chip 
                      label={(selectedProduct.status || 'PENDING').replace("_", " ").toUpperCase()}
                      color={getStatusColor(selectedProduct.status || 'PENDING')}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Pricing & Stock Section */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#2d3748',
                    mb: 3,
                    pb: 1,
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Box sx={{ 
                    width: 4, 
                    height: 20, 
                    backgroundColor: '#667eea', 
                    mr: 2, 
                    borderRadius: 1 
                  }} />
                  Pricing & Stock
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Price
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: "#10b981", mb: 2 }}>
                      ₹{selectedProduct.price}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Price Unit
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedProduct.priceUnit || "Not specified"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Stock Quantity
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedProduct.stockQuantity || selectedProduct.stock}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Dimensions Section */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#2d3748',
                    mb: 3,
                    pb: 1,
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Box sx={{ 
                    width: 4, 
                    height: 20, 
                    backgroundColor: '#667eea', 
                    mr: 2, 
                    borderRadius: 1 
                  }} />
                  Dimensions
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Thickness
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedProduct.specifications?.thickness || "Not specified"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Lot Size Details Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#2d3748',
                    mb: 3,
                    pb: 1,
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Box sx={{
                    width: 4,
                    height: 20,
                    backgroundColor: '#667eea',
                    mr: 2,
                    borderRadius: 1
                  }} />
                  Lot Size Details
                </Typography>
                {/* Show multi-lot if available, else legacy single lot */}
                {(() => {
                  const viewLots = (selectedProduct.lots && selectedProduct.lots.length > 0)
                    ? selectedProduct.lots
                    : (selectedProduct.lotLength || selectedProduct.lotWidth || selectedProduct.lotNos)
                      ? [{ lotName: 'Default Lot', lotLength: selectedProduct.lotLength, lotWidth: selectedProduct.lotWidth, lotNos: selectedProduct.lotNos, lotSft: selectedProduct.lotSft, lotLocation: selectedProduct.lotLocation }]
                      : [];
                  if (viewLots.length === 0) {
                    return <Typography variant="body2" color="text.secondary">No lot details specified.</Typography>;
                  }
                  const viewTotalSft = viewLots.reduce((sum, lot) => sum + ((parseFloat(lot.lotLength)||0)*(parseFloat(lot.lotWidth)||0)*(parseInt(lot.lotNos)||0)/144), 0);
                  const viewLocation = selectedProduct.lotLocation || viewLots[0]?.lotLocation || '';
                  return (
                    <>
                      {viewLots.map((lot, idx) => (
                        <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #e2e8f0', borderRadius: 1, bgcolor: '#f8fafc' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                            {lot.lotName || `Lot ${idx + 1}`}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Length (inch)</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{lot.lotLength || 'N/A'}</Typography></Grid>
                            <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Width (inch)</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{lot.lotWidth || 'N/A'}</Typography></Grid>
                            <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Nos</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{lot.lotNos || 'N/A'}</Typography></Grid>
                          </Grid>
                        </Box>
                      ))}
                      {viewLocation && <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Lot Location:</strong> {viewLocation}</Typography>}
                      {viewTotalSft > 0 && <Typography variant="body2"><strong>Total SFT:</strong> {viewTotalSft.toFixed(3)}</Typography>}
                    </>
                  );
                })()}
              </Box>

              {/* Product Images Section */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#2d3748',
                    mb: 3,
                    pb: 1,
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Box sx={{ 
                    width: 4, 
                    height: 20, 
                    backgroundColor: '#667eea', 
                    mr: 2, 
                    borderRadius: 1 
                  }} />
                  Product Images
                </Typography>
                {(() => {
                  const primaryImageUrl = getProductPrimaryImageUrl(selectedProduct);
                  return (
                    <Grid container spacing={3}>
                      {primaryImageUrl && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                            Primary Image
                          </Typography>
                          <Box
                            sx={{
                              borderRadius: 2,
                              overflow: 'hidden',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              position: 'relative'
                            }}
                          >
                            <img
                              key={`primary-image-${selectedProduct.id}-${primaryImageUrl}`}
                              src={primaryImageUrl}
                              alt={`${selectedProduct.name} - Primary`}
                              loading="lazy"
                              decoding="async"
                              style={{
                                width: "100%",
                                height: 250,
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Chip
                              label="Primary"
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontSize: '0.75rem'
                              }}
                            />
                          </Box>
                        </Grid>
                      )}
                      {selectedProduct.images && selectedProduct.images.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                            Additional Images
                          </Typography>
                          <Grid container spacing={1}>
                            {selectedProduct.images.map((image, index) => {
                              const imageUrl = image.imageUrl || image.url;
                              return imageUrl && (
                                <Grid item xs={6} key={`additional-image-${selectedProduct.id}-${index}-${imageUrl}`}>
                                  <Box
                                    sx={{
                                      borderRadius: 2,
                                      overflow: 'hidden',
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                  >
                                    <img
                                      key={`additional-image-img-${selectedProduct.id}-${index}-${imageUrl}`}
                                      src={imageUrl}
                                      alt={`${selectedProduct.name} - ${index + 1}`}
                                      loading="lazy"
                                      decoding="async"
                                      style={{
                                        width: "100%",
                                        height: 120,
                                        objectFit: "cover",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  );
                })()}
              </Box>

              {/* Product Videos Section */}
              {selectedProduct.videoUrls && selectedProduct.videoUrls.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#2d3748',
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ 
                      width: 4, 
                      height: 20, 
                      backgroundColor: '#667eea', 
                      mr: 2, 
                      borderRadius: 1 
                    }} />
                    Product Videos
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedProduct.videoUrls.map((videoUrl, index) => {
                      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
                      // Videos are stored in /uploads/products/ but served via /api/files/images/products/
                      let fullVideoUrl = typeof videoUrl === 'string' ? videoUrl : (videoUrl.url || videoUrl.videoUrl || '');
                      if (typeof fullVideoUrl === 'string' && !fullVideoUrl.startsWith('http')) {
                        // If it's a relative path, construct the full URL
                        if (fullVideoUrl.startsWith('/api/files/')) {
                          fullVideoUrl = `${API_BASE.replace('/api', '')}${fullVideoUrl}`;
                        } else if (fullVideoUrl.startsWith('/uploads/products/')) {
                          // Convert /uploads/products/filename to /api/files/images/products/filename
                          const filename = fullVideoUrl.replace('/uploads/products/', '');
                          fullVideoUrl = `${API_BASE.replace('/api', '')}/api/files/images/products/${filename}`;
                        } else {
                          // Assume it's a filename in the products directory
                          fullVideoUrl = `${API_BASE.replace('/api', '')}/api/files/images/products/${fullVideoUrl}`;
                        }
                      }
                      return (
                        <Grid item xs={12} sm={6} key={index}>
                          <Box
                            sx={{
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: '1px solid #e0e0e0',
                            }}
                          >
                            <Box
                              component="video"
                              src={fullVideoUrl}
                              controls
                              sx={{
                                width: '100%',
                                maxHeight: 300,
                                objectFit: 'contain',
                                display: 'block',
                                bgcolor: '#000',
                              }}
                            />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* Description Section */}
              {selectedProduct.description && (
                <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#2d3748',
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ 
                      width: 4, 
                      height: 20, 
                      backgroundColor: '#667eea', 
                      mr: 2, 
                      borderRadius: 1 
                    }} />
                    Product Description
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6, color: "#4a5568", p: 2, bgcolor: "#f8f9fa", borderRadius: 2 }}>
                    {selectedProduct.description}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
            Close
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Edit />}
            onClick={() => {
              setViewDialogOpen(false);
              handleProductAction('edit', selectedProduct);
            }}
            sx={{
              background: "linear-gradient(135deg,#2C3E50 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #2C3E50 100%)"
              }
            }}
          >
            Edit Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog
        open={productDialogOpen}
        onClose={closeProductDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflow: 'hidden',
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ 
          background: "linear-gradient(135deg, #2C3E50 100%)",
          color: 'white',
          fontSize: '1.25rem',
          fontWeight: 600,
          py: 3
        }}>
          {selectedProduct ? "Edit Product" : "Add New Product"}
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto', p: 0 }}>
          <Box sx={{ p: 3 }}>
            
            {/* Basic Information Section */}
            <Paper elevation={0} sx={{ 
              p: 3, 
              mb: 3, 
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              backgroundColor: '#fafafa'
            }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                color: "#1e293b", 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  width: 4, 
                  height: 20, 
                  backgroundColor: '#667eea', 
                  mr: 2, 
                  borderRadius: 1 
                }} />
                Basic Information
              </Typography>
              <Grid container spacing={3}>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Name"
                value={editForm.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="edit-category-label">Category</InputLabel>
                <Select
                  labelId="edit-category-label"
                  value={editForm.category || ''}
                  label="Category"
                  onChange={(e) => handleEditFormChange('category', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected || selected === '') {
                      return <em style={{ color: '#999' }}>Select Category</em>;
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  <MenuItem value="Marble">Marble</MenuItem>
                  <MenuItem value="Granite">Granite</MenuItem>
                  <MenuItem value="Tiles">Tiles</MenuItem>
                  <MenuItem value="Sandstone">Sandstone</MenuItem>
                  <MenuItem value="Indian Marble">Indian Marble</MenuItem>
                  <MenuItem value="Quartz">Quartz</MenuItem>
                  <MenuItem value="Slate">Slate</MenuItem>
                  <MenuItem value="Kota stone">Kota stone</MenuItem>
                  <MenuItem value="Quartzite">Quartzite</MenuItem>
                  <MenuItem value="Others">Others</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {editForm.category === "Others" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Custom Category"
                  value={editForm.customCategory}
                  onChange={(e) => handleEditFormChange('customCategory', e.target.value)}
                  placeholder="Enter custom category"
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="edit-finish-label">Finish</InputLabel>
                <Select
                  labelId="edit-finish-label"
                  value={editForm.specifications?.finish || ''}
                  label="Finish"
                  onChange={(e) => handleEditFormChange('specifications.finish', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected || selected === '') {
                      return <em style={{ color: '#999' }}>Select Finish</em>;
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="">
                    <em>Select Finish</em>
                  </MenuItem>
                  <MenuItem value="Polished">Polished</MenuItem>
                  <MenuItem value="Honed">Honed</MenuItem>
                  <MenuItem value="Brushed">Brushed</MenuItem>
                  <MenuItem value="Sandblasted">Sandblasted</MenuItem>
                  <MenuItem value="Flamed">Flamed</MenuItem>
                  <MenuItem value="Others">Others</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {editForm.specifications.finish === "Others" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Custom Finish"
                  value={editForm.customFinish}
                  onChange={(e) => handleEditFormChange('customFinish', e.target.value)}
                  placeholder="Enter custom finish"
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Origin"
                value={editForm.origin}
                onChange={(e) => handleEditFormChange('origin', e.target.value)}
                placeholder="e.g., Rajasthan, Italy"
              />
            </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="edit-color-label">Color</InputLabel>
                    <Select
                      labelId="edit-color-label"
                      value={editForm.specifications?.color || ''}
                      label="Color"
                      onChange={(e) => handleEditFormChange('specifications.color', e.target.value)}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected || selected === '') {
                          return <em style={{ color: '#999' }}>Select Color</em>;
                        }
                        return selected;
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Color</em>
                      </MenuItem>
                      <MenuItem value="White">White</MenuItem>
                      <MenuItem value="Beige">Beige</MenuItem>
                      <MenuItem value="Grey">Grey</MenuItem>
                      <MenuItem value="Red">Red</MenuItem>
                      <MenuItem value="Blue">Blue</MenuItem>
                      <MenuItem value="Green">Green</MenuItem>
                      <MenuItem value="Black">Black</MenuItem>
                      <MenuItem value="Brown">Brown</MenuItem>
                      <MenuItem value="Pink">Pink</MenuItem>
                      <MenuItem value="Others">Others (Please specify)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {editForm.specifications?.color === "Others" && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Custom Color"
                      value={editForm.customColor}
                      onChange={(e) => handleEditFormChange('customColor', e.target.value)}
                      placeholder="Enter custom color"
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Pricing & Stock Section */}
            <Paper elevation={0} sx={{ 
              p: 3, 
              mb: 3, 
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              backgroundColor: '#fafafa'
            }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                color: "#1e293b", 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  width: 4, 
                  height: 20, 
                  backgroundColor: '#667eea', 
                  mr: 2, 
                  borderRadius: 1 
                }} />
                Pricing & Stock
              </Typography>
              <Grid container spacing={3}>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price (₹)"
                type="number"
                value={editForm.price}
                onChange={(e) => handleEditFormChange('price', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Order SFT"
                type="number"
                value={editForm.minimumOrderQuantity}
                onChange={(e) => {
                  const raw = parseFloat(e.target.value) || 0;
                  const maxAllowed = computedEditTotalSft > 0 ? computedEditTotalSft : raw;
                  const v = Math.min(Math.max(0.001, raw), maxAllowed);
                  handleEditFormChange('minimumOrderQuantity', String(v));
                }}
                inputProps={{ min: 0.001, step: 0.001, max: computedEditTotalSft > 0 ? computedEditTotalSft : undefined }}
                helperText={`Minimum order in SFT${computedEditTotalSft > 0 ? ` (max ${computedEditTotalSft.toFixed(3)} SFT)` : ''}`}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stock Quantity (Auto-calculated)"
                type="number"
                value={computedEditStockQty}
                InputProps={{ readOnly: true }}
                helperText="Sum of all lot Nos"
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9fafb' } }}
              />
            </Grid>

              </Grid>
            </Paper>

            {/* Dimensions Section */}
            <Paper elevation={0} sx={{ 
              p: 3, 
              mb: 3, 
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              backgroundColor: '#fafafa'
            }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                color: "#1e293b", 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  width: 4, 
                  height: 20, 
                  backgroundColor: '#667eea', 
                  mr: 2, 
                  borderRadius: 1 
                }} />
                Dimensions
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Thickness (mm)"
                    type="number"
                    value={editForm.specifications.thickness}
                    onChange={(e) => handleEditFormChange('specifications.thickness', e.target.value)}
                    placeholder="e.g., 18, 20, 30"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Lot Size Details Section */}
            <Paper elevation={0} sx={{ 
              p: 3, 
              mb: 3, 
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              backgroundColor: '#fafafa'
            }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                color: "#1e293b", 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  width: 4, 
                  height: 20, 
                  backgroundColor: '#667eea', 
                  mr: 2, 
                  borderRadius: 1 
                }} />
                Lot Size Details
              </Typography>

              {/* Default lot name and location */}
              <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 600, mb: 1, display: 'block' }}>
                Default Lot
              </Typography>
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Lot Name" placeholder="Default Lot"
                    value={editForm.lotName || ''}
                    onChange={(e) => handleEditFormChange('lotName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Lot Location" placeholder="e.g., Warehouse A"
                    value={editForm.lotLocation}
                    onChange={(e) => handleEditFormChange('lotLocation', e.target.value)}
                  />
                </Grid>
              </Grid>

              {/* Default dimension row: Length (inch) | Width (inch) | Nos | + */}
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={4} sm={3}>
                  <TextField
                    fullWidth label="Length (inch)" type="number"
                    value={editForm.lotLength}
                    inputProps={{ min: 0 }}
                    onChange={(e) => handleEditFormChange('lotLength', String(Math.max(0, parseFloat(e.target.value) || 0) || e.target.value))}
                  />
                </Grid>
                <Grid item xs={4} sm={3}>
                  <TextField
                    fullWidth label="Width (inch)" type="number"
                    value={editForm.lotWidth}
                    inputProps={{ min: 0 }}
                    onChange={(e) => handleEditFormChange('lotWidth', String(Math.max(0, parseFloat(e.target.value) || 0) || e.target.value))}
                  />
                </Grid>
                <Grid item xs={3} sm={3}>
                  <TextField
                    fullWidth label="Nos" type="number"
                    value={editForm.lotNos}
                    inputProps={{ min: 0 }}
                    onChange={(e) => handleEditFormChange('lotNos', String(Math.max(0, parseInt(e.target.value) || 0) || e.target.value))}
                  />
                </Grid>
                <Grid item xs={1} sm={3} sx={{ pb: 1 }}>
                  <Box
                    component="button"
                    onClick={() => setEditFormAdditionalLots(prev => [...prev, emptyAdditionalLot()])}
                    sx={{
                      width: 40, height: 40, borderRadius: '8px', border: '2px solid #667eea',
                      backgroundColor: 'transparent', color: '#667eea', fontSize: '20px', fontWeight: 700,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      '&:hover': { backgroundColor: 'rgba(102,126,234,0.08)' }
                    }}
                  >
                    +
                  </Box>
                </Grid>
              </Grid>

              {/* Additional dimension rows */}
              {editFormAdditionalLots.map((lot, idx) => (
                <Grid key={lot.lotId} container spacing={2} alignItems="flex-end" sx={{ mt: 1 }}>
                  <Grid item xs={4} sm={3}>
                    <TextField fullWidth label="Length (inch)" type="number"
                      value={lot.lotLength || ''} inputProps={{ min: 0 }}
                      onChange={(e) => handleEditFormAdditionalLotChange(idx, 'lotLength', String(Math.max(0, parseFloat(e.target.value) || 0) || e.target.value))} />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <TextField fullWidth label="Width (inch)" type="number"
                      value={lot.lotWidth || ''} inputProps={{ min: 0 }}
                      onChange={(e) => handleEditFormAdditionalLotChange(idx, 'lotWidth', String(Math.max(0, parseFloat(e.target.value) || 0) || e.target.value))} />
                  </Grid>
                  <Grid item xs={3} sm={3}>
                    <TextField fullWidth label="Nos" type="number"
                      value={lot.lotNos || ''} inputProps={{ min: 0 }}
                      onChange={(e) => handleEditFormAdditionalLotChange(idx, 'lotNos', String(Math.max(0, parseInt(e.target.value) || 0) || e.target.value))} />
                  </Grid>
                  <Grid item xs={1} sm={3}>
                    <Button size="small" color="error" variant="text"
                      onClick={() => setEditFormAdditionalLots(prev => prev.filter((_, i) => i !== idx))}>
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}

              {/* Total SFT at bottom */}
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f0f4ff', borderRadius: 2, border: '1px solid #c7d2fe' }}>
                <Typography variant="subtitle2" sx={{ color: '#4b5563', fontWeight: 600, mb: 0.5, fontSize: '13px' }}>
                  Total SFT (Auto-calculated)
                </Typography>
                <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700 }}>
                  {computedEditTotalSft > 0 ? computedEditTotalSft.toFixed(3) : '0.000'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '11px' }}>
                  = sum(Length × Width × Nos) ÷ 144
                </Typography>
              </Box>
            </Paper>

            {/* Product Images Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                backgroundColor: '#fafafa'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: "#1e293b",
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box sx={{ 
                  width: 4, 
                  height: 20, 
                  backgroundColor: '#667eea', 
                  mr: 2, 
                  borderRadius: 1 
                }} />
                Product Images
              </Typography>
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
                <Typography variant="body2">
                  <strong>Thumbnail Selection:</strong> Click the star icon (<Star sx={{ fontSize: 14, verticalAlign: 'middle', ml: 0.5, mr: 0.5 }} />) on any image to set it as the thumbnail. 
                  The thumbnail will be used across all admin and seller pages (orders, visit requests, etc.). 
                  Only one image can be selected as thumbnail.
                </Typography>
              </Alert>

              {/* Image Validation Messages - Enhanced UI */}
              {editImageValidationMessages.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {editImageValidationMessages.map((msg, index) => {
                    const getIcon = () => {
                      if (msg.type === 'error') return <ErrorOutline sx={{ fontSize: 20 }} />;
                      if (msg.type === 'warning') return <Warning sx={{ fontSize: 20 }} />;
                      return <CheckCircle sx={{ fontSize: 20 }} />;
                    };
                    
                    const getColor = () => {
                      if (msg.type === 'error') return '#ef4444';
                      if (msg.type === 'warning') return '#f59e0b';
                      return '#10b981';
                    };
                    
                    const getBgColor = () => {
                      if (msg.type === 'error') return '#fef2f2';
                      if (msg.type === 'warning') return '#fffbeb';
                      return '#f0fdf4';
                    };
                    
                    return (
                      <Box
                        key={index}
                        sx={{
                          mb: 1.5,
                          p: 2,
                          borderRadius: '12px',
                          backgroundColor: getBgColor(),
                          border: `1.5px solid ${getColor()}20`,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            color: getColor(),
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0
                          }}
                        >
                          {getIcon()}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: '#1f2937',
                              mb: 0.5,
                              wordBreak: 'break-word'
                            }}
                          >
                            {msg.filename}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#6b7280',
                              fontSize: '0.875rem',
                              wordBreak: 'break-word'
                            }}
                          >
                            {msg.message}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Box
                sx={{
                  mb: 3,
                  p: 3,
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f3f4f6'
                  }
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onDrop={handleEditImageDrop}
              >
                <input
                  ref={editImageInputRef}
                  accept="image/*"
                  style={{ display: "none" }}
                  id="edit-image-upload"
                  multiple
                  type="file"
                  onChange={(e) => handleEditImageUpload(e.target.files)}
                />
                <label
                  htmlFor="edit-image-upload"
                  style={{ cursor: 'pointer', width: '100%', display: 'block' }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CloudUpload sx={{ fontSize: 40, color: '#9ca3af' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#374151' }}>
                      {editUploadingImages ? "Uploading..." : "Upload Images"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Click to browse or drag and drop product images here
                    </Typography>
                  </Box>
                </label>
              </Box>

              {editUploadingImages && (
                <LinearProgress
                  sx={{
                    mb: 3,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3
                    }
                  }}
                />
              )}

              <Grid container spacing={2}>
                {useMemo(() => {
                  if (!editForm.images || editForm.images.length === 0) {
                    return (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            p: 4,
                            textAlign: 'center',
                            border: '1px dashed #cbd5f5',
                            borderRadius: 2,
                            backgroundColor: '#f8fafc'
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#475569' }}>
                            No images added yet
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                            Upload images to showcase this product in the marketplace.
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  }
                  
                  return editForm.images.map((image, index) => {
                    // Memoize image URL calculation to prevent unnecessary recalculations
                    const imageUrlKey = image.imageUrl || image.thumbnailUrl || image.url;
                    const imageSrc = imageUrlKey ? getFullImageUrl(imageUrlKey) : null;
                    // Use stable key based on image URL or ID to prevent re-renders
                    const imageKey = image.imageUrl || image.url || image.fileName || image.id || `image-${index}`;

                    return (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={imageKey}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            borderRadius: '12px',
                            overflow: 'visible',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            cursor: imageSrc ? 'zoom-in' : 'default',
                          }}
                          onClick={() => {
                            if (imageSrc) {
                              const productName = editForm.name || selectedProduct?.name || 'Product';
                              openImagePreview(imageSrc, `${productName} image ${index + 1}`);
                          }
                          }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              minHeight: '150px',
                              backgroundColor: '#f1f5f9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 1
                            }}
                          >
                            {imageSrc ? (
                              <Box
                                component="img"
                                src={imageSrc}
                                alt={image.altText || `${selectedProduct?.name || editForm.name || 'Product'} image ${index + 1}`}
                                loading="lazy"
                                sx={{
                                  width: '100%',
                                  height: 'auto',
                                  maxHeight: '200px',
                                  objectFit: 'contain',
                                  display: 'block',
                                  transition: imageSrc ? 'transform 0.3s ease' : 'none',
                                  transformOrigin: 'center',
                                }}
                                onError={(event) => {
                                  event.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#e2e8f0',
                                  color: '#64748b',
                                  fontWeight: 500
                                }}
                              >
                                No preview
                              </Box>
                            )}
                            <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: editForm.thumbnailImageIndex === index 
                                    ? 'rgba(34, 197, 94, 0.9)' 
                                    : 'rgba(255,255,255,0.9)',
                                  color: editForm.thumbnailImageIndex === index ? '#ffffff' : '#64748b',
                                  '&:hover': {
                                    bgcolor: editForm.thumbnailImageIndex === index 
                                      ? 'rgba(34, 197, 94, 1)' 
                                      : 'rgba(255,255,255,1)',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setThumbnailImageEdit(index);
                                }}
                                aria-label={editForm.thumbnailImageIndex === index ? "Remove thumbnail" : "Set as thumbnail"}
                                title={editForm.thumbnailImageIndex === index ? "Thumbnail selected" : "Set as thumbnail"}
                              >
                                {editForm.thumbnailImageIndex === index ? (
                                  <CheckCircle fontSize="small" />
                                ) : (
                                  <Star fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: "rgba(255,255,255,0.9)",
                                  color: '#ef4444',
                                  "&:hover": { 
                                    bgcolor: "rgba(255,255,255,1)",
                                    transform: 'scale(1.05)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleRemoveEditImage(index);
                                }}
                                aria-label="Remove image"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Box sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#334155',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {image.originalName || image.fileName || `Image ${index + 1}`}
                            </Typography>
                            {image.fileSize ? (
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {(image.fileSize / 1024).toFixed(1)} KB
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      </Grid>
                    );
                  });
                }, [editForm.images, editForm.thumbnailImageIndex])}
              </Grid>
            </Paper>

            {/* Product Videos Section */}
            <Paper elevation={0} sx={{ 
              p: 3, 
              mb: 3, 
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              backgroundColor: '#fafafa'
            }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                color: "#1e293b", 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  width: 4, 
                  height: 20, 
                  backgroundColor: '#667eea', 
                  mr: 2, 
                  borderRadius: 1 
                }} />
                Product Videos
              </Typography>

              <Box
                sx={{
                  mb: 3,
                  p: 3,
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f3f4f6'
                  }
                }}
              >
                <input
                  ref={editVideoInputRef}
                  accept="video/*"
                  style={{ display: "none" }}
                  id="edit-video-upload"
                  multiple
                  type="file"
                  onChange={(e) => handleEditVideoUpload(e.target.files)}
                />
                <label
                  htmlFor="edit-video-upload"
                  style={{ cursor: 'pointer', width: '100%', display: 'block' }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CloudUpload sx={{ fontSize: 40, color: '#9ca3af' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#374151' }}>
                      {editUploadingVideos ? "Uploading..." : "Upload Videos"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Click to browse or drag and drop product videos here (any size)
                    </Typography>
                  </Box>
                </label>
              </Box>

              {editUploadingVideos && (
                <LinearProgress
                  sx={{
                    mb: 3,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3
                    }
                  }}
                />
              )}

              <Grid container spacing={2}>
                {editForm.videoUrls && editForm.videoUrls.length > 0 ? (
                  editForm.videoUrls.map((video, index) => {
                    const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
                    let videoUrl = '';
                    
                    // Handle different video formats
                    if (typeof video === 'string') {
                      // Existing video URL string
                      if (video.startsWith('http')) {
                        videoUrl = video;
                      } else if (video.startsWith('/api/files/')) {
                        videoUrl = `${API_BASE.replace('/api', '')}${video}`;
                      } else if (video.startsWith('/uploads/products/')) {
                        const filename = video.replace('/uploads/products/', '');
                        videoUrl = `${API_BASE.replace('/api', '')}/api/files/images/products/${filename}`;
                      } else {
                        // Assume it's a filename
                        videoUrl = `${API_BASE.replace('/api', '')}/api/files/images/products/${video}`;
                      }
                    } else if (video && typeof video === 'object') {
                      // Video object - check multiple possible properties
                      const possibleUrl = video.url || video.videoUrl || video.previewUrl || video.src;
                      if (possibleUrl) {
                        if (typeof possibleUrl === 'string') {
                          if (possibleUrl.startsWith('http')) {
                            videoUrl = possibleUrl;
                          } else if (possibleUrl.startsWith('/api/files/')) {
                            videoUrl = `${API_BASE.replace('/api', '')}${possibleUrl}`;
                          } else if (possibleUrl.startsWith('/uploads/products/')) {
                            const filename = possibleUrl.replace('/uploads/products/', '');
                        videoUrl = `${API_BASE.replace('/api', '')}/api/files/images/products/${filename}`;
                      } else {
                            videoUrl = `${API_BASE.replace('/api', '')}/api/files/images/products/${possibleUrl}`;
                          }
                        } else {
                          videoUrl = possibleUrl;
                        }
                      }
                    }

                    return (
                      <Grid
                        item
                        xs={12}
                        key={`video-${index}-${videoUrl}`}
                      >
                        <Box
                          sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid #e0e0e0',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                          }}
                        >
                          <Box sx={{ 
                            position: 'relative', 
                            width: '100%', 
                            minHeight: 200,
                            maxHeight: 300,
                            backgroundColor: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                          {videoUrl ? (
                            <>
                              <Box
                                component="video"
                                src={videoUrl}
                                controls
                                preload="metadata"
                                  playsInline
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    maxHeight: '300px',
                                    objectFit: 'contain',
                                  }}
                                sx={{
                                  width: '100%',
                                    minHeight: 200,
                                  maxHeight: 300,
                                    height: 'auto',
                                  objectFit: 'contain',
                                  display: 'block',
                                  bgcolor: '#000',
                                    cursor: 'pointer',
                                }}
                                onError={(event) => {
                                    console.error('Video load error:', event, 'Video URL:', videoUrl);
                                    // Don't hide on error, show error message instead
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                    // Ensure video can play when clicked
                                    if (e.currentTarget.paused) {
                                      e.currentTarget.play().catch(err => console.error('Play error:', err));
                                    }
                                }}
                              />
                              <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                bgcolor: "rgba(255,255,255,0.9)",
                                color: '#ef4444',
                                    zIndex: 10,
                                "&:hover": { 
                                  bgcolor: "rgba(255,255,255,1)",
                                  transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleRemoveEditVideo(index);
                              }}
                              aria-label="Remove video"
                            >
                              <Delete fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <Box
                              sx={{
                                position: 'relative',
                                pt: '66%',
                                backgroundColor: '#000'
                              }}
                            >
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#e2e8f0',
                                  color: '#64748b',
                                  fontWeight: 500
                                }}
                              >
                                No preview
                              </Box>
                            </Box>
                          )}
                          </Box>
                          <Box sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#334155',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {typeof video === 'string' 
                                ? `Video ${index + 1}` 
                                : (video.name || video.fileName || `Video ${index + 1}`)}
                            </Typography>
                            {video.fileSize ? (
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {(video.fileSize / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })
                ) : (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 4,
                        textAlign: 'center',
                        border: '1px dashed #cbd5f5',
                        borderRadius: 2,
                        backgroundColor: '#f8fafc'
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#475569' }}>
                        No videos added yet
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                        Upload videos to showcase this product in the marketplace.
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Product Description Section */}
            <Paper elevation={0} sx={{ 
              p: 3, 
              mb: 3, 
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              backgroundColor: '#fafafa'
            }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                color: "#1e293b", 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  width: 4, 
                  height: 20, 
                  backgroundColor: '#667eea', 
                  mr: 2, 
                  borderRadius: 1 
                }} />
                Product Description
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                    value={editForm.description}
                    onChange={(e) => handleEditFormChange('description', e.target.value)}
                    placeholder="Enter detailed product description..."
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={closeProductDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveProduct}
            disabled={loading || editUploadingImages}
            sx={{
              background: "linear-gradient(135deg, #2C3E50 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #2C3E50 100%)"
              }
            }}
          >
            {loading ? "Updating..." : "Update Product"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility sx={{ color: 'primary.main' }} />
            Order Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                Order #{selectedOrder.id}
              </Typography>
              
              {/* Customer Information */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountCircle fontSize="small" />
                  Customer Information
                  </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {selectedOrder.customer}
                  </Typography>
              </Paper>

              {/* Product Details */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingCart fontSize="small" />
                  Product Details
                  </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {selectedOrder.productImageUrl || selectedOrder.productImage ? (
                        <Box
                          component="img"
                          src={getFullImageUrl(selectedOrder.productImageUrl || selectedOrder.productImage)}
                          alt={selectedOrder.product}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                          sx={{
                            width: 120,
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: 2,
                            border: '2px solid #e0e0e0',
                            display: 'block'
                          }}
                        />
                      ) : null}
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          bgcolor: 'grey.300',
                          borderRadius: 2,
                          display: (selectedOrder.productImageUrl || selectedOrder.productImage) ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid #e0e0e0'
                        }}
                      >
                        <Inventory sx={{ fontSize: 48, color: 'grey.600' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">Product Name</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                          {selectedOrder.product}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Quantity</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedOrder.quantity}
                  </Typography>
                </Grid>
                </Grid>
              </Paper>

              {/* Order Status & Amount */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment fontSize="small" />
                  Order Information
                </Typography>
                <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Order Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedOrder.status.toUpperCase()}
                        color={getStatusColor(selectedOrder.status)}
                        size="medium"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Order Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedOrder.date}
                  </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedOrder.paymentStatus || 'PENDING'}
                        color={selectedOrder.paymentStatus === 'PAID' ? 'success' : selectedOrder.paymentStatus === 'FAILED' ? 'error' : 'warning'}
                        size="medium"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {selectedOrder.paymentMethod === 'UPI' && <Payment sx={{ fontSize: 20, color: 'primary.main' }} />}
                      {selectedOrder.paymentMethod === 'CARD' && <CreditCard sx={{ fontSize: 20, color: 'primary.main' }} />}
                      {selectedOrder.paymentMethod === 'COD' && <AttachMoney sx={{ fontSize: 20, color: 'success.main' }} />}
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedOrder.paymentMethod || 'COD'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    ₹{(selectedOrder.amount || 0).toLocaleString()}
                  </Typography>
                </Grid>
                </Grid>
              </Paper>

              {/* Payment Information */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payment fontSize="small" />
                  Payment Information
                  </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                  <Chip
                      label={selectedOrder.paymentStatus || 'PENDING'}
                      color={selectedOrder.paymentStatus === 'COMPLETED' ? 'success' : selectedOrder.paymentStatus === 'FAILED' ? 'error' : 'warning'}
                    size="small"
                      sx={{ mt: 0.5, fontWeight: 600 }}
                  />
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedOrder.paymentMethod || 'COD'}
                  </Typography>
                </Grid>
                </Grid>
              </Paper>

              {/* Shipping Address */}
              <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShipping fontSize="small" />
                    Shipping Address
                  </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.7 }}>
                    {selectedOrder.shippingAddress}
                  </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOrderDialogOpen(false)} variant="outlined">
            Close
          </Button>
          {selectedOrder?.status === "pending" && (
            <Button variant="contained" startIcon={<LocalShipping />} color="primary">
              Mark as Shipped
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Ship Order Dialog */}
      <Dialog
        open={shipDialogOpen}
        onClose={() => setShipDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ship Order</DialogTitle>
        <DialogContent>
          {orderToShip && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Order #{orderToShip.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Customer: {orderToShip.customer}
              </Typography>
              
              <TextField
                fullWidth
                label="Tracking Number"
                value={shippingForm.trackingNumber}
                onChange={(e) => setShippingForm(prev => ({ ...prev, trackingNumber: e.target.value.trim() }))}
                required
                sx={{ mb: 2 }}
                placeholder="Enter tracking number"
                error={shippingForm.trackingNumber.length > 0 && shippingForm.trackingNumber.trim().length < 3}
                helperText={shippingForm.trackingNumber.length > 0 && shippingForm.trackingNumber.trim().length < 3 ? "Tracking number must be at least 3 characters" : ""}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Carrier</InputLabel>
                <Select
                  value={shippingForm.carrier}
                  onChange={(e) => setShippingForm(prev => ({ ...prev, carrier: e.target.value }))}
                  label="Carrier"
                >
                  <MenuItem value="Standard">Standard</MenuItem>
                  <MenuItem value="FedEx">FedEx</MenuItem>
                  <MenuItem value="UPS">UPS</MenuItem>
                  <MenuItem value="DHL">DHL</MenuItem>
                  <MenuItem value="Blue Dart">Blue Dart</MenuItem>
                  <MenuItem value="DTDC">DTDC</MenuItem>
                  <MenuItem value="India Post">India Post</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShipDialogOpen(false)}
            disabled={shippingLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleShipOrder}
            disabled={shippingLoading || !shippingForm.trackingNumber.trim()}
            startIcon={shippingLoading ? <CircularProgress size={20} /> : <LocalShipping />}
          >
            {shippingLoading ? 'Shipping...' : 'Ship Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog
        open={bulkUploadDialogOpen}
        onClose={handleCloseBulkUploadDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Upload />
            Bulk Upload Products
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload a CSV or Excel file to add multiple products at once. Download the template below to get started with the correct format.
            </Typography>
            
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.dark' }}>
                Get the Template File
              </Typography>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleDownloadTemplate}
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                Download CSV Template
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Template includes all required fields with sample data
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Optional columns <strong>image1</strong>, <strong>image2</strong>, <strong>image3</strong>, <strong>image4</strong>, and <strong>image5</strong> let you specify image or video filenames for each product (up to 5 media files). Upload a ZIP file containing images and/or videos (any size) with matching names to automatically attach them. Videos can be specified in the image columns.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <input
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                id="bulk-upload-file"
                type="file"
                onChange={handleBulkUploadFileChange}
              />
              <label htmlFor="bulk-upload-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {bulkUploadFile ? bulkUploadFile.name : 'Choose File (CSV or Excel)'}
                </Button>
              </label>
            </Box>

            <Box sx={{ mb: 3 }}>
              <input
                accept=".zip"
                style={{ display: 'none' }}
                id="bulk-upload-images-zip"
                type="file"
                onChange={handleBulkUploadImagesFileChange}
                disabled={validatingBulkImages}
              />
              <label htmlFor="bulk-upload-images-zip">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={validatingBulkImages ? <CircularProgress size={20} /> : <FolderZip />}
                  fullWidth
                  sx={{ py: 2 }}
                  disabled={validatingBulkImages}
                >
                  {validatingBulkImages 
                    ? 'Validating images...' 
                    : bulkUploadImagesFile 
                      ? bulkUploadImagesFile.name 
                      : 'Optional: Choose Images ZIP'}
                </Button>
              </label>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Add a ZIP containing product images and/or videos (any size) named exactly as they appear in the image columns. Videos can be included in the same ZIP and specified in image columns.
              </Typography>
              
              {/* Bulk Upload Image Validation Messages - Enhanced UI */}
              {bulkUploadValidationMessages.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#1f2937' }}>
                    Image Validation Results ({bulkUploadValidationMessages.length} images)
                  </Typography>
                  <Box sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1 }}>
                    {bulkUploadValidationMessages.map((msg, index) => {
                      const getIcon = () => {
                        if (msg.type === 'error') return <ErrorOutline sx={{ fontSize: 20 }} />;
                        if (msg.type === 'warning') return <Warning sx={{ fontSize: 20 }} />;
                        if (msg.type === 'info') return <Info sx={{ fontSize: 20 }} />;
                        return <CheckCircle sx={{ fontSize: 20 }} />;
                      };
                      
                      const getColor = () => {
                        if (msg.type === 'error') return '#ef4444';
                        if (msg.type === 'warning') return '#f59e0b';
                        if (msg.type === 'info') return '#3b82f6';
                        return '#10b981';
                      };
                      
                      const getBgColor = () => {
                        if (msg.type === 'error') return '#fef2f2';
                        if (msg.type === 'warning') return '#fffbeb';
                        if (msg.type === 'info') return '#eff6ff';
                        return '#f0fdf4';
                      };
                      
                      return (
                        <Box
                          key={index}
                          sx={{
                            mb: 1.5,
                            p: 2,
                            borderRadius: '12px',
                            backgroundColor: getBgColor(),
                            border: `1.5px solid ${getColor()}20`,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              color: getColor(),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 32,
                              height: 32,
                              borderRadius: '8px',
                              backgroundColor: `${getColor()}15`,
                              flexShrink: 0
                            }}
                          >
                            {getIcon()}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                color: '#1f2937',
                                mb: 0.5,
                                fontSize: '0.875rem'
                              }}
                            >
                              {msg.filename}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#6b7280',
                                fontSize: '0.8125rem',
                                lineHeight: 1.5
                              }}
                            >
                              {msg.message}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>

            {bulkUploadResults && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Upload Results</Typography>
                <Typography variant="body2">
                  ✅ Successfully uploaded: {bulkUploadResults.successCount} products<br/>
                  {bulkUploadResults.errorCount > 0 && (
                    <>❌ Failed: {bulkUploadResults.errorCount} products</>
                  )}
                </Typography>
                
                {bulkUploadResults.errors && bulkUploadResults.errors.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Errors:</Typography>
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {bulkUploadResults.errors.map((error, index) => {
                        const message = typeof error === 'string'
                          ? error
                          : `Row ${error.row}: ${error.message}`;
                        return (
                          <Typography key={index} variant="body2" color="error" sx={{ mb: 0.5 }}>
                            {message}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkUploadDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkUpload}
            variant="contained"
            disabled={!bulkUploadFile || bulkUploadLoading}
            startIcon={bulkUploadLoading ? <CircularProgress size={20} /> : <Upload />}
          >
            {bulkUploadLoading ? 'Uploading...' : 'Upload Products'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={imagePreview.open}
        onClose={closeImagePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography variant="subtitle1">
            {imagePreview.alt || 'Image Preview'}
          </Typography>
          <IconButton onClick={closeImagePreview} aria-label="Close image preview">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'black' }}>
          {imagePreview.src && (
            imagePreview.type === 'video' ? (
              <Box
                component="video"
                src={imagePreview.src}
                controls
                autoPlay
                sx={{
                  width: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            ) : (
              <Box
                component="img"
                src={imagePreview.src}
                alt={imagePreview.alt || 'Selected image'}
                sx={{
                  width: '100%',
                  height: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SellerDashboard;
